import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Save,
  Download,
  Eye,
  FilePlus,
  FolderOpen,
  Plus,
  Users,
  UserCircle2,
  X,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify as ParagraphIcon,
  Trash2,
  ClipboardCheck,
  Share2,
  FlaskConical,
  CircleDot,
  CheckSquare,
  Type,
  ImagePlus,
  FolderTree,
  ListOrdered,
  Monitor,
  Compass,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { clsx } from 'clsx';
import DocumentToolbar from './DocumentToolbar';
import { useAuthStore } from '../../stores/authStore';
import { useDocumentStore } from '../../stores/documentStore';
import logo from '../../assets/images/logo.png';
import { InlineMathModal } from '../math/InlineMathField';
import { MathUtils } from '../math/MathUtils';
import ProfileModal from '../auth/ProfileModal';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { COLLECTIONS, FirestoreService } from '../../services/firestore';
import ImageUploadModal from './ImageUploadModal';
import AssessmentDocSettings from './AssessmentDocSettings';
import ResponseAreaConfigModal from './ResponseAreaConfigModal';
import ShareDocumentModal from './ShareDocumentModal';
import AssessmentDocPreviewModal from './AssessmentDocPreviewModal';
import MathRenderer from '../math/MathRenderer';
import PdfDocumentPanel from './PdfDocumentPanel';
import DocumentNarrationModal from './DocumentNarrationModal';
import DocumentEditorV2 from './v2/DocumentEditorV2';
import { getEditorVersion } from './v2/editorVersion';
import ResponseAreaRenderer from './ResponseAreaRenderer';
import {
  canUseAssessmentModule,
  canShareContent,
  canUsePdfUploads,
  evaluateDocumentSaveAccess,
  getCurrentPlan,
  getUserSubscription,
  PLAN_IDS,
} from '../../utils/subscription';
import { SubscriptionService } from '../../services/subscriptionService';

const replaceDollarMathInContainer = (container) => {
  if (!container) return;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.parentElement) continue;
    if (node.parentElement.closest('.math-container')) continue;
    if (!node.nodeValue || (!node.nodeValue.includes('$') && !node.nodeValue.includes('\\dots'))) continue;
    nodes.push(node);
  }

  const normalizePlainLatexText = (value = '') => value.replace(/\\dots\b/g, '...');

  nodes.forEach((node) => {
    const text = node.nodeValue;
    if (!text.includes('$')) {
      node.nodeValue = normalizePlainLatexText(text);
      return;
    }

    const regex = /\$\$([\s\S]+?)\$\$|\$([^$]+?)\$/g;
    let lastIndex = 0;
    let match;
    const fragment = document.createDocumentFragment();

    while ((match = regex.exec(text)) !== null) {
      const [raw, display, inline] = match;
      const isDisplay = !!display && !inline;
      const content = (display || inline || '').trim();

      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(normalizePlainLatexText(text.slice(lastIndex, match.index))));
      }

      if (content) {
        const mathSpan = document.createElement('span');
        mathSpan.className = isDisplay ? 'math-container display-math' : 'math-container';
        mathSpan.contentEditable = 'false';
        mathSpan.style.cursor = 'pointer';
        mathSpan.setAttribute('data-math-content', content);
        if (isDisplay) mathSpan.setAttribute('data-math-display', 'block');
        mathSpan.innerHTML = isDisplay ? `\\[${content}\\]` : `\\(${content}\\)`;
        fragment.appendChild(mathSpan);
      } else {
        fragment.appendChild(document.createTextNode(raw));
      }

      lastIndex = match.index + raw.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(normalizePlainLatexText(text.slice(lastIndex))));
    }

    if (fragment.childNodes.length) {
      node.parentNode?.replaceChild(fragment, node);
    }
  });
};

const isZWS = (node) => {
  if (!node) return false;
  if (node.nodeType === Node.TEXT_NODE) {
    return /^\u200B+$/.test(node.textContent);
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    return node.classList.contains('zws') || node.getAttribute('data-zws') === 'true';
  }
  return false;
};

const createZwsNode = () => {
  const span = document.createElement('span');
  span.className = 'zws';
  span.setAttribute('data-zws', 'true');
  span.textContent = '\u200B';
  return span;
};

const normalizeFolderPath = (value = '') =>
  value
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join('/');

const getFolderLabel = (value = '') => normalizeFolderPath(value) || 'Root';

const isMathContainer = (node) =>
  node && node.nodeType === Node.ELEMENT_NODE && node.classList.contains('math-container');

const isImageContainer = (node) =>
  node && node.nodeType === Node.ELEMENT_NODE && node.classList.contains('image-container');

const isResponseAreaBlock = (node) =>
  node && node.nodeType === Node.ELEMENT_NODE && node.classList.contains('response-area-block');

const isStepBlock = (node) =>
  node && node.nodeType === Node.ELEMENT_NODE && node.classList.contains('document-step-block');

const isSectionDividerBlock = (node) =>
  node && node.nodeType === Node.ELEMENT_NODE && node.classList.contains('section-divider-block');

const isAtomicBlock = (node) =>
  isMathContainer(node) || isImageContainer(node) || isResponseAreaBlock(node) || isStepBlock(node) ||
  isSectionDividerBlock(node);

const isEffectivelyEmptyParagraph = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE || node.tagName !== 'P') return false;
  const clone = node.cloneNode(true);
  clone.querySelectorAll('.zws, [data-cursor-marker]').forEach((child) => child.remove());
  const text = (clone.textContent || '').replace(/\u200B/g, '').trim();
  const nonBreakChildren = Array.from(clone.children).filter((child) => child.tagName !== 'BR');
  return text.length === 0 && nonBreakChildren.length === 0;
};

const hasMeaningfulBodyContent = (html = '') => {
  if (!html) return false;
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('.zws, [data-cursor-marker]').forEach((child) => child.remove());

  if (
    container.querySelector(
      'img, iframe, table, audio, video, hr, .math-container, .image-container, .response-area-block, .document-step-block, .section-divider-block'
    )
  ) {
    return true;
  }

  Array.from(container.querySelectorAll('p')).forEach((paragraph) => {
    if (isEffectivelyEmptyParagraph(paragraph)) {
      paragraph.remove();
    }
  });

  return (container.textContent || '').replace(/\u200B/g, '').trim().length > 0;
};

const getSanitizedEmbedHtml = (value = '') => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = String(value || '').trim();
  wrapper.querySelectorAll('script').forEach((node) => node.remove());
  return wrapper.innerHTML.trim();
};

const getSafeEmbedUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    return url.toString();
  } catch (_) {
    return '';
  }
};

const getYouTubeEmbedUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const directIdMatch = raw.match(/^[a-zA-Z0-9_-]{11}$/);
  if (directIdMatch) {
    return `https://www.youtube-nocookie.com/embed/${directIdMatch[0]}`;
  }

  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = url.pathname.slice(1).split('/')[0];
    } else if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v') || '';
      } else if (url.pathname.startsWith('/shorts/')) {
        videoId = url.pathname.split('/')[2] || '';
      } else if (url.pathname.startsWith('/embed/')) {
        videoId = url.pathname.split('/')[2] || '';
      }
    }

    if (!/^[a-zA-Z0-9_-]{11}$/.test(videoId)) return '';
    return `https://www.youtube-nocookie.com/embed/${videoId}`;
  } catch (_) {
    return '';
  }
};

const escapeHtmlAttribute = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const escapeHtmlText = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const convertPlainTextToEditorHtml = (value = '') => {
  const normalized = String(value || '').replace(/\r\n?/g, '\n');
  if (!normalized) return '<p><br></p>';

  const lines = normalized.split('\n');
  const html = lines.map((line) => {
    const escaped = escapeHtmlText(line).replace(/ {2}/g, ' &nbsp;').replace(/\t/g, '&emsp;');
    return escaped.trim()
      ? `<p>${escaped}</p>`
      : '<p><br></p>';
  }).join('');

  return html || '<p><br></p>';
};

const looksLikeMarkdown = (value = '') => {
  const normalized = String(value || '').replace(/\r\n?/g, '\n');
  return (
    /(^|\n)\s*#{1,6}\s+/.test(normalized) ||
    /(^|\n)\s*[*-]\s+/.test(normalized) ||
    /(^|\n)\s*\d+\.\s+/.test(normalized) ||
    /(^|\n)\s*\|.+\|/.test(normalized) ||
    /(^|\n)[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*(?=\n|$)/.test(normalized) ||
    /\*\*[^*]+\*\*/.test(normalized) ||
    /`[^`]+`/.test(normalized)
  );
};

const convertPastedTextToEditorHtml = async (value = '') => {
  const text = String(value || '');
  const html = looksLikeMarkdown(text)
    ? await MathUtils.processContentWithMarkdown(text)
    : convertPlainTextToEditorHtml(text);
  const container = document.createElement('div');
  container.innerHTML = html;

  // Materialize pasted math before inserting it into the contenteditable DOM.
  // Waiting until after insertion allows the browser/Markdown DOM to split a
  // delimiter pair across text nodes, at which point the renderer can consume
  // only part of the expression and leave a raw `$` behind.
  replaceDollarMathInContainer(container);
  return container.innerHTML;
};

const buildStepAudioBlockHtml = (audio) => {
  const safeUrl = escapeHtmlAttribute(audio?.url || '');
  const safeName = escapeHtmlAttribute(audio?.name || 'Step audio');
  const safeType = escapeHtmlAttribute(audio?.contentType || 'audio/webm');
  const safePath = escapeHtmlAttribute(audio?.path || '');
  const safeDuration = Number(audio?.duration) || 0;

  return `
    <div class="document-step-audio-block" contenteditable="false" data-step-audio-path="${safePath}">
      <div class="document-step-audio-header">
        <div>
          <div class="document-step-audio-title">Step audio</div>
          <div class="document-step-audio-meta">${safeName}${safeDuration > 0 ? ` • ${Math.floor(safeDuration)}s` : ''}</div>
        </div>
        <div class="document-step-audio-actions">
          <button type="button" class="document-step-audio-action" data-step-audio-action="replace">Replace</button>
          <button type="button" class="document-step-audio-action" data-step-audio-action="remove">Remove</button>
        </div>
      </div>
      <audio controls preload="metadata" src="${safeUrl}">
        <source src="${safeUrl}" type="${safeType}" />
      </audio>
    </div>
  `;
};

const getNarrationPages = (narration) => {
  if (!narration) return {};
  if (narration.pages && typeof narration.pages === 'object') return narration.pages;
  if (narration.url) {
    return {
      1: {
        ...narration,
        position: narration.position || { x: 94, y: 6 },
      },
    };
  }
  return {};
};

const RESPONSE_AREA_TYPE_LABELS = {
  'multiple-choice': 'Multiple Choice Response',
  'multi-select': 'Multi Select Response',
  'short-answer': 'Short Answer Response',
  'paragraph': 'Paragraph Response',
  'image-upload': 'Image Upload Response',
};

const DOCUMENT_TOOLS_ONBOARDING_DISMISSED_KEY = 'sigma.onboarding.documentTools.dismissed';
const DOCUMENT_TOOLS_ONBOARDING_SESSION_KEY = 'sigma.onboarding.documentTools.session';
const DOCUMENT_TOOLS_ONBOARDING_CONTEXT_KEY = 'sigma.onboarding.documentTools.context';

const buildResponseAreaDeleteButtonHtml = () => `
  <button
    type="button"
    class="response-area-preview-delete"
    data-response-action="delete"
    contenteditable="false"
    title="Delete response block"
    aria-label="Delete response block"
  >
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  </button>
`;

const buildResponseAreaBlockHtml = (type, config = {}) => {
  const label = String(config?.label || '').trim();
  const points = Number(config?.points || 0);
  const options = Array.isArray(config?.options) ? config.options.filter((option) => String(option || '').trim()) : [];

  const metaBits = [
    points > 0 ? `${points} pt${points === 1 ? '' : 's'}` : '',
    config?.required === false ? 'Optional' : 'Required',
  ].filter(Boolean);

  let bodyHtml = '';
  if (type === 'multiple-choice' || type === 'multi-select') {
    bodyHtml = options.length > 0
      ? `
        <div class="response-area-preview-options">
          ${options.map((option, index) => `
            <div class="response-area-preview-option">
              <span class="response-area-preview-marker">${type === 'multiple-choice' ? String.fromCharCode(65 + index) : index + 1}</span>
              <span class="response-area-preview-text">${escapeHtmlAttribute(option)}</span>
            </div>
          `).join('')}
        </div>
      `
      : '<div class="response-area-preview-empty">Add answer options in the response settings.</div>';
  } else if (type === 'paragraph') {
    bodyHtml = `
      <div class="response-area-preview-lines">
        <div class="response-area-preview-line"></div>
        <div class="response-area-preview-line"></div>
        <div class="response-area-preview-line short"></div>
      </div>
    `;
  } else if (type === 'image-upload') {
    bodyHtml = `
      <div class="response-area-preview-upload">
        <span class="response-area-preview-upload-icon">+</span>
        <span>Image upload response</span>
      </div>
    `;
  } else {
    bodyHtml = '<div class="response-area-preview-line"></div>';
  }

  return `
    <div class="response-area-placeholder">
      <div class="response-area-preview-header">
        <div class="response-area-preview-title">\u270D ${RESPONSE_AREA_TYPE_LABELS[type] || 'Response Area'}</div>
        <div class="response-area-preview-header-actions">
          ${metaBits.length ? `<div class="response-area-preview-meta">${metaBits.join(' • ')}</div>` : ''}
          ${buildResponseAreaDeleteButtonHtml()}
        </div>
      </div>
      ${label ? `<div class="response-area-preview-label">${escapeHtmlAttribute(label)}</div>` : ''}
      ${bodyHtml}
    </div>
  `;
};

const syncResponseAreaBlockElement = (blockEl, config = {}) => {
  if (!blockEl) return false;
  const type = blockEl.getAttribute('data-response-type') || config?.type || 'short-answer';
  const nextHtml = buildResponseAreaBlockHtml(type, config);
  let changed = false;

  const nextLabel = String(config?.label || '');
  const nextPoints = String(Number(config?.points || 0));
  const nextRequired = config?.required === false ? 'false' : 'true';

  if (blockEl.getAttribute('data-response-label') !== nextLabel) {
    blockEl.setAttribute('data-response-label', nextLabel);
    changed = true;
  }
  if (blockEl.getAttribute('data-response-points') !== nextPoints) {
    blockEl.setAttribute('data-response-points', nextPoints);
    changed = true;
  }
  if (blockEl.getAttribute('data-response-required') !== nextRequired) {
    blockEl.setAttribute('data-response-required', nextRequired);
    changed = true;
  }
  if (blockEl.innerHTML !== nextHtml) {
    blockEl.innerHTML = nextHtml;
    changed = true;
  }

  return changed;
};

const normalizeResponseAreaBlocksInHtml = (html = '', responseAreas = {}, responseAreaOrder = []) => {
  if (!html || !html.includes('response-area-block')) return html || '';

  const container = document.createElement('div');
  container.innerHTML = html;
  let changed = false;

  container.querySelectorAll('.response-area-block').forEach((blockEl, index) => {
    let blockId = blockEl.getAttribute('data-response-id');
    if (!blockId && responseAreaOrder[index]) {
      blockId = responseAreaOrder[index];
      blockEl.setAttribute('data-response-id', blockId);
      changed = true;
    }
    if (!blockId) return;
    const config = responseAreas?.[blockId];
    if (!config) return;
    const nextType = config?.type || blockEl.getAttribute('data-response-type') || 'short-answer';
    const nextLabel = String(config?.label || '');
    const nextPoints = String(Number(config?.points || 0));
    const nextRequired = config?.required === false ? 'false' : 'true';
    if (blockEl.getAttribute('data-response-type') !== nextType) {
      blockEl.setAttribute('data-response-type', nextType);
      changed = true;
    }
    if (blockEl.getAttribute('data-response-label') !== nextLabel) {
      blockEl.setAttribute('data-response-label', nextLabel);
      changed = true;
    }
    if (blockEl.getAttribute('data-response-points') !== nextPoints) {
      blockEl.setAttribute('data-response-points', nextPoints);
      changed = true;
    }
    if (blockEl.getAttribute('data-response-required') !== nextRequired) {
      blockEl.setAttribute('data-response-required', nextRequired);
      changed = true;
    }
    const nextHtml = buildResponseAreaBlockHtml(nextType, config);
    if (blockEl.innerHTML !== nextHtml) {
      blockEl.innerHTML = nextHtml;
      changed = true;
    }
  });

  return changed ? container.innerHTML : html;
};

const ensureStepDeleteControlsInHtml = (html = '') => {
  if (!html || !html.includes('document-step-block')) return html || '';
  const container = document.createElement('div');
  container.innerHTML = html;
  let changed = false;

  container.querySelectorAll('.document-step-block').forEach((block) => {
    const shell = block.querySelector('.document-step-shell');
    const header = block.querySelector('.document-step-header');
    const toolbar = block.querySelector('.document-step-toolbar');
    if (!shell || !header) return;

    if (!shell.querySelector('.document-step-delete-button')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'document-step-delete-button';
      button.setAttribute('data-step-action', 'delete');
      button.setAttribute('contenteditable', 'false');
      button.setAttribute('title', 'Delete step');
      button.setAttribute('aria-label', 'Delete step');
      button.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M3 6h18" />
          <path d="M8 6V4h8v2" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
      `;
      shell.insertBefore(button, header);
      changed = true;
    }

    toolbar?.querySelectorAll('[data-step-action="delete"]').forEach((node) => {
      node.remove();
      changed = true;
    });

    const embedButtons = toolbar
      ? Array.from(toolbar.querySelectorAll('[data-step-action="html"], [data-step-action="url"], [data-step-action="youtube"]'))
      : [];
    const embedBlocks = Array.from(block.querySelectorAll('.document-step-embed-block'));
    const hasEmbed = embedBlocks.length > 0;

    embedButtons.forEach((button) => {
      const desiredDisplay = hasEmbed ? 'none' : '';
      if (button.style.display !== desiredDisplay) {
        button.style.display = desiredDisplay;
        changed = true;
      }
    });

    embedBlocks.forEach((embedBlock) => {
      if (!embedBlock.querySelector('.document-step-embed-edit')) {
        const editButton = document.createElement('button');
        editButton.type = 'button';
        editButton.className = 'document-step-embed-edit';
        editButton.setAttribute('data-step-embed-action', 'edit');
        editButton.setAttribute('contenteditable', 'false');
        editButton.setAttribute('title', 'Edit embed');
        editButton.setAttribute('aria-label', 'Edit embed');
        editButton.textContent = 'Edit embed';
        embedBlock.appendChild(editButton);
        changed = true;
      }
    });

    if (toolbar && !toolbar.querySelector('[data-step-action="audio"]')) {
      const imageButton = toolbar.querySelector('[data-step-action="image"]');
      const audioButton = document.createElement('button');
      audioButton.type = 'button';
      audioButton.className = 'document-step-action';
      audioButton.setAttribute('data-step-action', 'audio');
      audioButton.textContent = 'Audio';
      if (imageButton?.nextSibling) {
        toolbar.insertBefore(audioButton, imageButton.nextSibling);
      } else {
        toolbar.appendChild(audioButton);
      }
      changed = true;
    }

    block.querySelectorAll('.document-step-audio-block').forEach((audioBlock) => {
      const actionsHost = audioBlock.querySelector('.document-step-audio-actions');
      if (!actionsHost) {
        const header = audioBlock.querySelector('.document-step-audio-header');
        if (!header) return;
        const nextActions = document.createElement('div');
        nextActions.className = 'document-step-audio-actions';

        const replaceButton = document.createElement('button');
        replaceButton.type = 'button';
        replaceButton.className = 'document-step-audio-action';
        replaceButton.setAttribute('data-step-audio-action', 'replace');
        replaceButton.textContent = 'Replace';

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'document-step-audio-action';
        removeButton.setAttribute('data-step-audio-action', 'remove');
        removeButton.textContent = 'Remove';

        nextActions.appendChild(replaceButton);
        nextActions.appendChild(removeButton);
        header.appendChild(nextActions);
        changed = true;
      }
    });
  });

  return changed ? container.innerHTML : html;
};

const findAtomicBefore = (range) => {
  const { startContainer, startOffset } = range;
  if (startContainer.nodeType === Node.TEXT_NODE) {
    if (startOffset === 0 || isZWS(startContainer)) {
      let prev = startContainer.previousSibling;
      if (isZWS(prev)) prev = prev.previousSibling;
      return isAtomicBlock(prev) ? prev : null;
    }
  } else if (startOffset > 0) {
    let prev = startContainer.childNodes[startOffset - 1];
    if (isZWS(prev) && startOffset > 1) prev = startContainer.childNodes[startOffset - 2];
    return isAtomicBlock(prev) ? prev : null;
  }
  return null;
};

const findAtomicAfter = (range) => {
  const { endContainer, endOffset } = range;
  if (endContainer.nodeType === Node.TEXT_NODE) {
    if (endOffset === endContainer.textContent.length || isZWS(endContainer)) {
      let next = endContainer.nextSibling;
      if (isZWS(next)) next = next.nextSibling;
      return isAtomicBlock(next) ? next : null;
    }
  } else if (endOffset < endContainer.childNodes.length) {
    let next = endContainer.childNodes[endOffset];
    if (isZWS(next) && endOffset + 1 < endContainer.childNodes.length) {
      next = endContainer.childNodes[endOffset + 1];
    }
    return isAtomicBlock(next) ? next : null;
  }
  return null;
};

// Prepend overflow HTML to a page, inserting after any existing header
const prependOverflowToPage = (pageHtml, overflowHtml) => {
  if (!overflowHtml) return pageHtml || '';
  if (!pageHtml) return overflowHtml;
  const container = document.createElement('div');
  container.innerHTML = pageHtml;
  const header = container.querySelector('.doc-header');
  if (header) {
    header.insertAdjacentHTML('afterend', overflowHtml);
  } else {
    container.insertAdjacentHTML('afterbegin', overflowHtml);
  }
  return container.innerHTML;
};

// Append overflow HTML to a page, inserting before any existing footer
const appendOverflowToPage = (pageHtml, overflowHtml) => {
  if (!overflowHtml) return pageHtml || '';
  if (!pageHtml) return overflowHtml;
  const container = document.createElement('div');
  container.innerHTML = pageHtml;
  const footer = container.querySelector('.doc-footer');
  if (footer) {
    footer.insertAdjacentHTML('beforebegin', overflowHtml);
  } else {
    container.insertAdjacentHTML('beforeend', overflowHtml);
  }
  return container.innerHTML;
};

const getPageBodyHtml = (pageHtml) => {
  if (!pageHtml) return '';
  const container = document.createElement('div');
  container.innerHTML = pageHtml;
  container.querySelectorAll('.doc-header, .doc-footer').forEach((node) => node.remove());
  return container.innerHTML;
};

const getPageRegionsHtml = (pageHtml) => {
  const container = document.createElement('div');
  container.innerHTML = pageHtml || '';
  const header = container.querySelector('.doc-header');
  const footer = container.querySelector('.doc-footer');
  const headerHtml = header ? header.outerHTML : '';
  const footerHtml = footer ? footer.outerHTML : '';
  if (header) header.remove();
  if (footer) footer.remove();
  return {
    headerHtml,
    bodyHtml: container.innerHTML,
    footerHtml,
  };
};

const buildPageHtmlFromRegions = ({ headerHtml = '', bodyHtml = '', footerHtml = '' }) =>
  `${headerHtml}${bodyHtml}${footerHtml}`;

const resetPageBodyScroll = (root) => {
  if (!root) return;
  const bodies = root.matches?.('[data-page-body="true"]')
    ? [root]
    : Array.from(root.querySelectorAll?.('[data-page-body="true"]') || []);
  bodies.forEach((body) => {
    if (!body) return;
    if (body.scrollTop !== 0) body.scrollTop = 0;
    if (body.scrollLeft !== 0) body.scrollLeft = 0;
  });
};

const getPageBodyVisualOverflowPx = (body) => {
  if (!body) return 0;
  const bodyRect = body.getBoundingClientRect();
  let contentBottom = bodyRect.top;
  Array.from(body.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const rect = node.getBoundingClientRect();
      if (rect.width || rect.height) contentBottom = Math.max(contentBottom, rect.bottom);
      return;
    }
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      const range = document.createRange();
      range.selectNodeContents(node);
      Array.from(range.getClientRects()).forEach((rect) => {
        contentBottom = Math.max(contentBottom, rect.bottom);
      });
    }
  });
  return Math.max(0, contentBottom - bodyRect.bottom);
};

const updatePageShellRegionSpacing = (shell) => {
  if (!shell) return;
  const headerHost = shell.querySelector('[data-page-header-region="true"]');
  const footerHost = shell.querySelector('[data-page-footer-region="true"]');
  if (!headerHost || !footerHost) return;

  const headerHeight = headerHost.offsetHeight || 0;
  const footerHeight = footerHost.offsetHeight || 0;
  shell.style.setProperty(
    '--page-region-top-space',
    `${Math.max(MIN_REGION_SPACE_PX, headerHeight > 0 ? headerHeight + REGION_GAP_PX : MIN_REGION_SPACE_PX)}px`
  );
  shell.style.setProperty(
    '--page-region-bottom-space',
    `${Math.max(MIN_REGION_SPACE_PX, footerHeight > 0 ? footerHeight + REGION_GAP_PX : MIN_REGION_SPACE_PX)}px`
  );
};

const setPageShellContent = (shell, pageHtml) => {
  if (!shell) return;
  const headerHost = shell.querySelector('[data-page-header-region="true"]');
  const bodyHost = shell.querySelector('[data-page-body="true"]');
  const footerHost = shell.querySelector('[data-page-footer-region="true"]');
  if (!headerHost || !bodyHost || !footerHost) return;

  const { headerHtml, bodyHtml, footerHtml } = getPageRegionsHtml(pageHtml);
  headerHost.innerHTML = headerHtml;
  bodyHost.innerHTML = bodyHtml;
  footerHost.innerHTML = footerHtml;
  resetPageBodyScroll(bodyHost);
  updatePageShellRegionSpacing(shell);
};

const getPageHtmlFromShell = (shell) => {
  if (!shell) return '';
  const headerHost = shell.querySelector('[data-page-header-region="true"]');
  const bodyHost = shell.querySelector('[data-page-body="true"]');
  const footerHost = shell.querySelector('[data-page-footer-region="true"]');
  return buildPageHtmlFromRegions({
    headerHtml: headerHost?.innerHTML || '',
    bodyHtml: bodyHost?.innerHTML || '',
    footerHtml: footerHost?.innerHTML || '',
  });
};

const PAGE_PADDING_MM = 25.4;
const PAGE_PADDING_TOP_MM = PAGE_PADDING_MM / 2;
const PAGE_PADDING_BOTTOM_MM = PAGE_PADDING_MM;
const MIN_REGION_SPACE_PX = 8;
const REGION_GAP_PX = 8;

const splitPageOffscreen = (html, measureWidthPx, measureHeightPx) => {
  if (!html) return null;

  const widthCss = measureWidthPx
    ? `width:${measureWidthPx}px;`
    : `width:calc(210mm - ${PAGE_PADDING_MM * 2}mm);`;

  // Strip header/footer — they are position:absolute in the real editor
  // and must not participate in overflow measurement
  const { headerHtml: hdrHtml, bodyHtml, footerHtml: ftrHtml } = getPageRegionsHtml(html);
  const measureRegionSpacePx = (regionHtml) => {
    if (!regionHtml) return MIN_REGION_SPACE_PX;
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;left:-9999px;top:0;visibility:hidden;' +
      widthCss +
      'box-sizing:border-box;font-size:14px;line-height:1.6;font-family:Inter,system-ui,sans-serif;';
    probe.innerHTML = regionHtml;
    document.body.appendChild(probe);
    try {
      const h = Math.ceil(probe.getBoundingClientRect().height);
      return Math.max(MIN_REGION_SPACE_PX, h > 0 ? h + REGION_GAP_PX : MIN_REGION_SPACE_PX);
    } finally {
      document.body.removeChild(probe);
    }
  };
  const topRegionSpacePx = measureRegionSpacePx(hdrHtml);
  const bottomRegionSpacePx = measureRegionSpacePx(ftrHtml);
  const container = document.createElement('div');
  container.className = 'page-body-editor';
  container.style.cssText =
    'position:absolute;left:-9999px;top:0;visibility:hidden;' +
    widthCss +
    (measureHeightPx > 0
      ? `height:${measureHeightPx}px;`
      : `height:calc(297mm - ${PAGE_PADDING_TOP_MM + PAGE_PADDING_BOTTOM_MM}mm - ${topRegionSpacePx + bottomRegionSpacePx}px);`) +
    'box-sizing:border-box;overflow:hidden;' +
    'font-size:14px;line-height:1.6;font-family:Inter,system-ui,sans-serif;';
  document.body.appendChild(container);
  container.innerHTML = bodyHtml;
  try {
    const childHtml = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) return node.outerHTML;
      if (node.nodeType === Node.TEXT_NODE) {
        const span = document.createElement('span');
        span.textContent = node.textContent || '';
        return span.innerHTML;
      }
      return '';
    };

    const nodeBounds = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const r = node.getBoundingClientRect();
        return { top: r.top, bottom: r.bottom };
      }
      if (node.nodeType === Node.TEXT_NODE) {
        if (!node.textContent.trim()) return null;
        const range = document.createRange();
        range.selectNodeContents(node);
        const r = range.getBoundingClientRect();
        return { top: r.top, bottom: r.bottom };
      }
      return null;
    };

    const cloneElementShallow = (el) => {
      const clone = document.createElement(el.tagName);
      for (const attr of el.attributes) clone.setAttribute(attr.name, attr.value);
      return clone;
    };

    const prefixFits = (children, endExclusive) => {
      if (endExclusive <= 0) return true;
      const probe = document.createElement('div');
      probe.className = container.className;
      probe.style.cssText = container.style.cssText;
      probe.innerHTML = children.slice(0, endExclusive).map((child) => childHtml(child)).join('');
      document.body.appendChild(probe);
      try {
        return probe.scrollHeight <= probe.clientHeight + 4;
      } finally {
        document.body.removeChild(probe);
      }
    };

    const splitTableChild = (children, table, childIndex, cutoff) => {
      if (table.tagName !== 'TABLE') return null;
      const directBodies = Array.from(table.children).filter((n) => n.tagName === 'TBODY');
      const body = directBodies[0];
      const rows = body ? Array.from(body.children).filter((n) => n.tagName === 'TR') : [];
      if (!rows.length) return null;

      let hasRowsAbove = false;
      let splitRow = -1;
      for (let j = 0; j < rows.length; j++) {
        const bounds = nodeBounds(rows[j]);
        if (!bounds) continue;
        if (bounds.top < cutoff) hasRowsAbove = true;
        // Row starts at or below cutoff — split before it
        if (bounds.top >= cutoff && hasRowsAbove) {
          splitRow = j;
          break;
        }
        // Row straddles the cutoff (top above, bottom below) — split before it
        if (bounds.bottom > cutoff && hasRowsAbove && j > 0) {
          splitRow = j;
          break;
        }
      }
      if (splitRow < 0) return null;

      const buildTable = (fromRow, toRow, repeatHead = false) => {
        const tableClone = cloneElementShallow(table);
        Array.from(table.children).forEach((childNode) => {
          if (childNode.tagName === 'CAPTION' || childNode.tagName === 'COLGROUP') {
            tableClone.appendChild(childNode.cloneNode(true));
          }
          if (childNode.tagName === 'THEAD' && (fromRow === 0 || repeatHead)) {
            tableClone.appendChild(childNode.cloneNode(true));
          }
        });
        const tbodyClone = body ? cloneElementShallow(body) : document.createElement('tbody');
        rows.slice(fromRow, toRow).forEach((row) => tbodyClone.appendChild(row.cloneNode(true)));
        tableClone.appendChild(tbodyClone);
        const tfoot = Array.from(table.children).find((n) => n.tagName === 'TFOOT');
        if (tfoot && toRow === rows.length) {
          tableClone.appendChild(tfoot.cloneNode(true));
        }
        return tableClone;
      };

      const fp = [];
      for (let k = 0; k < childIndex; k++) fp.push(childHtml(children[k]));
      fp.push(buildTable(0, splitRow, false).outerHTML);

      const op = [buildTable(splitRow, rows.length, true).outerHTML];
      for (let k = childIndex + 1; k < children.length; k++) op.push(childHtml(children[k]));
      return { fits: hdrHtml + fp.join('') + ftrHtml, overflow: op.join('') };
    };

    const splitListElement = (list, cutoff) => {
      if (list.tagName !== 'OL' && list.tagName !== 'UL') return null;
      const items = Array.from(list.children).filter((node) => node.tagName === 'LI');
      if (items.length < 2) return null;

      let splitItem = -1;
      for (let index = 0; index < items.length; index += 1) {
        const bounds = nodeBounds(items[index]);
        if (bounds && (bounds.top >= cutoff || bounds.bottom > cutoff)) {
          splitItem = index;
          break;
        }
      }
      if (splitItem < 1) return null;

      const kept = cloneElementShallow(list);
      items.slice(0, splitItem).forEach((item) => kept.appendChild(item.cloneNode(true)));
      const moved = cloneElementShallow(list);
      if (list.tagName === 'OL' && !moved.hasAttribute('reversed')) {
        const initialStart = Number.parseInt(list.getAttribute('start') || '1', 10);
        moved.setAttribute('start', String((Number.isFinite(initialStart) ? initialStart : 1) + splitItem));
      }
      items.slice(splitItem).forEach((item) => moved.appendChild(item.cloneNode(true)));

      return { kept, moved };
    };

    const splitListChild = (children, list, childIndex, cutoff) => {
      const split = splitListElement(list, cutoff);
      if (!split) return null;

      const fits = [];
      for (let index = 0; index < childIndex; index += 1) fits.push(childHtml(children[index]));
      fits.push(split.kept.outerHTML);
      const overflow = [split.moved.outerHTML];
      for (let index = childIndex + 1; index < children.length; index += 1) {
        overflow.push(childHtml(children[index]));
      }
      return { fits: hdrHtml + fits.join('') + ftrHtml, overflow: overflow.join('') };
    };

    const trySplitWithinChild = (children, child, childIndex, cutoff) => {
      if (child.nodeType !== Node.ELEMENT_NODE) return null;
      if (
        child.classList.contains('response-area-block') ||
        child.classList.contains('document-step-block') ||
        child.classList.contains('section-divider-block') ||
        child.classList.contains('math-container') ||
        child.classList.contains('image-container')
      ) {
        return null;
      }
      const tableSplit = splitTableChild(children, child, childIndex, cutoff);
      if (tableSplit) return tableSplit;
      const listSplit = splitListChild(children, child, childIndex, cutoff);
      if (listSplit) return listSplit;
      const isList = child.tagName === 'OL' || child.tagName === 'UL';
      const hasMeaningfulPrefix = children.slice(0, childIndex).some((node) =>
        node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
      );
      // Give an overflowing first item a fresh page before splitting it. If it
      // still cannot fit there, the nested path below creates an unnumbered
      // continuation fragment instead of another numbered item.
      if (isList && hasMeaningfulPrefix) return null;
      const splitElementTextAtCutoff = (element) => {
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        let textNode = null;
        while (walker.nextNode()) {
          const node = walker.currentNode;
          const text = node.textContent || '';
          if (!text.trim() || text.length < 2) continue;
          const bounds = nodeBounds(node);
          if (!bounds || bounds.bottom <= cutoff || bounds.top >= cutoff) continue;
          textNode = node;
          break;
        }
        if (!textNode) return null;

        const text = textNode.textContent || '';
        let lo = 0;
        let hi = text.length;
        const probe = document.createRange();
        while (lo < hi) {
          const mid = Math.floor((lo + hi) / 2);
          probe.setStart(textNode, 0);
          probe.setEnd(textNode, mid);
          const rects = Array.from(probe.getClientRects());
          const bottom = rects.length ? rects[rects.length - 1].bottom : cutoff;
          if (bottom <= cutoff) lo = mid + 1; else hi = mid;
        }

        let splitPos = lo;
        while (splitPos > 0 && text[splitPos - 1] !== ' ' && text[splitPos - 1] !== '\n') splitPos--;
        if (splitPos === 0 && lo > 0) splitPos = lo;
        if (splitPos <= 0 || splitPos >= text.length) return null;

        const keptRange = document.createRange();
        keptRange.setStart(element, 0);
        keptRange.setEnd(textNode, splitPos);
        const movedRange = document.createRange();
        movedRange.setStart(textNode, splitPos);
        movedRange.setEnd(element, element.childNodes.length);

        const kept = cloneElementShallow(element);
        kept.appendChild(keptRange.cloneContents());
        const moved = cloneElementShallow(element);
        moved.appendChild(movedRange.cloneContents());

        if (!hasMeaningfulBodyContent(kept.innerHTML) || !hasMeaningfulBodyContent(moved.innerHTML)) {
          return null;
        }
        return { kept, moved };
      };
      const subs = Array.from(child.childNodes);
      if (!subs.length) return null;
      let hasSAbove = false;
      for (let j = 0; j < subs.length; j++) {
        const sub = subs[j];
        const bounds = nodeBounds(sub);
        if (!bounds) continue;
        const { top: sTop } = bounds;
        if (sTop < cutoff) hasSAbove = true;
        if (sTop >= cutoff && hasSAbove) {
          const kept = document.createElement(child.tagName);
          for (const a of child.attributes) kept.setAttribute(a.name, a.value);
          const moved = kept.cloneNode(false);
          subs.forEach((sc, idx) => (idx < j ? kept : moved).appendChild(sc.cloneNode(true)));
          const fp = [];
          for (let k = 0; k < childIndex; k++) fp.push(childHtml(children[k]));
          fp.push(kept.outerHTML);
          const op = [moved.outerHTML];
          for (let k = childIndex + 1; k < children.length; k++) op.push(childHtml(children[k]));
          return { fits: hdrHtml + fp.join('') + ftrHtml, overflow: op.join('') };
        }
        if (
          sub.nodeType === Node.ELEMENT_NODE &&
          bounds.bottom > cutoff &&
          bounds.top < cutoff &&
          !isAtomicBlock(sub)
        ) {
          const nestedSplit = splitListElement(sub, cutoff) || splitElementTextAtCutoff(sub);
          if (nestedSplit) {
            const kept = cloneElementShallow(child);
            const moved = cloneElementShallow(child);
            const isSplitListItem =
              (child.tagName === 'OL' || child.tagName === 'UL') && sub.tagName === 'LI';
            if (isSplitListItem) {
              nestedSplit.moved.classList.add('list-item-continuation');
              nestedSplit.moved.setAttribute('data-list-item-continuation', 'true');
              if (child.tagName === 'OL') {
                const initialStart = Number.parseInt(child.getAttribute('start') || '1', 10);
                const priorItems = subs.slice(0, j).filter((node) => node.tagName === 'LI').length;
                const logicalValue = (Number.isFinite(initialStart) ? initialStart : 1) + priorItems;
                moved.setAttribute('start', String(logicalValue));
                nestedSplit.moved.setAttribute('value', String(logicalValue));
              }
            }
            subs.forEach((sc, idx) => {
              if (idx < j) kept.appendChild(sc.cloneNode(true));
              else if (idx > j) moved.appendChild(sc.cloneNode(true));
              else {
                kept.appendChild(nestedSplit.kept);
                moved.appendChild(nestedSplit.moved);
              }
            });
            const fp = [];
            for (let k = 0; k < childIndex; k++) fp.push(childHtml(children[k]));
            fp.push(kept.outerHTML);
            const op = [moved.outerHTML];
            for (let k = childIndex + 1; k < children.length; k++) op.push(childHtml(children[k]));
            return { fits: hdrHtml + fp.join('') + ftrHtml, overflow: op.join('') };
          }
        }
      }
      // Sub-node boundaries didn't work — try splitting within a wrapping text node
      for (let j = 0; j < subs.length; j++) {
        const sub = subs[j];
        if (sub.nodeType !== Node.TEXT_NODE) continue;
        const bnds = nodeBounds(sub);
        if (!bnds || bnds.bottom <= cutoff || bnds.top >= cutoff) continue;
        const text = sub.textContent;
        if (!text || text.length < 2) continue;
        let lo = 0, hi = text.length;
        const testRange = document.createRange();
        while (lo < hi) {
          const mid = Math.floor((lo + hi) / 2);
          testRange.setStart(sub, mid);
          testRange.setEnd(sub, mid);
          const rct = testRange.getBoundingClientRect();
          if (rct.top < cutoff) lo = mid + 1; else hi = mid;
        }
        // Back up to a word boundary
        let splitPos = lo;
        while (splitPos > 0 && text[splitPos - 1] !== ' ' && text[splitPos - 1] !== '\n') splitPos--;
        if (splitPos === 0 && lo > 0) splitPos = lo;
        if (splitPos <= 0 || splitPos >= text.length) continue;
        const keptEl = cloneElementShallow(child);
        const movedEl = cloneElementShallow(child);
        subs.forEach((sc, idx) => {
          if (idx < j) keptEl.appendChild(sc.cloneNode(true));
          else if (idx > j) movedEl.appendChild(sc.cloneNode(true));
          else {
            keptEl.appendChild(document.createTextNode(text.substring(0, splitPos)));
            movedEl.appendChild(document.createTextNode(text.substring(splitPos)));
          }
        });
        const fp2 = [];
        for (let k = 0; k < childIndex; k++) fp2.push(childHtml(children[k]));
        fp2.push(keptEl.outerHTML);
        const op2 = [movedEl.outerHTML];
        for (let k = childIndex + 1; k < children.length; k++) op2.push(childHtml(children[k]));
        return { fits: hdrHtml + fp2.join('') + ftrHtml, overflow: op2.join('') };
      }
      return null;
    };

    const rect = container.getBoundingClientRect();
    if (container.scrollHeight <= container.clientHeight + 4) return null;
    const cutoff = rect.top + container.clientHeight;
    const children = Array.from(container.childNodes);
    let splitIdx = -1;
    let hasAbove = false;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      let cTop, cBot;
      if (child.nodeType === Node.ELEMENT_NODE) {
        const cr = child.getBoundingClientRect();
        cTop = cr.top; cBot = cr.bottom;
      } else if (child.nodeType === Node.TEXT_NODE) {
        if (!child.textContent.trim()) continue;
        const tr = document.createRange();
        tr.selectNodeContents(child);
        const trr = tr.getBoundingClientRect();
        cTop = trr.top; cBot = trr.bottom;
      } else continue;
      if (cTop >= cutoff) {
        if (hasAbove) {
          if (prefixFits(children, i + 1)) {
            hasAbove = true;
            continue;
          }
          splitIdx = i;
          break;
        }
        continue;
      }
      if (cBot > cutoff && splitIdx === -1) {
        const innerSplit = trySplitWithinChild(children, child, i, cutoff);
        if (innerSplit) return innerSplit;
        if (hasAbove) {
          if (prefixFits(children, i + 1)) {
            hasAbove = true;
            continue;
          }
          splitIdx = i;
          break;
        }
      }
      hasAbove = true;
    }
    if (splitIdx < 0) {
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.getBoundingClientRect().bottom <= cutoff) continue;
          const innerSplit = trySplitWithinChild(children, child, i, cutoff);
          if (innerSplit) return innerSplit;
          break;
        }
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent || '';
          if (!text.trim() || text.length < 2) continue;
          const tr = document.createRange();
          tr.selectNodeContents(child);
          const trr = tr.getBoundingClientRect();
          if (trr.bottom <= cutoff) continue;

          let lo = 0;
          let hi = text.length;
          const probe = document.createRange();
          while (lo < hi) {
            const mid = Math.floor((lo + hi) / 2);
            probe.setStart(child, mid);
            probe.setEnd(child, mid);
            const rct = probe.getBoundingClientRect();
            if (rct.top < cutoff) lo = mid + 1;
            else hi = mid;
          }

          let splitPos = lo;
          while (splitPos > 0 && text[splitPos - 1] !== ' ' && text[splitPos - 1] !== '\n') splitPos--;
          if (splitPos === 0 && lo > 0) splitPos = lo;
          if (splitPos <= 0 || splitPos >= text.length) continue;

          const fp2 = [];
          for (let k = 0; k < i; k++) fp2.push(childHtml(children[k]));
          fp2.push(text.substring(0, splitPos));
          const op2 = [text.substring(splitPos)];
          for (let k = i + 1; k < children.length; k++) op2.push(childHtml(children[k]));
          return { fits: hdrHtml + fp2.join('') + ftrHtml, overflow: op2.join('') };
        }
      }
      return null;
    }
    const fp = [], op = [];
    children.forEach((c, i) => {
      const h = childHtml(c);
      (i < splitIdx ? fp : op).push(h);
    });
    return { fits: hdrHtml + fp.join('') + ftrHtml, overflow: op.join('') };
  } finally {
    document.body.removeChild(container);
  }
};

const ensureRegionInHtml = (html, regionClass) => {
  const container = document.createElement('div');
  container.innerHTML = html || '';
  if (!container.querySelector(`.${regionClass}`)) {
    const region = document.createElement('div');
    region.className = regionClass;
    region.setAttribute(`data-${regionClass}`, 'true');
    region.innerHTML = '<span class="doc-region-placeholder">&nbsp;</span>';
    container.prepend(region);
  }
  return container.innerHTML;
};

const appendFooterInHtml = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html || '';
  if (!container.querySelector('.doc-footer')) {
    const region = document.createElement('div');
    region.className = 'doc-footer';
    region.setAttribute('data-doc-footer', 'true');
    region.innerHTML = '<span class="doc-region-placeholder">&nbsp;</span>';
    container.appendChild(region);
  }
  return container.innerHTML;
};

// Extract header/footer HTML from the first page that has them
const getRegionsFromPages = (pages) => {
  for (const html of pages) {
    if (!html) continue;
    const container = document.createElement('div');
    container.innerHTML = html;
    const header = container.querySelector('.doc-header');
    const footer = container.querySelector('.doc-footer');
    if (header || footer) {
      return {
        headerHtml: header ? header.outerHTML : '',
        footerHtml: footer ? footer.outerHTML : '',
      };
    }
  }
  return { headerHtml: '', footerHtml: '' };
};

// Build a new page that inherits header/footer from existing pages
const newPageWithRegions = (pages, bodyHtml = '', pageIndex = pages.length) => {
  const { headerHtml, footerHtml } = getRegionsFromPages(pages);
  const headerContainer = document.createElement('div');
  headerContainer.innerHTML = headerHtml;
  const repeatHeader = headerContainer.querySelector('.doc-header')?.getAttribute('data-header-repeat') === 'all';
  const pageHeaderHtml = pageIndex === 0 || repeatHeader ? headerHtml : '';
  return pageHeaderHtml + bodyHtml + footerHtml;
};

const repaginatePages = (pages, measureWidthPx, measureHeightPx) => {
  const sourcePages = Array.isArray(pages) && pages.length > 0 ? pages : [''];
  const combinedBodyHtml = sourcePages.map((html) => getPageBodyHtml(html || '')).join('');
  const nextPages = [];

  if (!hasMeaningfulBodyContent(combinedBodyHtml)) {
    return [newPageWithRegions(sourcePages, '', 0)];
  }

  let remainingHtml = combinedBodyHtml;
  let guard = 0;

  while (remainingHtml && guard < 200) {
    const candidate = newPageWithRegions(sourcePages, remainingHtml, nextPages.length);
    const split = splitPageOffscreen(candidate, measureWidthPx, measureHeightPx);

    if (!split || !split.overflow) {
      nextPages.push(candidate);
      remainingHtml = '';
      break;
    }

    if (!hasMeaningfulBodyContent(getPageBodyHtml(split.fits))) {
      if (split.overflow === remainingHtml) {
        nextPages.push(candidate);
        remainingHtml = '';
        break;
      }
      remainingHtml = split.overflow;
      guard += 1;
      continue;
    }

    if (split.overflow === remainingHtml) {
      nextPages.push(candidate);
      remainingHtml = '';
      break;
    }

    nextPages.push(split.fits);
    remainingHtml = split.overflow;
    guard += 1;
  }

  if (remainingHtml) {
    nextPages.push(newPageWithRegions(sourcePages, remainingHtml, nextPages.length));
  }

  return nextPages.length > 0 ? nextPages : [newPageWithRegions(sourcePages, '', 0)];
};

const getPageNumberStart = (pageNumberEl, fallback = 1) => {
  const parsed = Number.parseInt(pageNumberEl?.getAttribute('data-page-number-start') || fallback, 10);
  return Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
};

const getPageNumberText = (pageIndex, totalPages, startNumber = 1) =>
  `Page ${startNumber + pageIndex} of ${startNumber + totalPages - 1}`;

const upsertPageNumberInHtml = (html, region, align, pageIndex, totalPages, startNumber = 1) => {
  const container = document.createElement('div');
  container.innerHTML = html || '';
  const regionClass = region === 'header' ? 'doc-header' : 'doc-footer';
  let regionEl = container.querySelector(`.${regionClass}`);
  if (!regionEl) {
    if (region === 'header') {
      html = ensureRegionInHtml(container.innerHTML, 'doc-header');
    } else {
      html = appendFooterInHtml(container.innerHTML);
    }
    container.innerHTML = html;
    regionEl = container.querySelector(`.${regionClass}`);
  }
  if (!regionEl) return container.innerHTML;

  let pageNumberEl = regionEl.querySelector('.page-number');
  if (!pageNumberEl) {
    pageNumberEl = document.createElement('div');
    pageNumberEl.className = `page-number align-${align}`;
    pageNumberEl.setAttribute('data-page-number', 'true');
    regionEl.appendChild(pageNumberEl);
  } else {
    pageNumberEl.className = `page-number align-${align}`;
  }
  const normalizedStart = getPageNumberStart(null, startNumber);
  pageNumberEl.setAttribute('data-page-number-start', String(normalizedStart));
  pageNumberEl.textContent = getPageNumberText(pageIndex, totalPages, normalizedStart);
  return container.innerHTML;
};

const isWithinHeaderOrFooter = (node) => {
  if (!node) return false;
  if (node.nodeType === Node.ELEMENT_NODE) {
    return !!node.closest('.doc-header, .doc-footer');
  }
  return !!node.parentElement?.closest('.doc-header, .doc-footer');
};

/** Save cursor position as character offset from container start */
const saveCursorOffset = (container) => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return -1;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.startContainer)) return -1;
  try {
    const pre = document.createRange();
    pre.setStart(container, 0);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString().length;
  } catch (_) { return -1; }
};

/** Restore cursor to a character offset within container */
const restoreCursorOffset = (container, offset) => {
  if (offset < 0 || !container) return;
  const sel = window.getSelection();
  if (!sel) return;
  const maxLen = container.textContent?.length || 0;
  let remaining = Math.min(offset, maxLen);
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    if (remaining <= node.textContent.length) {
      try {
        const r = document.createRange();
        r.setStart(node, remaining);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      } catch (_) { /* ignore */ }
      return;
    }
    remaining -= node.textContent.length;
  }
  // Fallback: set to end
  try {
    const r = document.createRange();
    r.selectNodeContents(container);
    r.collapse(false);
    sel.removeAllRanges();
    sel.addRange(r);
  } catch (_) { /* ignore */ }
};

const stripCursorMarkerFromHtml = (html) => {
  if (!html || !html.includes('data-cursor-marker')) return html || '';
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('[data-cursor-marker]').forEach((n) => n.remove());
  return container.innerHTML;
};

const injectCursorMarkerAtSelection = (container) => {
  if (!container) return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.startContainer)) return false;
  const marker = document.createElement('span');
  marker.setAttribute('data-cursor-marker', 'true');
  marker.style.display = 'inline-block';
  marker.style.width = '0';
  marker.style.overflow = 'hidden';
  marker.textContent = '\u200B';
  const r = range.cloneRange();
  r.collapse(true);
  r.insertNode(marker);
  r.setStartAfter(marker);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  return true;
};

const restoreCursorFromMarker = (pageBodiesMap, fallbackBody, fallbackOffset = -1) => {
  const values = Object.values(pageBodiesMap.current || {});
  for (const body of values) {
    if (!body) continue;
    const marker = body.querySelector('[data-cursor-marker]');
    if (!marker) continue;
    const sel = window.getSelection();
    const r = document.createRange();
    r.setStartBefore(marker);
    r.collapse(true);
    marker.remove();
    sel?.removeAllRanges();
    sel?.addRange(r);
    // If the restored position is inside a nested contenteditable (e.g. step
    // body), focus that element so the browser places the caret correctly
    // within the nested editing context instead of the outer page body.
    const startNode = r.startContainer;
    const el = startNode.nodeType === Node.ELEMENT_NODE ? startNode : startNode.parentElement;
    const nestedEditable = el?.closest?.(
      '.document-step-body, .document-step-number, .document-step-title'
    );
    if (nestedEditable && body.contains(nestedEditable)) {
      nestedEditable.focus();
    } else {
      body.focus();
    }
    return true;
  }
  if (fallbackBody && fallbackOffset >= 0) {
    fallbackBody.focus();
    restoreCursorOffset(fallbackBody, fallbackOffset);
    return true;
  }
  return false;
};

const moveCaretToBody = (editor, position = 'start') => {
  if (!editor) return;
  let targetNode = null;
  const children = Array.from(editor.childNodes);
  const orderedChildren = position === 'end' ? [...children].reverse() : children;
  for (const child of orderedChildren) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      if (child.classList.contains('doc-header') || child.classList.contains('doc-footer')) continue;
      targetNode = child;
      break;
    }
    if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
      targetNode = child;
      break;
    }
  }

  if (!targetNode) {
    const p = document.createElement('p');
    p.innerHTML = '<br>';
    const footer = editor.querySelector('.doc-footer');
    if (footer) {
      editor.insertBefore(p, footer);
    } else {
      editor.appendChild(p);
    }
    targetNode = p;
  }

  const range = document.createRange();
  if (targetNode.nodeType === Node.TEXT_NODE) {
    const offset = position === 'end' ? targetNode.textContent.length : 0;
    range.setStart(targetNode, offset);
  } else {
    range.selectNodeContents(targetNode);
    range.collapse(position !== 'end');
  }
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }
};


const NOOP_FN = () => {};

const LegacyDocumentEditor = ({ documentId, className, debugInitialDocument = null, useV2Engine = false }) => {
  const v2EngineRef = useRef(null);
  const v2EditorDomRef = useRef(null);
  const navigate = useNavigate();
  const { user, userData, isAdmin } = useAuthStore();
  const needsProfileCompletion = userData?.requiresProfileCompletion === true;
  const isUserAdmin = isAdmin();
  const {
    currentDocument,
    setCurrentDocument,
    updateDocument,
    createDocument,
    loadDocuments,
    deleteDocument,
    loadDocument: storeLoadDocument,
    addResponseArea,
    removeResponseArea,
    uploadPdfFile,
    removePdfFile,
    uploadAudioResource,
    removeAudioResource,
  } = useDocumentStore();

  const editorRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const selectionRef = useRef(null);
  const mathRenderTimer = useRef(null);
  const textSizeSteps = useRef([10, 12, 14, 16, 18, 24, 32]);
  const overflowTimerRef = useRef(null);
  const allowUnderflowRepaginationRef = useRef(false);
  const pendingCursorOffsetRef = useRef(-1);
  const pendingCursorPageRef = useRef(-1);
  const handleSaveRef = useRef(null);
  const saveInFlightRef = useRef(null);
  const saveRequestedRef = useRef(false);
  const lastSavedPagesRef = useRef(null);
  const dirtyRevisionRef = useRef(0);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const historyBoundaryActiveRef = useRef(false);
  const historyBoundaryTimerRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showPreview] = useState(true);
  const [documentName, setDocumentName] = useState('Untitled Document');
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [visiblePageIndex, setVisiblePageIndex] = useState(0);
  const [visiblePageCount, setVisiblePageCount] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [teachingZoom, setTeachingZoom] = useState(100);
  const [showAppMenu, setShowAppMenu] = useState(false);
  const [showContentCreator, setShowContentCreator] = useState(false);
  const [showToolsOnboarding, setShowToolsOnboarding] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(DOCUMENT_TOOLS_ONBOARDING_DISMISSED_KEY) !== 'true';
  });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [useMathJax, setUseMathJax] = useState(false);
  const [showMathModal, setShowMathModal] = useState(false);
  const [initialMathLatex, setInitialMathLatex] = useState('');
  const [mathAnchorRect, setMathAnchorRect] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [headerFirstPageOnly, setHeaderFirstPageOnly] = useState(true);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedModalMode, setEmbedModalMode] = useState('html');
  const [embedModalHtml, setEmbedModalHtml] = useState('');
  const [embedModalUrl, setEmbedModalUrl] = useState('');
  const [embedModalError, setEmbedModalError] = useState('');
  const [selectedImageEl, setSelectedImageEl] = useState(null);
  const [imageOverlayRect, setImageOverlayRect] = useState(null);
  const draggedImageRef = useRef(null);
  const pageRefsMap = useRef({});
  const pageBodyRefsMap = useRef({});
  const documentScrollRef = useRef(null);
  const pageCounterFrameRef = useRef(null);
  const fullScreenRootRef = useRef(null);
  const stepDeleteMigrationKeyRef = useRef(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentModalMode, setDocumentModalMode] = useState('my');
  const [modalDocuments, setModalDocuments] = useState([]);
  const [templateActionId, setTemplateActionId] = useState('');
  const [modalFolderFilter, setModalFolderFilter] = useState('all');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderDraft, setFolderDraft] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [showAssessmentSettings, setShowAssessmentSettings] = useState(false);
  const [showResponseAreaConfig, setShowResponseAreaConfig] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAssessmentPreview, setShowAssessmentPreview] = useState(false);
  const [showTeachingMode, setShowTeachingMode] = useState(false);
  const [openTeachingSteps, setOpenTeachingSteps] = useState({});
  const [selectedResponseAreaId, setSelectedResponseAreaId] = useState(null);
  const editorResponseRootsRef = useRef(new Map());
  const lastSyncedHtmlRef = useRef({});
  const lastSyncedShellRef = useRef({});
  const [domRebuildCounter, setDomRebuildCounter] = useState(0);
  const [showResponseTypeMenu, setShowResponseTypeMenu] = useState(false);
  const [responseTypeMenuPos, setResponseTypeMenuPos] = useState({ x: 0, y: 0 });
  const [narrationTarget, setNarrationTarget] = useState(null);
  const [pdfActionError, setPdfActionError] = useState('');
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [showNarrationModal, setShowNarrationModal] = useState(false);
  const [isUploadingNarration, setIsUploadingNarration] = useState(false);
  const [narrationError, setNarrationError] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });
  const [isDesktopBannerDismissed, setIsDesktopBannerDismissed] = useState(false);
  const [creatorSubject, setCreatorSubject] = useState('');
  const [creatorGrade, setCreatorGrade] = useState('');
  const [creatorTopic, setCreatorTopic] = useState('');
  const [creatorSubtopic, setCreatorSubtopic] = useState('');
  const [creatorContentType, setCreatorContentType] = useState('notes');
  const [creatorProjects, setCreatorProjects] = useState([]);
  const [creatorProjectId, setCreatorProjectId] = useState('');
  const [creatorInstructions, setCreatorInstructions] = useState('');
  const [isGeneratingCreatorContent, setIsGeneratingCreatorContent] = useState(false);
  const [creatorError, setCreatorError] = useState('');
  const [creatorContent, setCreatorContent] = useState('');
  const [creatorDraft, setCreatorDraft] = useState('');
  const responseTypeRangeRef = useRef(null);
  const stepEmbedTargetRef = useRef(null);
  const stepEmbedRangeRef = useRef(null);
  const stepEmbedEditRef = useRef(null);
  const appMenuRef = useRef(null);
  const canAccessMarking = user?.email?.toLowerCase() === 'info@appcentral.co.za';
  const canAccessAdmin = isUserAdmin;
  const isDocumentOwner = !!(user?.uid && currentDocument?.userId && user.uid === currentDocument.userId);
  const isPdfMode = currentDocument?.contentType === 'pdf';
  const narrationPages = getNarrationPages(currentDocument?.narration);

  const updateNarrationState = useCallback((updater) => {
    const doc = useDocumentStore.getState().currentDocument;
    if (!doc) return;
    const nextNarration = updater(doc.narration || null);
    setCurrentDocument({
      ...doc,
      narration: nextNarration && Object.keys(getNarrationPages(nextNarration)).length === 0 && !nextNarration.path
        ? null
        : nextNarration,
    });
  }, [setCurrentDocument]);

  const preserveSelection = useCallback(() => {
    const editor = editorRef.current;
    const sel = window.getSelection();
    if (!editor || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) {
      selectionRef.current = range.cloneRange();
    }
  }, []);

  const pageWidthMm = 210;
  const pageHeightMm = 297;
  const pagePaddingMm = PAGE_PADDING_MM;
  const pagePaddingTopMm = PAGE_PADDING_TOP_MM;
  const pagePaddingBottomMm = PAGE_PADDING_BOTTOM_MM;
  const zoomScale = zoom / 100;
  const scaledWidth = pageWidthMm * zoomScale;
  const scaledHeight = pageHeightMm * zoomScale;
  const responseTypeOptions = [
    { value: 'multiple-choice', label: 'Multiple Choice', icon: CircleDot },
    { value: 'multi-select', label: 'Multi Select', icon: CheckSquare },
    { value: 'short-answer', label: 'Short Answer', icon: Type },
    { value: 'paragraph', label: 'Paragraph', icon: ParagraphIcon },
    { value: 'image-upload', label: 'Image Upload', icon: ImagePlus },
  ];
  const insertMenuOptions = [
    { value: 'step', label: 'Step', icon: ListOrdered },
    ...(currentDocument?.assessment?.enabled ? responseTypeOptions : []),
  ];
  const creatorContentTypeOptions = [
    'notes',
    'quiz',
    'tutorial',
    'text',
    'exam',
    'worksheet',
    'summary',
  ];
  const shouldShowDesktopBanner = isMobileViewport && !isDesktopBannerDismissed;
  const shouldShowContentCreator = canAccessAdmin && showContentCreator && !isPdfMode && !isMobileViewport;

  const openToolsFromDocument = useCallback(() => {
    const returnPath = documentId && documentId !== 'new'
      ? `/document/${documentId}`
      : '/document/new';

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(DOCUMENT_TOOLS_ONBOARDING_SESSION_KEY, 'true');
      window.sessionStorage.setItem(
        DOCUMENT_TOOLS_ONBOARDING_CONTEXT_KEY,
        JSON.stringify({
          returnPath,
          documentName: documentName || currentDocument?.name || 'Untitled Document',
          startedAt: Date.now(),
        })
      );
    }

    const params = new URLSearchParams({
      from: 'document-editor',
      returnTo: returnPath,
    });

    navigate(`/tools?${params.toString()}`);
  }, [currentDocument?.name, documentId, documentName, navigate]);

  const dismissToolsOnboarding = useCallback(() => {
    setShowToolsOnboarding(false);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DOCUMENT_TOOLS_ONBOARDING_DISMISSED_KEY, 'true');
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateViewportMode = () => {
      setIsMobileViewport(window.innerWidth < 1024);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => window.removeEventListener('resize', updateViewportMode);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCreatorProjects = async () => {
      if (!user?.uid || !canAccessAdmin) {
        setCreatorProjects([]);
        setCreatorProjectId('');
        return;
      }
      try {
        const getProjectsFn = httpsCallable(functions, 'getContentCreatorProjects');
        const result = await getProjectsFn();
        if (cancelled) return;
        const projects = Array.isArray(result?.data?.projects) ? result.data.projects : [];
        setCreatorProjects(projects);
        if (!projects.length) {
          setCreatorProjectId('');
          return;
        }
        setCreatorProjectId((prev) => {
          if (prev && projects.some((project) => project.id === prev)) return prev;
          return projects[0].id;
        });
      } catch (error) {
        if (!cancelled) {
          setCreatorProjects([]);
          setCreatorProjectId('');
        }
      }
    };
    loadCreatorProjects();
    return () => {
      cancelled = true;
    };
  }, [canAccessAdmin, user?.uid]);

  const HeaderButton = ({ icon: Icon, label, onClick, variant = 'ghost', disabled }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'group inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
        disabled && 'opacity-50 cursor-not-allowed',
        variant === 'ghost' && 'border-white/20 bg-white/10 text-white hover:bg-white/20',
        variant === 'solid' && 'border-transparent bg-white text-sigma-800 hover:bg-sigma-50',
        variant === 'accent' && 'border-transparent bg-pink-500 text-white hover:bg-pink-400'
      )}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden whitespace-nowrap md:inline md:max-w-0 md:overflow-hidden md:opacity-0 md:transition-all md:duration-200 md:group-hover:max-w-[140px] md:group-hover:opacity-100">
        {label}
      </span>
    </button>
  );

  const isProfileComplete = !!(
    userData?.firstName &&
    userData?.lastName &&
    userData?.institutionName
  );

  const canSave = !!user && !!userData && isProfileComplete;
  const appMenuTabs = [
    {
      key: 'admin',
      label: 'Admin',
      disabled: !canAccessAdmin,
      badge: canAccessAdmin ? '' : 'Restricted',
      onClick: () => navigate('/admin'),
    },
    {
      key: 'marking',
      label: 'Marking',
      disabled: !canAccessMarking,
      badge: canAccessMarking ? '' : 'Restricted',
      onClick: () => navigate('/marking'),
    },
    {
      key: 'grading',
      label: 'Grading',
      disabled: false,
      badge: '',
      onClick: () => navigate('/grading'),
    },
    {
      key: 'learners',
      label: 'Learners',
      disabled: false,
      badge: '',
      onClick: () => navigate('/learners'),
    },
    {
      key: 'analysis',
      label: 'Analysis',
      disabled: !canAccessAdmin,
      badge: canAccessAdmin ? '' : 'Restricted',
      onClick: () => navigate('/analysis'),
    },
    {
      key: 'tools',
      label: 'Tools',
      disabled: false,
      badge: '',
      onClick: openToolsFromDocument,
    },
  ];

  const getPages = useCallback(() => {
    const pages = useDocumentStore.getState().currentDocument?.pages;
    return Array.isArray(pages) && pages.length > 0 ? pages : [''];
  }, []);

  useEffect(() => {
    const pages = useDocumentStore.getState().currentDocument?.pages || [];
    const storedHeader = pages.map((pageHtml) => {
      const container = document.createElement('div');
      container.innerHTML = pageHtml || '';
      return container.querySelector('.doc-header');
    }).find(Boolean);
    setHeaderFirstPageOnly(storedHeader?.getAttribute('data-header-repeat') !== 'all');
  }, [currentDocument?.id, currentDocument?.pages]);

  const getPageShell = useCallback((pageIndex = activePageIndex) => {
    return pageRefsMap.current[pageIndex] || null;
  }, [activePageIndex]);

  const getFocusedPageIndex = useCallback(() => {
    const activeEl = document.activeElement;
    if (!activeEl) return -1;
    const pageRegion = activeEl.closest?.(
      '[data-page-body="true"], [data-page-header-region="true"]'
    );
    if (!pageRegion) return -1;
    const raw = pageRegion.getAttribute('data-page-index');
    const idx = Number.parseInt(raw || '', 10);
    return Number.isNaN(idx) ? -1 : idx;
  }, []);

  const getCurrentPageHtml = useCallback((pageIndex = activePageIndex) => {
    const shell = getPageShell(pageIndex);
    if (shell) {
      const rawHtml = getPageHtmlFromShell(shell);
      // Read responseAreas imperatively to avoid callback identity changing
      // on every response area config update, which would re-fire downstream effects.
      const currentDoc = useDocumentStore.getState().currentDocument;
      const responseAreas = currentDoc?.assessment?.responseAreas || {};
      const responseAreaOrder = currentDoc?.assessment?.responseAreaOrder || [];
      return normalizeResponseAreaBlocksInHtml(rawHtml, responseAreas, responseAreaOrder);
    }
    const doc = useDocumentStore.getState().currentDocument;
    const pages = Array.isArray(doc?.pages) && doc.pages.length > 0 ? doc.pages : [''];
    return pages[pageIndex] || '';
  }, [activePageIndex, getPageShell]);

  const syncPageShell = useCallback((pageIndex, pageHtml) => {
    const shell = pageRefsMap.current[pageIndex];
    if (!shell) return;
    const currentDoc = useDocumentStore.getState().currentDocument;
    const responseAreas = currentDoc?.assessment?.responseAreas || {};
    const responseAreaOrder = currentDoc?.assessment?.responseAreaOrder || [];
    const normalizedHtml = normalizeResponseAreaBlocksInHtml(pageHtml || '', responseAreas, responseAreaOrder);
    // Bail out if the HTML hasn't changed — avoids destroying React roots
    // mounted on response area blocks and prevents oscillation loops.
    if (
      lastSyncedHtmlRef.current[pageIndex] === normalizedHtml &&
      lastSyncedShellRef.current[pageIndex] === shell
    ) {
      // Still keep body/editor refs updated in case the shell was re-created
      pageBodyRefsMap.current[pageIndex] = shell.querySelector('[data-page-body="true"]');
      if (pageIndex === activePageIndex) {
        editorRef.current = pageBodyRefsMap.current[pageIndex] || null;
      }
      updatePageShellRegionSpacing(shell);
      return;
    }
    lastSyncedHtmlRef.current[pageIndex] = normalizedHtml;
    lastSyncedShellRef.current[pageIndex] = shell;
    setPageShellContent(shell, normalizedHtml);
    pageBodyRefsMap.current[pageIndex] = shell.querySelector('[data-page-body="true"]');
    if (pageIndex === activePageIndex) {
      editorRef.current = pageBodyRefsMap.current[pageIndex] || null;
    }
    // Signal that page DOM was rebuilt so response area roots can be re-mounted
    setDomRebuildCounter(c => c + 1);
  }, [activePageIndex]);

  const updateLocalPages = useCallback((pages) => {
    const doc = useDocumentStore.getState().currentDocument;
    if (!doc) return;
    const existingPages = Array.isArray(doc.pages) && doc.pages.length > 0 ? doc.pages : [''];
    const unchanged =
      existingPages.length === pages.length &&
      existingPages.every((pageHtml, index) => pageHtml === pages[index]);
    if (unchanged) return;
    useDocumentStore.getState().setCurrentDocument({ ...doc, pages });
  }, []);

  const commitPageShellToState = useCallback((targetIndex, shell) => {
    const doc = useDocumentStore.getState().currentDocument;
    const latestPages = Array.isArray(doc?.pages) && doc.pages.length > 0 ? doc.pages : [''];
    if (!shell) return latestPages;
    const rawHtml = getPageHtmlFromShell(shell);
    // Read responseAreas imperatively to avoid callback identity changing
    // on every response area config update, which would re-fire downstream effects.
    const currentDoc = useDocumentStore.getState().currentDocument;
    const responseAreas = currentDoc?.assessment?.responseAreas || {};
    const responseAreaOrder = currentDoc?.assessment?.responseAreaOrder || [];
    const nextHtml = normalizeResponseAreaBlocksInHtml(rawHtml, responseAreas, responseAreaOrder);
    if ((latestPages[targetIndex] || '') === nextHtml) return latestPages;
    const pages = [...latestPages];
    pages[targetIndex] = nextHtml;
    updateLocalPages(pages);
    return pages;
  }, [updateLocalPages]);

  const commitCurrentPageToState = useCallback(() => {
    const doc = useDocumentStore.getState().currentDocument;
    const latestPages = Array.isArray(doc?.pages) && doc.pages.length > 0 ? doc.pages : [''];
    const focusedIndex = getFocusedPageIndex();
    const targetIndex = focusedIndex >= 0 ? focusedIndex : activePageIndex;
    const shell = getPageShell(targetIndex);
    if (!shell) return latestPages;
    const pages = commitPageShellToState(targetIndex, shell);
    if (targetIndex !== activePageIndex) {
      setActivePageIndex(targetIndex);
    }
    return pages;
  }, [activePageIndex, commitPageShellToState, getFocusedPageIndex, getPageShell]);

  const commitAllPageShellsToState = useCallback(() => {
    const doc = useDocumentStore.getState().currentDocument;
    const latestPages = Array.isArray(doc?.pages) && doc.pages.length > 0 ? doc.pages : [''];
    const responseAreas = doc?.assessment?.responseAreas || {};
    const responseAreaOrder = doc?.assessment?.responseAreaOrder || [];
    const pages = latestPages.map((pageHtml, index) => {
      const shell = pageRefsMap.current[index];
      if (!shell) return pageHtml || '';
      return normalizeResponseAreaBlocksInHtml(
        getPageHtmlFromShell(shell),
        responseAreas,
        responseAreaOrder
      );
    });
    const changed = pages.some((pageHtml, index) => pageHtml !== (latestPages[index] || ''));
    if (changed) updateLocalPages(pages);
    return pages;
  }, [updateLocalPages]);

  // Auto-save after 2s of inactivity
  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    dirtyRevisionRef.current += 1;
    setHasUnsavedChanges(true);
    autoSaveTimer.current = setTimeout(() => handleSaveRef.current?.(false), 2000);
  }, []);

  const handleV2Dirty = useCallback(() => {
    v2EditorDomRef.current = v2EngineRef.current?.dom || null;
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  const updatePagesWithTransform = useCallback((transform) => {
    const doc = useDocumentStore.getState().currentDocument;
    if (!doc) return;
    const pages = commitCurrentPageToState();
    const nextPages = pages.map((html, index) => transform(html, index, pages));
    const changed = nextPages.some((p, i) => p !== pages[i]);
    if (!changed) return;
    updateLocalPages(nextPages);
    syncPageShell(activePageIndex, nextPages[activePageIndex] || '');
    scheduleAutoSave();
  }, [activePageIndex, commitCurrentPageToState, scheduleAutoSave, syncPageShell, updateLocalPages]);

  const updatePageNumbersInPages = useCallback((pages) => {
    return pages.map((html, index) => {
      const container = document.createElement('div');
      container.innerHTML = html || '';
      const pageNumberEl = container.querySelector('.page-number[data-page-number]');
      if (!pageNumberEl) return html || '';
      const alignClass = ['align-left', 'align-center', 'align-right'].find((cls) =>
        pageNumberEl.classList.contains(cls)
      ) || 'align-right';
      const startNumber = getPageNumberStart(pageNumberEl);
      pageNumberEl.className = `page-number ${alignClass}`;
      pageNumberEl.setAttribute('data-page-number-start', String(startNumber));
      pageNumberEl.textContent = getPageNumberText(index, pages.length, startNumber);
      return container.innerHTML;
    });
  }, []);

  const pushUndo = useCallback(() => {
    const snapshotHtml = getCurrentPageHtml(activePageIndex);
    if (snapshotHtml == null) return;
    const snapshot = { pageIndex: activePageIndex, html: snapshotHtml };
    const stack = undoStack.current;
    // Avoid duplicate consecutive snapshots
    if (stack.length > 0 && stack[stack.length - 1].html === snapshot.html && stack[stack.length - 1].pageIndex === snapshot.pageIndex) return;
    stack.push(snapshot);
    if (stack.length > 100) stack.shift();
    redoStack.current = [];
  }, [activePageIndex, getCurrentPageHtml]);

  const markUndoBoundary = useCallback(() => {
    if (!historyBoundaryActiveRef.current) {
      pushUndo();
      historyBoundaryActiveRef.current = true;
    }
    if (historyBoundaryTimerRef.current) clearTimeout(historyBoundaryTimerRef.current);
    historyBoundaryTimerRef.current = setTimeout(() => {
      historyBoundaryActiveRef.current = false;
    }, 500);
  }, [pushUndo]);

  const handleUndo = useCallback(() => {
    if (!editorRef.current) return;
    if (historyBoundaryTimerRef.current) clearTimeout(historyBoundaryTimerRef.current);
    historyBoundaryActiveRef.current = false;
    const stack = undoStack.current;
    if (stack.length === 0) return;
    // Save current state to redo
    redoStack.current.push({ pageIndex: activePageIndex, html: getCurrentPageHtml(activePageIndex) });
    const prev = stack.pop();
    if (prev.pageIndex === activePageIndex) {
      syncPageShell(activePageIndex, prev.html);
    } else {
      // Commit current, switch page, restore
      commitCurrentPageToState();
      const doc = useDocumentStore.getState().currentDocument;
      const pages = [...(doc?.pages || [''])];
      pages[prev.pageIndex] = prev.html;
      updateLocalPages(pages);
      setActivePageIndex(prev.pageIndex);
    }
    commitCurrentPageToState();
    scheduleAutoSave();
  }, [activePageIndex, commitCurrentPageToState, getCurrentPageHtml, scheduleAutoSave, syncPageShell, updateLocalPages]);

  const handleRedo = useCallback(() => {
    if (!editorRef.current) return;
    if (historyBoundaryTimerRef.current) clearTimeout(historyBoundaryTimerRef.current);
    historyBoundaryActiveRef.current = false;
    const stack = redoStack.current;
    if (stack.length === 0) return;
    undoStack.current.push({ pageIndex: activePageIndex, html: getCurrentPageHtml(activePageIndex) });
    const next = stack.pop();
    if (next.pageIndex === activePageIndex) {
      syncPageShell(activePageIndex, next.html);
    } else {
      commitCurrentPageToState();
      const doc = useDocumentStore.getState().currentDocument;
      const pages = [...(doc?.pages || [''])];
      pages[next.pageIndex] = next.html;
      updateLocalPages(pages);
      setActivePageIndex(next.pageIndex);
    }
    commitCurrentPageToState();
    scheduleAutoSave();
  }, [activePageIndex, commitCurrentPageToState, getCurrentPageHtml, scheduleAutoSave, syncPageShell, updateLocalPages]);

  // Load document on mount
  useEffect(() => {
    setLoadError(null);
    setPdfActionError('');
    if (documentId && documentId !== 'new') {
      if (!user?.uid) return;
      storeLoadDocument(documentId, { currentUserId: user.uid, isAdmin: isUserAdmin })
        .then((doc) => {
          setDocumentName(doc?.name || 'Untitled Document');
          setActivePageIndex(0);
          setPdfActionError('');
        })
        .catch(() => {
          setCurrentDocument(null);
          setLoadError('This document could not be found or you no longer have access to it.');
        });
    } else {
      setCurrentDocument(debugInitialDocument || {
        id: null,
        name: 'Untitled Document',
        pages: [''],
        contentType: 'editor',
        pdf: null,
        narration: null,
        folderPath: '',
        isTemplate: false,
        metadata: { createdAt: new Date(), updatedAt: new Date() },
      });
      setActivePageIndex(0);
      setPdfActionError('');
    }
  }, [debugInitialDocument, documentId, storeLoadDocument, setCurrentDocument, isUserAdmin, setPdfActionError, user?.uid]);

  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (overflowTimerRef.current) clearTimeout(overflowTimerRef.current);
      if (historyBoundaryTimerRef.current) clearTimeout(historyBoundaryTimerRef.current);
    };
  }, []);

  // ─── Save ──────────────────────────────────────────────────
  const handleSave = (manual = true) => {
    if (saveInFlightRef.current) {
      saveRequestedRef.current = true;
      return saveInFlightRef.current;
    }

    const saveRevision = dirtyRevisionRef.current;
    const savePromise = (async () => {
    const existingDocumentId = useDocumentStore.getState().currentDocument?.id || null;
    if (!user) return false;
    if (!manual && existingDocumentId && !hasUnsavedChanges) {
      return true;
    }
    if (!isPdfMode && !useV2Engine && !editorRef.current) {
      return Boolean(existingDocumentId) && !hasUnsavedChanges;
    }
    if (!canSave) {
      setSaveError('Complete your profile to save documents.');
      return false;
    }
    if (!isPdfMode && useV2Engine) await v2EngineRef.current?.commit?.();
    const pages = isPdfMode || useV2Engine ? getPages() : commitCurrentPageToState();
    const pagesWithNumbers = updatePageNumbersInPages(pages);
    if (!isPdfMode && pagesWithNumbers.some((p, i) => p !== pages[i])) {
      updateLocalPages(pagesWithNumbers);
    }
    lastSavedPagesRef.current = pagesWithNumbers;
    const doc = useDocumentStore.getState().currentDocument;
    const docData = {
      name: documentName,
      pages: pagesWithNumbers,
      userId: user.uid,
      folderPath: normalizeFolderPath(doc?.folderPath || ''),
      assessment: doc?.assessment || null,
      contentType: doc?.contentType === 'pdf' ? 'pdf' : 'editor',
      pdf: doc?.pdf || null,
      narration: doc?.narration || null,
      isTemplate: Boolean(doc?.isTemplate) && doc?.contentType !== 'pdf',
      paginationMode: useV2Engine ? 'preserve' : 'reflow',
    };

    try {
      setIsSaving(true);
      setSaveError('');
      const documentCount = currentDocument?.id
        ? 0
        : await SubscriptionService.getUserDocumentCount(user.uid);
      const access = evaluateDocumentSaveAccess({
        userData,
        existingDocumentId: currentDocument?.id || null,
        documentCount,
        nextPageCount: pagesWithNumbers.length,
      });

      if (!access.allowed) {
        setSaveError(access.message);
        return false;
      }

      if (currentDocument?.id) {
        await updateDocument(currentDocument.id, docData);
      } else {
        const created = await createDocument(docData);
        if (created?.id) {
          window.history.replaceState(null, '', `/document/${created.id}`);
        }
      }
      setLastSaved(new Date());
      if (dirtyRevisionRef.current === saveRevision) {
        setHasUnsavedChanges(false);
      }
      return true;
    } catch (err) {
      console.error('Save failed:', err);
      return false;
    } finally {
      setIsSaving(false);
    }
    })();

    saveInFlightRef.current = savePromise;
    const finishSave = () => {
      if (saveInFlightRef.current === savePromise) saveInFlightRef.current = null;
      const shouldSaveLatest = saveRequestedRef.current && dirtyRevisionRef.current !== saveRevision;
      saveRequestedRef.current = false;
      if (shouldSaveLatest) {
        window.setTimeout(() => handleSaveRef.current?.(false), 0);
      }
    };
    savePromise.then(finishSave, finishSave);
    return savePromise;
  };
  handleSaveRef.current = handleSave;

  const handleSwitchContentType = useCallback((nextType) => {
    const doc = useDocumentStore.getState().currentDocument;
    if (!doc) return;
    setPdfActionError('');
    useDocumentStore.getState().setCurrentDocument({
      ...doc,
      contentType: nextType === 'pdf' ? 'pdf' : 'editor',
    });
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  const handlePdfUpload = useCallback(async (file) => {
    if (!user?.uid) return;
    if (!canUsePdfUploads(userData)) {
      setPdfActionError('PDF uploads require the Basic or Pro plan.');
      return;
    }
    try {
      setIsUploadingPdf(true);
      setPdfActionError('');
      const existingPath = useDocumentStore.getState().currentDocument?.pdf?.path || null;
      await uploadPdfFile(file, user.uid, existingPath);
      scheduleAutoSave();
    } catch (error) {
      console.error('PDF upload failed:', error);
      setPdfActionError(error.message || 'Failed to upload PDF.');
    } finally {
      setIsUploadingPdf(false);
    }
  }, [scheduleAutoSave, uploadPdfFile, user?.uid, userData]);

  const handlePdfDelete = useCallback(async () => {
    const existingPath = useDocumentStore.getState().currentDocument?.pdf?.path || null;
    try {
      setPdfActionError('');
      await removePdfFile(existingPath);
      scheduleAutoSave();
    } catch (error) {
      console.error('PDF delete failed:', error);
      setPdfActionError('Failed to remove PDF.');
    }
  }, [removePdfFile, scheduleAutoSave]);

  const openPdfNarrationModal = useCallback((pageNumber) => {
    setNarrationError('');
    setNarrationTarget({ type: 'pdf-page', pageNumber });
    setShowNarrationModal(true);
  }, []);

  const updatePdfNarrationPosition = useCallback((pageNumber, position) => {
    updateNarrationState((currentNarration) => {
      const currentPages = getNarrationPages(currentNarration);
      if (!currentPages[pageNumber]) return currentNarration;
      return {
        ...(currentNarration?.pages ? currentNarration : {}),
        pages: {
          ...currentPages,
          [pageNumber]: {
            ...currentPages[pageNumber],
            position,
          },
        },
      };
    });
    scheduleAutoSave();
  }, [scheduleAutoSave, updateNarrationState]);

  const handleNarrationSave = useCallback(async ({ file, duration }) => {
    if (!user?.uid) return;
    try {
      setIsUploadingNarration(true);
      setNarrationError('');
      const target = narrationTarget;
      if (!target) return;

      if (target.type === 'pdf-page') {
        const existingPath = getNarrationPages(useDocumentStore.getState().currentDocument?.narration)?.[target.pageNumber]?.path || null;
        const audio = await uploadAudioResource(file, user.uid, existingPath, { duration });
        updateNarrationState((currentNarration) => {
          const currentPages = getNarrationPages(currentNarration);
          return {
            ...(currentNarration?.pages ? currentNarration : {}),
            pages: {
              ...currentPages,
              [target.pageNumber]: {
                ...audio,
                position: currentPages[target.pageNumber]?.position || { x: 94, y: 6 },
              },
            },
          };
        });
      } else if (target.type === 'step') {
        const existingBlock = target.stepBlock?.querySelector('.document-step-audio-block');
        const existingPath = existingBlock?.getAttribute('data-step-audio-path') || null;
        const audio = await uploadAudioResource(file, user.uid, existingPath, { duration });
        const stepBody = target.stepBlock?.querySelector('.document-step-body');
        if (stepBody) {
          if (existingBlock) {
            existingBlock.outerHTML = buildStepAudioBlockHtml(audio);
          } else {
            stepBody.insertAdjacentHTML('afterbegin', buildStepAudioBlockHtml(audio));
          }
          commitCurrentPageToState();
        }
      }

      setShowNarrationModal(false);
      setNarrationTarget(null);
      scheduleAutoSave();
    } catch (error) {
      console.error('Narration upload failed:', error);
      setNarrationError(error.message || 'Failed to save narration.');
    } finally {
      setIsUploadingNarration(false);
    }
  }, [commitCurrentPageToState, narrationTarget, scheduleAutoSave, updateNarrationState, uploadAudioResource, user?.uid]);

  const handlePdfNarrationDelete = useCallback(async (pageNumber) => {
    try {
      setNarrationError('');
      const existingPath = getNarrationPages(useDocumentStore.getState().currentDocument?.narration)?.[pageNumber]?.path || null;
      await removeAudioResource(existingPath);
      updateNarrationState((currentNarration) => {
        const currentPages = { ...getNarrationPages(currentNarration) };
        delete currentPages[pageNumber];
        return Object.keys(currentPages).length > 0 ? { pages: currentPages } : null;
      });
      scheduleAutoSave();
    } catch (error) {
      console.error('Narration delete failed:', error);
      setNarrationError('Failed to remove narration.');
    }
  }, [removeAudioResource, scheduleAutoSave, updateNarrationState]);

  // ─── Export as PDF ──────────────────────────────────────────
  const handleExport = async (layout = 'portrait') => {
    if (!user) return;
    setShowExportModal(false);
    try {
      setIsExporting(true);
      setExportError('');
      const docIdBeforeSave = useDocumentStore.getState().currentDocument?.id || null;
      const shouldSaveBeforeExport = hasUnsavedChanges || !docIdBeforeSave;
      const saved = shouldSaveBeforeExport ? await handleSave(false) : true;
      if (!saved) {
        setExportError('Document must be saved before exporting.');
        return;
      }
      // Re-read the ID after save (may have been created for the first time)
      const docId = useDocumentStore.getState().currentDocument?.id;
      if (!docId) {
        setExportError('Document must be saved before exporting.');
        return;
      }
      const exportFn = httpsCallable(functions, 'exportDocumentAsPdf');
      const pagesForExport = Array.isArray(lastSavedPagesRef.current) && lastSavedPagesRef.current.length
        ? lastSavedPagesRef.current
        : getPages();
      const result = await exportFn({
        documentId: docId,
        pages: pagesForExport,
        layout,
        paginationMode: useV2Engine ? 'preserve' : 'reflow',
      });
      if (result.data?.pdf) {
        const blob = new Blob(
          [Uint8Array.from(atob(result.data.pdf), c => c.charCodeAt(0))],
          { type: 'application/pdf' }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.data.fileName || 'document.pdf';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export failed:', err);
      setExportError('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // ─── Document name ────────────────────────────────────────
  const handleDocumentNameChange = (newName) => {
    setDocumentName(newName);
    if (currentDocument) {
      setCurrentDocument({ ...currentDocument, name: newName });
    }
    scheduleAutoSave();
  };

  const handleInsertHeader = useCallback(() => {
    updatePagesWithTransform((html) => ensureRegionInHtml(html, 'doc-header'));
  }, [updatePagesWithTransform]);

  const handleInsertFooter = useCallback(() => {
    updatePagesWithTransform((html) => appendFooterInHtml(html));
  }, [updatePagesWithTransform]);

  const handleInsertPageNumber = useCallback((location, startNumber = 1) => {
    const [region, align] = location.split(':');
    updatePagesWithTransform((html, index, pages) =>
      upsertPageNumberInHtml(html, region, align, index, pages.length, startNumber)
    );
  }, [updatePagesWithTransform]);

  // ─── Inline Math (Ctrl+M) ────────────────────────────────
  const resolveEditorSelectionRange = useCallback(() => {
    const selection = window.getSelection();
    if (selection?.rangeCount > 0) {
      const liveRange = selection.getRangeAt(0);
      if (editorRef.current?.contains(liveRange.commonAncestorContainer)) {
        return liveRange.cloneRange();
      }
    }
    const savedRange = selectionRef.current;
    if (savedRange && editorRef.current?.contains(savedRange.commonAncestorContainer)) {
      return savedRange.cloneRange();
    }
    return null;
  }, []);

  const openMathModal = useCallback(() => {
    const editor = editorRef.current;
    const range = resolveEditorSelectionRange();
    if (!editor || !range) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    selectionRef.current = range.cloneRange();

    const selectedText = range.toString();
    if (selectedText) {
      if (selectedText.startsWith('$') && selectedText.endsWith('$')) {
        setInitialMathLatex(selectedText.slice(1, -1));
      } else {
        setInitialMathLatex(selectedText);
      }
    } else {
      setInitialMathLatex('');
    }

    setMathAnchorRect(range.getBoundingClientRect());
    setShowMathModal(true);
  }, [resolveEditorSelectionRange]);

  const resetMathContainers = useCallback((container) => {
    if (!container) return;
    const mathContainers = container.querySelectorAll('.math-container[data-math-content]');
    mathContainers.forEach((el) => {
      const latex = el.getAttribute('data-math-content');
      el.contentEditable = 'false';
      el.style.cursor = 'pointer';
      el.innerHTML = `\\(${latex}\\)`;
    });
  }, []);

  const renderMathLive = useCallback(async (container) => {
    if (!container) return;
    replaceDollarMathInContainer(container);
    resetMathContainers(container);
    const mathContainers = container.querySelectorAll('.math-container[data-math-content]');
    if (mathContainers.length === 0) return;
    try {
      const { renderMathInElement } = await import('mathlive');
      mathContainers.forEach((el) => {
        renderMathInElement(el, { renderAccessibleContent: false });
      });
    } catch (err) {
      console.warn('MathLive render failed:', err);
    }
  }, [resetMathContainers]);

  const renderMathJax = useCallback(async (container) => {
    if (!container) return;
    replaceDollarMathInContainer(container);
    resetMathContainers(container);
    await MathUtils.renderMath(container);
  }, [resetMathContainers]);

  // ─── Pagination rebalance ──────────────────────────────────
  const overflowRef = useRef(null);
  const lastRepaginationTimeRef = useRef(0);

  overflowRef.current = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const livePageBodies = Object.values(pageBodyRefsMap.current || {}).filter(Boolean);
    livePageBodies.forEach((body) => resetPageBodyScroll(body));
    const hasBodyOverflow = livePageBodies.some((body) => getPageBodyVisualOverflowPx(body) > 0.5);
    const pagesSnapshot = getPages();
    const allowUnderflowRepagination = allowUnderflowRepaginationRef.current;
    allowUnderflowRepaginationRef.current = false;
    if (!hasBodyOverflow && (!allowUnderflowRepagination || pagesSnapshot.length <= 1)) return;

    // Anti-oscillation cooldown: if we repaginated very recently and there
    // is no actual overflow, skip to prevent measure→repaginate→measure loops.
    if (
      Date.now() - lastRepaginationTimeRef.current < 400 &&
      !hasBodyOverflow
    ) {
      return;
    }

    const markerPlaced = injectCursorMarkerAtSelection(editor);
    const pages = commitAllPageShellsToState();
    const cleanPages = pages.map((p) => stripCursorMarkerFromHtml(p));
    // Use committed pages (with normalized placeholders) for stable measurement.
    // Reading from the live DOM picks up React-rendered response area content
    // whose height may differ from the stored placeholder, causing oscillation.
    const measurementPages = pages;
    const measurementBodies = Object.values(pageBodyRefsMap.current || {}).filter(Boolean);
    const editorWidth = measurementBodies[0]?.clientWidth;
    const editorHeight = measurementBodies.length > 0
      ? Math.min(...measurementBodies.map((body) => body.clientHeight).filter((height) => height > 0))
      : undefined;
    const repaginatedPages = repaginatePages(measurementPages, editorWidth, editorHeight);
    const repaginatedWithNumbers = updatePageNumbersInPages(repaginatedPages);
    const responseAreas = currentDocument?.assessment?.responseAreas || {};
    const responseAreaOrder = currentDocument?.assessment?.responseAreaOrder || [];

    // Build pages WITH markers still present (for cursor restoration)
    const pagesWithMarkers = repaginatedWithNumbers.map((pageHtml) =>
      normalizeResponseAreaBlocksInHtml(pageHtml, responseAreas, responseAreaOrder)
    );
    // Build clean pages (markers stripped) for comparison and final state
    const updatedPages = pagesWithMarkers.map((p) => stripCursorMarkerFromHtml(p));

    const changed =
      updatedPages.length !== cleanPages.length ||
      updatedPages.some((pageHtml, index) => pageHtml !== cleanPages[index]);

    if (!changed) {
      if (markerPlaced) {
        requestAnimationFrame(() => {
          restoreCursorFromMarker(pageBodyRefsMap, editor, pendingCursorOffsetRef.current);
        });
      }
      return;
    }

    // Determine which page the cursor marker landed on
    let cursorTargetPage = -1;
    if (markerPlaced) {
      for (let i = 0; i < pagesWithMarkers.length; i++) {
        if (pagesWithMarkers[i].includes('data-cursor-marker')) {
          cursorTargetPage = i;
          break;
        }
      }
    }

    // Advance activePageIndex to follow the cursor to its new page
    const effectiveTargetPage = cursorTargetPage >= 0
      ? Math.min(cursorTargetPage, updatedPages.length - 1)
      : Math.min(activePageIndex, updatedPages.length - 1);

    if (effectiveTargetPage !== activePageIndex) {
      setActivePageIndex(effectiveTargetPage);
    } else if (activePageIndex >= updatedPages.length) {
      setActivePageIndex(Math.max(0, updatedPages.length - 1));
    }

    // Sync page shells WITH cursor markers still in the HTML so that
    // restoreCursorFromMarker can find the marker in the live DOM.
    pagesWithMarkers.forEach((html, idx) => {
      // Temporarily bypass the lastSyncedHtml cache — we will update it
      // with clean HTML after cursor restoration.
      lastSyncedHtmlRef.current[idx] = null;
      syncPageShell(idx, html);
    });

    // Restore cursor from the live DOM marker element.
    const doRestore = () => {
      if (markerPlaced) {
        restoreCursorFromMarker(pageBodyRefsMap, editor, pendingCursorOffsetRef.current);
      } else {
        const targetBody = pageBodyRefsMap.current[effectiveTargetPage];
        if (targetBody) {
          moveCaretToBody(targetBody, 'end');
          editorRef.current = targetBody;
        }
      }

      // Now remove any leftover marker elements from the live DOM
      Object.values(pageBodyRefsMap.current).forEach((body) => {
        if (!body) return;
        body.querySelectorAll('[data-cursor-marker]').forEach((m) => m.remove());
      });

      // The rendered shells are authoritative after layout and cursor
      // restoration. Persist their actual partition instead of overwriting it
      // with an offscreen prediction that can differ around MathLive nodes.
      const livePages = updatedPages.map((fallbackHtml, idx) => {
        const shell = pageRefsMap.current[idx];
        return shell
          ? stripCursorMarkerFromHtml(getPageHtmlFromShell(shell))
          : fallbackHtml;
      });
      updateLocalPages(livePages);
      livePages.forEach((html, idx) => {
        lastSyncedHtmlRef.current[idx] = normalizeResponseAreaBlocksInHtml(
          html,
          responseAreas,
          responseAreaOrder
        );
      });

      lastRepaginationTimeRef.current = Date.now();

      // Region heights and MathLive line boxes can settle after the first
      // shell replacement. Verify every rendered page once layout is final;
      // a residual overflow must trigger another pass even if focus moved.
      clearTimeout(overflowTimerRef.current);
      overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 180);
    };

    // New overflow pages may be added beyond the cursor's page. Do not
    // finalize merely because the target shell exists; every generated shell
    // must be mounted and populated first.
    const allPageShellsExist = pagesWithMarkers.every((_, index) => pageRefsMap.current[index]);
    if (allPageShellsExist) {
      doRestore();
    } else {
      updateLocalPages(updatedPages);
      requestAnimationFrame(() => {
        pagesWithMarkers.forEach((html, idx) => {
          lastSyncedHtmlRef.current[idx] = null;
          syncPageShell(idx, html);
        });
        requestAnimationFrame(() => {
          doRestore();
        });
      });
    }

    scheduleAutoSave();
  };

  // ResizeObserver — debounced overflow check
  // Deps ensure re-attach when editor first appears in DOM
  const hasDocument = !!currentDocument;
  useEffect(() => {
    if (showPreview || !hasDocument) return;
    const editor = editorRef.current;
    if (!editor) return;
    let timer;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => overflowRef.current?.(), 200);
    });
    observer.observe(editor);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [hasDocument, showPreview]);

  useEffect(() => {
    if (showPreview || !currentDocument?.assessment?.responseAreas) return;

    const responseAreas = currentDocument.assessment.responseAreas || {};
    const responseAreaOrder = currentDocument.assessment.responseAreaOrder || [];
    // Read pages imperatively — reacting to getPages() would re-run this
    // effect on every keystroke, causing syncPageShell to replace the page
    // body innerHTML and destroy cursor position.
    const storeDoc = useDocumentStore.getState().currentDocument;
    const pages = Array.isArray(storeDoc?.pages) && storeDoc.pages.length > 0 ? storeDoc.pages : [''];
    const normalizedPages = pages.map((pageHtml) =>
      normalizeResponseAreaBlocksInHtml(pageHtml || '', responseAreas, responseAreaOrder)
    );
    const changedPages = normalizedPages.some((pageHtml, index) => pageHtml !== (pages[index] || ''));

    if (changedPages) {
      updateLocalPages(normalizedPages);
    }

    let changed = false;
    Object.values(pageBodyRefsMap.current || {}).forEach((pageBody) => {
      if (!pageBody) return;
      const blocks = pageBody.querySelectorAll('.response-area-block');
      blocks.forEach((block, index) => {
        let blockId = block.getAttribute('data-response-id');
        if (!blockId && responseAreaOrder[index]) {
          blockId = responseAreaOrder[index];
          block.setAttribute('data-response-id', blockId);
          changed = true;
        }
        if (!blockId) return;
        const config = responseAreas?.[blockId];
        if (!config) return;
        const nextType = config?.type || block.getAttribute('data-response-type') || 'short-answer';
        const nextLabel = String(config?.label || '');
        const nextPoints = String(Number(config?.points || 0));
        const nextRequired = config?.required === false ? 'false' : 'true';
        if (block.getAttribute('data-response-type') !== nextType) {
          block.setAttribute('data-response-type', nextType);
          changed = true;
        }
        if (block.getAttribute('data-response-label') !== nextLabel) {
          block.setAttribute('data-response-label', nextLabel);
          changed = true;
        }
        if (block.getAttribute('data-response-points') !== nextPoints) {
          block.setAttribute('data-response-points', nextPoints);
          changed = true;
        }
        if (block.getAttribute('data-response-required') !== nextRequired) {
          block.setAttribute('data-response-required', nextRequired);
          changed = true;
        }
      });
    });

    if (!changed && !changedPages) return;

    commitCurrentPageToState();
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
  }, [commitCurrentPageToState, currentDocument?.assessment?.responseAreas, currentDocument?.assessment?.responseAreaOrder, showPreview, updateLocalPages]);

  useEffect(() => {
    if (isPdfMode || !currentDocument?.assessment?.responseAreas) {
      editorResponseRootsRef.current.forEach((root) => {
        try {
          root.unmount();
        } catch (_) {
          // ignore unmount errors for detached roots
        }
      });
      editorResponseRootsRef.current.clear();
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const responseAreas = currentDocument.assessment?.responseAreas || {};
      const responseAreaOrder = currentDocument.assessment?.responseAreaOrder || [];
      const liveHosts = new Set();

      Object.entries(pageBodyRefsMap.current || {}).forEach(([pageIndex, pageBody]) => {
        if (!pageBody) return;
        const blocks = pageBody.querySelectorAll('.response-area-block');
        blocks.forEach((block, index) => {
          let blockId = block.getAttribute('data-response-id');
          if (!blockId && responseAreaOrder[index]) {
            blockId = responseAreaOrder[index];
            block.setAttribute('data-response-id', blockId);
          }
          if (!blockId || !responseAreas[blockId]) return;
          block.setAttribute('contenteditable', 'false');
          block.classList.add('response-area-active');
          let mountNode = block.querySelector('[data-response-render-root="true"]');
          if (!mountNode) {
            block.innerHTML = '';
            mountNode = document.createElement('div');
            mountNode.setAttribute('data-response-render-root', 'true');
            block.appendChild(mountNode);
          }
          liveHosts.add(block);

          let root = editorResponseRootsRef.current.get(block);
          if (!root) {
            root = createRoot(mountNode);
            editorResponseRootsRef.current.set(block, root);
          }

          root.render(
            <ResponseAreaRenderer
              blockId={blockId}
              config={responseAreas[blockId]}
              value=""
              onChange={NOOP_FN}
              disabled
              userId={user?.uid}
              showModelAnswer={false}
              showDeleteButton
            />
          );
        });
      });

      Array.from(editorResponseRootsRef.current.entries()).forEach(([block, root]) => {
        if (liveHosts.has(block)) return;
        try {
          root.unmount();
        } catch (_) {
          // ignore unmount errors for detached roots
        }
        editorResponseRootsRef.current.delete(block);
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    activePageIndex,
    domRebuildCounter,
    currentDocument?.assessment?.responseAreaOrder,
    currentDocument?.assessment?.responseAreas,
    currentDocument?.id,
    isPdfMode,
    user?.uid,
  ]);

  useEffect(() => () => {
    editorResponseRootsRef.current.forEach((root) => {
      try {
        root.unmount();
      } catch (_) {
        // ignore unmount errors for detached roots
      }
    });
    editorResponseRootsRef.current.clear();
  }, []);

  // Populate editor when document loads or when returning from preview
  useEffect(() => {
    if (showPreview || !currentDocument) return;
    const responseAreas = currentDocument?.assessment?.responseAreas || {};
    const responseAreaOrder = currentDocument?.assessment?.responseAreaOrder || [];
    // Read pages imperatively to avoid re-running on every keystroke
    const storeDoc = useDocumentStore.getState().currentDocument;
    const pages = Array.isArray(storeDoc?.pages) && storeDoc.pages.length > 0 ? storeDoc.pages : [''];
    const html = normalizeResponseAreaBlocksInHtml(pages[activePageIndex] || '', responseAreas, responseAreaOrder);
    const shell = getPageShell(activePageIndex);
    if (getCurrentPageHtml(activePageIndex) !== html) {
      syncPageShell(activePageIndex, html);
      if (!shell) return;
      if (useMathJax) {
        renderMathJax(shell);
      } else {
        renderMathLive(shell);
      }
    }
    // Check for overflow after content is loaded, including pages created by
    // older measurement rules whose HTML may already match the store.
    if (shell) setTimeout(() => overflowRef.current?.(), 300);
  }, [activePageIndex, currentDocument?.id, getCurrentPageHtml, getPageShell, showPreview, syncPageShell, useMathJax, renderMathJax, renderMathLive]);

  // Populate all page divs when entering scroll view or when document loads.
  // Pages are read imperatively to avoid re-running on every keystroke —
  // reacting to getPages() would call syncPageShell on every input, replacing
  // the page body innerHTML and destroying cursor position.
  useEffect(() => {
    if (!showPreview || !currentDocument) return;
    const storeDoc = useDocumentStore.getState().currentDocument;
    const pages = Array.isArray(storeDoc?.pages) && storeDoc.pages.length > 0 ? storeDoc.pages : [''];
    let needsMath = false;
    pages.forEach((html, index) => {
      const el = pageRefsMap.current[index];
      if (!el) return;
      if (getPageHtmlFromShell(el) !== (html || '')) {
        syncPageShell(index, html || '');
        needsMath = true;
      }
    });
    // Clean stale refs
    Object.keys(pageRefsMap.current).forEach((key) => {
      if (parseInt(key, 10) >= pages.length) {
        delete pageRefsMap.current[key];
        delete pageBodyRefsMap.current[key];
        delete lastSyncedHtmlRef.current[key];
        delete lastSyncedShellRef.current[key];
      }
    });
    if (needsMath) {
      const renderAllMath = async () => {
        for (let i = 0; i < pages.length; i++) {
          const el = pageRefsMap.current[i];
          if (!el) continue;
          if (useMathJax) {
            await renderMathJax(el);
          } else {
            await renderMathLive(el);
          }
        }
      };
      renderAllMath();
    }
  }, [showPreview, currentDocument?.id, renderMathJax, renderMathLive, syncPageShell, useMathJax]);

  useEffect(() => {
    setOpenTeachingSteps({});
  }, [currentDocument?.id]);

  // Clear stale lastSyncedHtmlRef when switching documents so old
  // cached HTML doesn't interfere with the new document's pages.
  useEffect(() => {
    lastSyncedHtmlRef.current = {};
    lastSyncedShellRef.current = {};
  }, [currentDocument?.id]);

  useEffect(() => {
    if (!currentDocument || currentDocument?.contentType === 'pdf') return;
    const migrationKey = currentDocument.id || `draft:${documentId || 'new'}`;
    if (stepDeleteMigrationKeyRef.current === migrationKey) return;
    stepDeleteMigrationKeyRef.current = migrationKey;
    const pages = Array.isArray(currentDocument.pages) ? currentDocument.pages : [''];
    const nextPages = pages.map((pageHtml) => ensureStepDeleteControlsInHtml(pageHtml || ''));
    const changed = nextPages.some((pageHtml, index) => pageHtml !== pages[index]);
    if (!changed) return;
    updateLocalPages(nextPages);
    if (!showPreview) {
      syncPageShell(activePageIndex, nextPages[activePageIndex] || '');
    }
  }, [activePageIndex, currentDocument, documentId, showPreview, syncPageShell, updateLocalPages]);

  useEffect(() => {
    if (!showTeachingMode || typeof document === 'undefined') return undefined;

    const root = fullScreenRootRef.current;
    const requestFullScreen = root?.requestFullscreen?.bind(root)
      || root?.webkitRequestFullscreen?.bind(root)
      || null;

    if (!requestFullScreen) return undefined;

    requestFullScreen().catch?.(() => {});

    return () => {
      if (document.fullscreenElement === root) {
        document.exitFullscreen?.().catch?.(() => {});
      } else if (document.webkitFullscreenElement === root) {
        document.webkitExitFullscreen?.();
      }
    };
  }, [showTeachingMode]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleFullscreenChange = () => {
      const root = fullScreenRootRef.current;
      const activeElement = document.fullscreenElement || document.webkitFullscreenElement || null;
      if (activeElement !== root) {
        setShowTeachingMode(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    if (useMathJax) {
      renderMathJax(editorRef.current);
    } else {
      renderMathLive(editorRef.current);
    }
  }, [useMathJax, renderMathJax, renderMathLive]);

  // ─── Editor keydown handler ───────────────────────────────
  const isSelectionInEditor = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    return editor.contains(range.commonAncestorContainer);
  }, []);

  // Continuously track selection so toolbar clicks can restore it
  useEffect(() => {
    const onSelectionChange = () => {
      const editor = editorRef.current;
      if (!editor) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) return;
      if (isWithinHeaderOrFooter(range.commonAncestorContainer)) {
        const regionNode = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
          ? range.commonAncestorContainer.closest('.doc-header, .doc-footer')
          : range.commonAncestorContainer.parentElement?.closest('.doc-header, .doc-footer');
        requestAnimationFrame(() => moveCaretToBody(editor, regionNode?.classList.contains('doc-footer') ? 'end' : 'start'));
        return;
      }
      selectionRef.current = range.cloneRange();
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  const restoreSelectionIfNeeded = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return false;
    editor.focus();
    if (isSelectionInEditor()) return true;
    // Selection was lost (e.g. toolbar click) — restore from saved range
    const saved = selectionRef.current;
    if (!saved) return false;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(saved);
    return isSelectionInEditor();
  }, [isSelectionInEditor]);

  const applyFontSizePx = useCallback((fontSizePx) => {
    const editor = editorRef.current;
    if (!editor) return false;
    if (!restoreSelectionIfNeeded()) return false;
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('fontSize', false, '7');
    const converted = editor.querySelectorAll('font[size="7"]');
    converted.forEach((node) => {
      node.removeAttribute('size');
      node.style.fontSize = `${fontSizePx}px`;
    });
    if (converted.length > 0) return true;

    // Fallback: apply to the current selection container when execCommand doesn't emit <font>.
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    const anchorNode = selection.anchorNode;
    const target = anchorNode?.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode;
    if (target && editor.contains(target)) {
      target.style.fontSize = `${fontSizePx}px`;
      return true;
    }
    return false;
  }, [restoreSelectionIfNeeded]);

  const getCurrentFontSizePx = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return null;
    const anchorNode = selection.anchorNode;
    if (!anchorNode) return null;
    const target = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode;
    if (!target || !editor.contains(target)) return null;
    const computed = window.getComputedStyle(target);
    const parsed = Number.parseFloat(computed.fontSize);
    return Number.isFinite(parsed) ? Math.round(parsed) : null;
  }, []);

  const adjustFontSize = useCallback((direction) => {
    const steps = textSizeSteps.current;
    const current = getCurrentFontSizePx() || 14;
    const next = direction === 'up'
      ? (steps.find((step) => step > current) || steps[steps.length - 1])
      : ([...steps].reverse().find((step) => step < current) || steps[0]);
    return applyFontSizePx(next);
  }, [applyFontSizePx, getCurrentFontSizePx]);

  const execFormatCommand = useCallback((command, value) => {
    const editor = editorRef.current;
    if (!editor) return;
    if (!restoreSelectionIfNeeded()) return;
    pushUndo();
    let manualListStart = null;
    if (command === 'insertOrderedList') {
      const selection = window.getSelection();
      const anchor = selection?.anchorNode;
      const element = anchor?.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor;
      const block = element?.closest?.('p, div, h1, h2, h3, h4, h5, h6, blockquote');
      if (block && editor.contains(block) && !block.closest('li')) {
        const match = (block.textContent || '').match(/^\s*(\d+)[.)]\s+/);
        if (match) {
          manualListStart = Math.max(1, Number.parseInt(match[1], 10) || 1);
          let remaining = match[0].length;
          const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
          let textNode = walker.nextNode();
          while (textNode && remaining > 0) {
            const removeCount = Math.min(remaining, textNode.textContent.length);
            textNode.deleteData(0, removeCount);
            remaining -= removeCount;
            textNode = walker.nextNode();
          }
        }
      }
    }
    if (command === 'fontSizePx') {
      const parsed = Number.parseInt(String(value), 10);
      if (Number.isFinite(parsed)) applyFontSizePx(parsed);
    } else if (command === 'increaseFontSize') {
      adjustFontSize('up');
    } else if (command === 'decreaseFontSize') {
      adjustFontSize('down');
    } else {
      document.execCommand(command, false, value || null);
    }
    const selection = window.getSelection();
    const anchor = selection?.anchorNode;
    const selectedElement = anchor?.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor;
    const listItem = selectedElement?.closest?.('li');
    if (listItem && editor.contains(listItem)) {
      if (manualListStart !== null) {
        const orderedList = listItem.closest('ol');
        if (orderedList) orderedList.setAttribute('start', String(manualListStart));
      }
      if (command === 'insertOrderedList') {
        const walker = document.createTreeWalker(listItem, NodeFilter.SHOW_TEXT);
        let firstText = walker.nextNode();
        while (firstText && !firstText.textContent.trim()) firstText = walker.nextNode();
        const textStyle = window.getComputedStyle(firstText?.parentElement || selectedElement || listItem);
        listItem.style.setProperty('--list-marker-font-size', textStyle.fontSize);
        listItem.style.setProperty('--list-marker-font-weight', textStyle.fontWeight);
      }
      if (command === 'fontSizePx') {
        const parsed = Number.parseInt(String(value), 10);
        if (Number.isFinite(parsed)) listItem.style.setProperty('--list-marker-font-size', `${parsed}px`);
      }
      if (command === 'increaseFontSize' || command === 'decreaseFontSize') {
        const computed = window.getComputedStyle(selectedElement);
        listItem.style.setProperty('--list-marker-font-size', computed.fontSize);
      }
      if (command === 'bold') {
        listItem.style.setProperty(
          '--list-marker-font-weight',
          document.queryCommandState('bold') ? '700' : '400'
        );
      }
    }
    commitCurrentPageToState();
    scheduleAutoSave();
  }, [adjustFontSize, applyFontSizePx, commitCurrentPageToState, pushUndo, restoreSelectionIfNeeded, scheduleAutoSave]);

  const openResponseTypeMenuAtSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0).cloneRange();
    responseTypeRangeRef.current = range;

    let rect = range.getBoundingClientRect();
    const editorRect = editorRef.current?.getBoundingClientRect();

    if (!rect || (!rect.left && !rect.top)) {
      const marker = document.createElement('span');
      marker.textContent = '\u200B';
      marker.setAttribute('data-caret-marker', 'true');
      const tempRange = range.cloneRange();
      tempRange.insertNode(marker);
      rect = marker.getBoundingClientRect();
      marker.remove();
      selection.removeAllRanges();
      selection.addRange(range);
    }

    const menuWidth = 224;
    const padding = 8;
    const x = Math.max(
      padding,
      Math.min((rect?.left ?? editorRect?.left ?? 0), window.innerWidth - menuWidth - padding)
    );
    const y = (rect?.bottom ?? editorRect?.top ?? 0) + 6;
    setResponseTypeMenuPos({ x, y });
    setShowResponseTypeMenu(true);
  }, []);

  const closeResponseTypeMenu = useCallback(() => {
    setShowResponseTypeMenu(false);
    responseTypeRangeRef.current = null;
  }, []);

  const setSelectionToRange = useCallback((range) => {
    if (!range) return null;
    const selection = window.getSelection();
    if (!selection) return null;
    selection.removeAllRanges();
    selection.addRange(range);
    selectionRef.current = range.cloneRange();
    return range;
  }, []);

  const createCollapsedRangeAtNode = useCallback((node, position = 'end') => {
    if (!node) return null;
    const range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(position !== 'start');
    return range;
  }, []);

  const getSelectionRangeWithin = useCallback((container) => {
    if (!container) return null;
    const selection = window.getSelection();
    if (selection?.rangeCount > 0) {
      const liveRange = selection.getRangeAt(0);
      if (container.contains(liveRange.commonAncestorContainer)) {
        return liveRange.cloneRange();
      }
    }
    const savedRange = selectionRef.current;
    if (savedRange && container.contains(savedRange.commonAncestorContainer)) {
      return savedRange.cloneRange();
    }
    return createCollapsedRangeAtNode(container, 'end');
  }, [createCollapsedRangeAtNode]);

  const focusEditableNode = useCallback((node, position = 'end') => {
    if (!node) return null;
    const range = createCollapsedRangeAtNode(node, position);
    if (!range) return null;
    node.focus?.();
    return setSelectionToRange(range);
  }, [createCollapsedRangeAtNode, setSelectionToRange]);

  const ensureStepBodySelection = useCallback((stepBlock, position = 'end') => {
    const stepBody = stepBlock?.querySelector?.('.document-step-body');
    if (!stepBody || !editorRef.current?.contains(stepBody)) return null;
    const range = getSelectionRangeWithin(stepBody) || createCollapsedRangeAtNode(stepBody, position);
    if (!range) return null;
    editorRef.current.focus();
    setSelectionToRange(range);
    return { stepBody, range };
  }, [createCollapsedRangeAtNode, getSelectionRangeWithin, setSelectionToRange]);

  const insertHtmlAtCurrentSelection = useCallback(async (html, rangeOverride) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const liveRange = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const range = rangeOverride ||
      selectionRef.current ||
      (liveRange && editor?.contains(liveRange.commonAncestorContainer) ? liveRange.cloneRange() : null);
    if (!editor || !range || !html?.trim()) return null;

    pushUndo();
    range.deleteContents();

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const fragment = document.createDocumentFragment();
    let lastNode = null;
    while (wrapper.firstChild) {
      lastNode = fragment.appendChild(wrapper.firstChild);
    }
    range.insertNode(fragment);

    if (lastNode) {
      const afterRange = document.createRange();
      afterRange.setStartAfter(lastNode);
      afterRange.collapse(true);
      setSelectionToRange(afterRange);
    }

    const schedulePostInsertOverflow = () => {
      clearTimeout(overflowTimerRef.current);
      requestAnimationFrame(() => {
        overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 80);
      });
    };

    commitCurrentPageToState();
    scheduleAutoSave();
    schedulePostInsertOverflow();

    if (useMathJax) {
      await renderMathJax(editor);
    } else {
      await renderMathLive(editor);
    }

    commitCurrentPageToState();
    schedulePostInsertOverflow();

    return lastNode;
  }, [
    commitCurrentPageToState,
    pushUndo,
    renderMathJax,
    renderMathLive,
    scheduleAutoSave,
    setSelectionToRange,
    useMathJax,
  ]);

  const syncStepEmbedUi = useCallback((stepBlock) => {
    if (!stepBlock) return;
    const toolbar = stepBlock.querySelector('.document-step-toolbar');
    const embedButtons = toolbar
      ? Array.from(toolbar.querySelectorAll('[data-step-action="html"], [data-step-action="url"], [data-step-action="youtube"]'))
      : [];
    const embedBlocks = Array.from(stepBlock.querySelectorAll('.document-step-embed-block'));
    const hasEmbed = embedBlocks.length > 0;

    embedButtons.forEach((button) => {
      button.style.display = hasEmbed ? 'none' : '';
    });

    embedBlocks.forEach((embedBlock) => {
      if (!embedBlock.querySelector('.document-step-embed-edit')) {
        const editButton = document.createElement('button');
        editButton.type = 'button';
        editButton.className = 'document-step-embed-edit';
        editButton.setAttribute('data-step-embed-action', 'edit');
        editButton.setAttribute('contenteditable', 'false');
        editButton.setAttribute('title', 'Edit embed');
        editButton.setAttribute('aria-label', 'Edit embed');
        editButton.textContent = 'Edit embed';
        embedBlock.appendChild(editButton);
      }
    });
  }, []);

  const handleInsertStep = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;

    pushUndo();
    const range = selection.getRangeAt(0);
    range.deleteContents();

    const stepId = `step_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const container = document.createElement('div');
    container.className = 'document-step-block';
    container.contentEditable = 'false';
    container.setAttribute('data-step-id', stepId);
    container.innerHTML = `
      <div class="document-step-shell">
        <button type="button" class="document-step-delete-button" data-step-action="delete" contenteditable="false" title="Delete step" aria-label="Delete step">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M3 6h18" />
            <path d="M8 6V4h8v2" />
            <path d="M19 6l-1 14H6L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
        <div class="document-step-header">
          <span class="document-step-chip">Step</span>
          <span class="document-step-number" contenteditable="true" data-placeholder="No."></span>
          <span class="document-step-title" contenteditable="true" data-placeholder="Optional step name"></span>
        </div>
        <div class="document-step-toolbar" contenteditable="false">
          <button type="button" class="document-step-action" data-step-action="focus">Text</button>
          <button type="button" class="document-step-action" data-step-action="math">Math</button>
          <button type="button" class="document-step-action" data-step-action="image">Image</button>
          <button type="button" class="document-step-action" data-step-action="audio">Audio</button>
          <button type="button" class="document-step-action" data-step-action="html">HTML</button>
          <button type="button" class="document-step-action" data-step-action="url">URL</button>
          <button type="button" class="document-step-action" data-step-action="youtube">YouTube</button>
          <span class="document-step-toolbar-hint">Paste with Ctrl+V</span>
        </div>
        <div class="document-step-body" contenteditable="true"><p><br></p></div>
      </div>
    `;

    range.insertNode(container);

    const spacer = createZwsNode();
    const trailingParagraph = document.createElement('p');
    trailingParagraph.innerHTML = '<br>';
    container.parentNode?.insertBefore(spacer, container.nextSibling);
    spacer.parentNode?.insertBefore(trailingParagraph, spacer.nextSibling);

    syncStepEmbedUi(container);
    focusEditableNode(trailingParagraph, 'start');

    commitCurrentPageToState();
    scheduleAutoSave();
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
  }, [commitCurrentPageToState, focusEditableNode, pushUndo, scheduleAutoSave, syncStepEmbedUi]);

  const selectAllDocumentPages = useCallback(() => {
    const pageBodies = getPages()
      .map((_, index) => pageBodyRefsMap.current[index])
      .filter(Boolean);

    const fallbackBody = editorRef.current;
    const bodies = pageBodies.length > 0
      ? pageBodies
      : fallbackBody
        ? [fallbackBody]
        : [];
    if (bodies.length === 0) return false;

    const firstBody = bodies[0];
    const lastBody = bodies[bodies.length - 1];
    const range = document.createRange();
    range.setStart(firstBody, 0);
    range.setEnd(lastBody, lastBody.childNodes.length);

    try {
      firstBody.focus({ preventScroll: true });
    } catch (_) {
      firstBody.focus?.();
    }

    setSelectionToRange(range);
    return true;
  }, [getPages, setSelectionToRange]);

  const handleEditorKeyDown = useCallback((e) => {
    const key = e.key.toLowerCase();
    const hasModifier = e.ctrlKey || e.metaKey;

    if (hasModifier && !e.altKey && key === 'a') {
      e.preventDefault();
      selectAllDocumentPages();
      return;
    }

    const selection = window.getSelection();
    if (selection?.rangeCount > 0 && isWithinHeaderOrFooter(selection.getRangeAt(0).commonAncestorContainer)) {
      e.preventDefault();
      const regionNode = selection.getRangeAt(0).commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? selection.getRangeAt(0).commonAncestorContainer.closest('.doc-header, .doc-footer')
        : selection.getRangeAt(0).commonAncestorContainer.parentElement?.closest('.doc-header, .doc-footer');
      moveCaretToBody(editorRef.current, regionNode?.classList.contains('doc-footer') ? 'end' : 'start');
      return;
    }

    if (e.key === '/' && !hasModifier && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      openResponseTypeMenuAtSelection();
      return;
    }

    if (hasModifier && key === 'b') {
      e.preventDefault();
      execFormatCommand('bold');
      return;
    }
    if (hasModifier && key === 'i') {
      e.preventDefault();
      execFormatCommand('italic');
      return;
    }
    if (hasModifier && key === 'u') {
      e.preventDefault();
      execFormatCommand('underline');
      return;
    }
    if (hasModifier && key === 'z' && !e.shiftKey) {
      e.preventDefault();
      handleUndo();
      return;
    }
    if (hasModifier && (key === 'y' || (key === 'z' && e.shiftKey))) {
      e.preventDefault();
      handleRedo();
      return;
    }
    if (hasModifier && key === 'm') {
      e.preventDefault();
      openMathModal();
      return;
    }
    if (hasModifier && key === 's') {
      e.preventDefault();
      handleSave(true);
      return;
    }
    if (hasModifier && e.shiftKey && key === 'l') {
      e.preventDefault();
      execFormatCommand('justifyLeft');
      return;
    }
    if (hasModifier && e.shiftKey && key === 'e') {
      e.preventDefault();
      execFormatCommand('justifyCenter');
      return;
    }
    if (hasModifier && e.shiftKey && key === 'r') {
      e.preventDefault();
      execFormatCommand('justifyRight');
      return;
    }
    if (hasModifier && e.shiftKey && key === 'j') {
      e.preventDefault();
      execFormatCommand('justifyFull');
      return;
    }
    if (hasModifier && e.shiftKey && key === '>') {
      e.preventDefault();
      execFormatCommand('increaseFontSize');
      return;
    }
    if (hasModifier && e.shiftKey && key === '<') {
      e.preventDefault();
      execFormatCommand('decreaseFontSize');
      return;
    }
    if (key === 'tab') {
      e.preventDefault();
      const selection = window.getSelection();
      const anchor = selection?.anchorNode;
      const element = anchor?.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor;
      if (selection && !selection.isCollapsed) {
        execFormatCommand(e.shiftKey ? 'outdent' : 'indent');
      } else if (element?.closest?.('li')) {
        execFormatCommand(e.shiftKey ? 'outdent' : 'indent');
      } else if (e.shiftKey) {
        execFormatCommand('outdent');
      } else {
        document.execCommand('insertHTML', false, '&emsp;&emsp;');
      }
      return;
    }
    // ─── Enter near math / atomic blocks ─────────────────────────────
    // The browser's default paragraph split fails when the cursor sits inside
    // a ZWS spacer node or directly adjacent to a contenteditable="false"
    // math-container — the caret jumps to the top of the page or gets lost.
    // We detect this situation and perform the split manually.
    if (key === 'enter' && !hasModifier && !e.shiftKey) {
      const sel = window.getSelection();
      const editor = editorRef.current;
      if (sel && sel.isCollapsed && sel.rangeCount > 0 && editor) {
        const range = sel.getRangeAt(0);
        const { startContainer } = range;
        const isInZws = isZWS(startContainer) ||
          (startContainer.nodeType === Node.ELEMENT_NODE && startContainer.querySelector?.('.zws'));
        const adjacentMath = findAtomicBefore(range) || findAtomicAfter(range);
        if (isInZws || adjacentMath) {
          e.preventDefault();
          markUndoBoundary();
          // Find the enclosing block element (p, div, etc.) to split after
          const el = startContainer.nodeType === Node.ELEMENT_NODE
            ? startContainer
            : startContainer.parentElement;
          const block = el?.closest('p, div, h1, h2, h3, h4, h5, h6, li, blockquote');
          const newP = document.createElement('p');
          newP.innerHTML = '<br>';
          if (block && editor.contains(block)) {
            block.parentNode.insertBefore(newP, block.nextSibling);
          } else {
            editor.appendChild(newP);
          }
          // Place cursor inside the new paragraph
          const r = document.createRange();
          r.setStart(newP, 0);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
          commitCurrentPageToState();
          scheduleAutoSave();
          return;
        }
      }
    }
    // ─── Two-step math delete: first press selects, second deletes ───
    if ((key === 'backspace' || key === 'delete') && !hasModifier && !e.shiftKey) {
      const sel = window.getSelection();
      const editor = editorRef.current;
      if (sel && sel.rangeCount > 0 && editor) {
        const selectedRange = sel.getRangeAt(0);
        if (!sel.isCollapsed && editor.contains(selectedRange.commonAncestorContainer)) {
          e.preventDefault();
          pushUndo();
          editor.querySelectorAll('.response-area-block[data-response-id]').forEach((block) => {
            if (!selectedRange.intersectsNode(block)) return;
            const blockId = block.getAttribute('data-response-id');
            if (blockId) removeResponseArea(blockId);
          });
          selectedRange.deleteContents();
          selectedRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(selectedRange);
          if (!editor.textContent.trim() && !editor.querySelector('img, .math-container, .image-container')) {
            editor.innerHTML = '<p><br></p>';
          }
          commitCurrentPageToState();
          scheduleAutoSave();
          return;
        }
        // Second press: selection wraps an atomic block → delete it
        if (!sel.isCollapsed) {
          const atomicEls = editor.querySelectorAll(
            '.math-container, .image-container, .response-area-block, .document-step-block, .section-divider-block'
          );
          for (const el of atomicEls) {
            if (sel.containsNode(el, false)) {
              e.preventDefault();
              // If it's a response area, also remove from store
              if (el.classList.contains('response-area-block')) {
                const blockId = el.getAttribute('data-response-id');
                if (blockId) removeResponseArea(blockId);
              }
              const next = el.nextSibling;
              const prev = el.previousSibling;
              if (isZWS(next)) next.remove();
              if (isZWS(prev)) prev.remove();
              el.remove();
              commitCurrentPageToState();
              scheduleAutoSave();
              return;
            }
          }
        }
        // First press: cursor adjacent to math → select it
        if (sel.isCollapsed) {
          const range = sel.getRangeAt(0);
          const mathEl = key === 'backspace' ? findAtomicBefore(range) : findAtomicAfter(range);
          if (mathEl) {
            e.preventDefault();
            const selectRange = document.createRange();
            selectRange.selectNode(mathEl);
            sel.removeAllRanges();
            sel.addRange(selectRange);
            return;
          }
        }
      }
    }
    if (key === 'backspace' && !hasModifier && !e.shiftKey) {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const editor = editorRef.current;

        // At the very start of a page: merge this page into the previous page.
        if (editor && activePageIndex > 0 && !isWithinHeaderOrFooter(range.startContainer)) {
          // Build a range from the start of the body to the cursor position
          // and check if there's any visible content before the cursor
          const testRange = document.createRange();
          try {
            testRange.setStart(editor, 0);
            testRange.setEnd(range.startContainer, range.startOffset);
          } catch (_) {
            // If range construction fails, skip merge
          }
          const fragment = testRange.cloneContents();
          const textBefore = fragment.textContent.replace(/\u200B/g, '').trim();
          const hasMediaBefore = fragment.querySelector('img, math-field, .math-container, .image-container');
          const atPageBodyStart = textBefore.length === 0 && !hasMediaBefore;

          if (atPageBodyStart) {
            e.preventDefault();
            pushUndo();

            // Use getPageHtmlFromShell to preserve header/footer
            const pages = commitCurrentPageToState();
            const prevIndex = activePageIndex - 1;

            // Remember cursor join point: end of previous page body content length
            const prevBody = getPageBodyHtml(pages[prevIndex]);
            const joinOffset = prevBody.length;

            const currentBody = getPageBodyHtml(pages[activePageIndex]);
            pages[prevIndex] = appendOverflowToPage(pages[prevIndex], currentBody);
            pages.splice(activePageIndex, 1);

            const updatedPages = updatePageNumbersInPages(repaginatePages(pages));
            updateLocalPages(updatedPages);

            // Sync all page shells immediately
            updatedPages.forEach((html, idx) => {
              syncPageShell(idx, html);
            });

            scheduleAutoSave();
            setActivePageIndex(prevIndex);

            requestAnimationFrame(() => {
              const targetBody = pageBodyRefsMap.current[prevIndex];
              if (!targetBody) return;
              // Place cursor at the join point (end of original previous page content)
              restoreCursorOffset(targetBody, joinOffset);
              targetBody.focus();
              editorRef.current = targetBody;
            });
            return;
          }
        }

        if (range.startOffset === 0) {
          const element = range.startContainer.nodeType === Node.TEXT_NODE
            ? range.startContainer.parentElement
            : range.startContainer;
          const blockquote = element?.closest('blockquote');
          if (blockquote && editorRef.current?.contains(blockquote)) {
            // Verify cursor is at the very start of the blockquote
            let node = range.startContainer;
            let atStart = true;
            while (node && node !== blockquote) {
              if (node.previousSibling) {
                atStart = false;
                break;
              }
              node = node.parentNode;
            }
            if (atStart) {
              e.preventDefault();
              execFormatCommand('outdent');
              return;
            }
          }
        }
      }
    }
  }, [activePageIndex, commitCurrentPageToState, execFormatCommand, getPages, handleRedo, handleSave, handleUndo, markUndoBoundary, openMathModal, openResponseTypeMenuAtSelection, pushUndo, removeResponseArea, scheduleAutoSave, selectAllDocumentPages, syncPageShell, updateLocalPages, updatePageNumbersInPages]);

  useEffect(() => {
    if (!showResponseTypeMenu) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeResponseTypeMenu();
    };
    const onClick = (e) => {
      const menu = document.getElementById('response-type-menu');
      if (!menu || !menu.contains(e.target)) closeResponseTypeMenu();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [showResponseTypeMenu, closeResponseTypeMenu]);

  // ─── Editor input handler (auto-save) ─────────────────────
  useEffect(() => {
    if (!showAppMenu) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowAppMenu(false);
    };
    const onClick = (e) => {
      if (!appMenuRef.current || appMenuRef.current.contains(e.target)) return;
      setShowAppMenu(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [showAppMenu]);

  const handleEditorInput = useCallback((e) => {
    const nativeEvent = e?.nativeEvent;
    const inputType = nativeEvent?.inputType || '';
    const pageIdxRaw = e?.currentTarget?.getAttribute?.('data-page-index');
    const pageIdx = Number.parseInt(pageIdxRaw || '', 10);
    if (!Number.isNaN(pageIdx)) {
      pendingCursorPageRef.current = pageIdx;
      pendingCursorOffsetRef.current = saveCursorOffset(e.currentTarget);
    } else {
      pendingCursorPageRef.current = activePageIndex;
      pendingCursorOffsetRef.current = saveCursorOffset(editorRef.current);
    }
    const inputTarget = e?.currentTarget || editorRef.current;
    resetPageBodyScroll(inputTarget);
    requestAnimationFrame(() => resetPageBodyScroll(inputTarget));
    const hasBodyOverflow = inputTarget
      ? getPageBodyVisualOverflowPx(inputTarget) > 0.5
      : false;
    const shouldBackfillPages = inputType.startsWith('delete') || inputType === 'historyUndo' || inputType === 'historyRedo';
    allowUnderflowRepaginationRef.current = shouldBackfillPages;
    if (hasBodyOverflow || shouldBackfillPages) {
      commitCurrentPageToState();
    }
    scheduleAutoSave();
    // Always debounce the overflow check. Running it synchronously on every
    // keystroke triggers syncPageShell which replaces the entire page body
    // DOM and destroys cursor position — especially problematic with
    // response area blocks in assessment mode.
    clearTimeout(overflowTimerRef.current);
    if (hasBodyOverflow || shouldBackfillPages) {
      overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 120);
    }
    const inserted = nativeEvent?.data || '';
    const shouldRenderMath =
      inputType === 'insertFromPaste' ||
      inserted.includes('$');

    // Avoid re-rendering math on every keystroke because it mutates editor DOM
    // and can force caret/scroll jumps while typing near inline math.
    if (shouldRenderMath) {
      if (mathRenderTimer.current) clearTimeout(mathRenderTimer.current);
      mathRenderTimer.current = setTimeout(() => {
        if (!editorRef.current) return;
        if (useMathJax) {
          renderMathJax(editorRef.current);
        } else {
          renderMathLive(editorRef.current);
        }
      }, 120);
    }
  }, [activePageIndex, commitCurrentPageToState, renderMathJax, renderMathLive, scheduleAutoSave, useMathJax]);

  const handleEditorBeforeInput = useCallback((e) => {
    const inputType = e?.nativeEvent?.inputType || '';
    if (!inputType || inputType.startsWith('history')) return;
    markUndoBoundary();
  }, [markUndoBoundary]);

  const getEditorSelectionRange = useCallback(() => {
    const editor = editorRef.current;
    const sel = window.getSelection();
    if (!editor || !sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return null;
    return range;
  }, []);

  const insertImageAtRange = useCallback((url, align, rangeOverride) => {
    if (!url) return;
    const editor = editorRef.current;
    const range = rangeOverride || selectionRef.current || getEditorSelectionRange();
    if (!editor || !range) return;

    pushUndo();
    range.deleteContents();

    const container = document.createElement('span');
    container.className = 'image-container';
    container.contentEditable = 'false';
    container.draggable = true;
    container.setAttribute('data-image-align', align || 'left');
    container.style.width = '300px';
    container.style.maxWidth = '100%';
    container.style.position = 'relative';

    const img = document.createElement('img');
    img.src = url;
    img.style.width = '100%';
    img.style.display = 'block';
    img.draggable = false;
    container.appendChild(img);

    range.insertNode(container);

    // ZWS spacer for cursor landing
    const spacer = createZwsNode();
    container.parentNode?.insertBefore(spacer, container.nextSibling);

    // Ensure a block-level paragraph exists after the image for typing
    let nextEditable = spacer.nextSibling;
    while (nextEditable && isZWS(nextEditable)) {
      nextEditable = nextEditable.nextSibling;
    }
    const hasEditableBlock = nextEditable &&
      nextEditable.nodeType === Node.ELEMENT_NODE &&
      !nextEditable.classList.contains('doc-footer') &&
      !nextEditable.classList.contains('image-container') &&
      !nextEditable.classList.contains('math-container') &&
      !nextEditable.classList.contains('response-area-block') &&
      !nextEditable.classList.contains('section-divider-block') &&
      nextEditable.getAttribute('contenteditable') !== 'false';

    let cursorTarget;
    if (!hasEditableBlock) {
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      spacer.parentNode?.insertBefore(p, spacer.nextSibling);
      cursorTarget = p;
    } else {
      cursorTarget = nextEditable;
    }

    commitCurrentPageToState();
    scheduleAutoSave();

    // Overflow check after image insertion
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);

    requestAnimationFrame(() => {
      if (!editor.contains(cursorTarget)) return;
      const sel = window.getSelection();
      const r = document.createRange();
      r.selectNodeContents(cursorTarget);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
      editor.focus();
    });
  }, [commitCurrentPageToState, getEditorSelectionRange, pushUndo, scheduleAutoSave]);

  const handleEditorPaste = useCallback(async (e) => {
    pushUndo();
    const clipboard = e?.clipboardData;
    const items = clipboard?.items ? Array.from(clipboard.items) : [];
    const imageItem = items.find((item) => item.type?.startsWith('image/'));
    const imageFile = imageItem?.getAsFile?.() ||
      (clipboard?.files && Array.from(clipboard.files).find((file) => file.type?.startsWith('image/')));

    if (imageFile) {
      e.preventDefault();
      const range = getEditorSelectionRange();
      const replaceRange = range?.cloneRange() || null;
      if (replaceRange) selectionRef.current = replaceRange.cloneRange();
      const reader = new FileReader();
      reader.onload = () => {
        insertImageAtRange(reader.result, 'left', replaceRange);
      };
      reader.readAsDataURL(imageFile);
      return;
    }
    e.preventDefault();

    const range = getEditorSelectionRange();
    if (!range) return;

    let text = clipboard?.getData?.('text/plain') || '';
    if (!text) {
      const html = clipboard?.getData?.('text/html') || '';
      if (html) {
        const probe = document.createElement('div');
        probe.innerHTML = html;
        text = probe.innerText || probe.textContent || '';
      }
    }

    const html = await convertPastedTextToEditorHtml(text);
    insertHtmlAtCurrentSelection(html, range.cloneRange());
  }, [getEditorSelectionRange, insertHtmlAtCurrentSelection, insertImageAtRange, pushUndo]);

  // ─── Click-to-edit math ──────────────────────────────────
  const handleEditorClick = (e) => {
    const stepAction = e.target.closest('[data-step-action]');
    if (stepAction && editorRef.current?.contains(stepAction)) {
      const stepBlock = stepAction.closest('.document-step-block');
      const action = stepAction.getAttribute('data-step-action');
      handleStepAction(action, stepBlock);
      return;
    }
    const stepEmbedAction = e.target.closest('[data-step-embed-action]');
    if (stepEmbedAction && editorRef.current?.contains(stepEmbedAction)) {
      const embedBlock = stepEmbedAction.closest('.document-step-embed-block');
      const stepBlock = stepEmbedAction.closest('.document-step-block');
      const action = stepEmbedAction.getAttribute('data-step-embed-action');
      if (action === 'edit' && embedBlock && stepBlock) {
        openEmbedModalForStep(stepBlock, embedBlock.getAttribute('data-step-embed-kind') || 'html', embedBlock);
      }
      return;
    }
    const stepAudioAction = e.target.closest('[data-step-audio-action]');
    if (stepAudioAction && editorRef.current?.contains(stepAudioAction)) {
      const stepBlock = stepAudioAction.closest('.document-step-block');
      const audioBlock = stepAudioAction.closest('.document-step-audio-block');
      const action = stepAudioAction.getAttribute('data-step-audio-action');
      if (action === 'replace' && stepBlock) {
        setNarrationError('');
        setNarrationTarget({ type: 'step', stepBlock });
        setShowNarrationModal(true);
      }
      if (action === 'remove' && audioBlock) {
        const audioPath = audioBlock.getAttribute('data-step-audio-path') || null;
        removeAudioResource(audioPath).catch(() => {});
        audioBlock.remove();
        commitCurrentPageToState();
        scheduleAutoSave();
      }
      return;
    }
    // Click on math container → open math editor
    const responseAction = e.target.closest('[data-response-action]');
    if (responseAction && editorRef.current?.contains(responseAction)) {
      const responseBlock = responseAction.closest('.response-area-block');
      const action = responseAction.getAttribute('data-response-action');
      if (action === 'delete' && responseBlock) {
        handleDeleteResponseArea(responseBlock);
      }
      return;
    }

    const mathContainer = e.target.closest('.math-container');
    if (mathContainer && editorRef.current?.contains(mathContainer)) {
      setSelectedImageEl(null);
      const range = document.createRange();
      range.selectNode(mathContainer);
      selectionRef.current = range.cloneRange();
      const latex = mathContainer.getAttribute('data-math-content') || '';
      setInitialMathLatex(latex);
      setMathAnchorRect(mathContainer.getBoundingClientRect());
      setShowMathModal(true);
      return;
    }

    // Click on response area block → open config modal
    const responseBlock = e.target.closest('.response-area-block');
    if (responseBlock && editorRef.current?.contains(responseBlock)) {
      const blockId = responseBlock.getAttribute('data-response-id');
      if (blockId) {
        setSelectedResponseAreaId(blockId);
        setShowResponseAreaConfig(true);
      }
      return;
    }

    // Click on image container → select it
    const stepBlock = e.target.closest('.document-step-block');
    if (stepBlock && editorRef.current?.contains(stepBlock)) {
      setSelectedImageEl(null);
      if (!e.target.closest('[contenteditable="true"]')) {
        ensureStepBodySelection(stepBlock, 'end');
      }
      return;
    }

    const imgContainer = e.target.closest('.image-container');
    if (imgContainer && editorRef.current?.contains(imgContainer)) {
      setSelectedImageEl(imgContainer);
      return;
    }

    // Click elsewhere → deselect image
    setSelectedImageEl(null);
  };

  // ─── Format command (from toolbar) ────────────────────────
  const handleSyncAll = useCallback(async () => {
    if (!editorRef.current) return;
    if (useMathJax) {
      await renderMathJax(editorRef.current);
    } else {
      await renderMathLive(editorRef.current);
    }
    scheduleAutoSave();
  }, [renderMathJax, renderMathLive, scheduleAutoSave, useMathJax]);

  const handleAddPage = useCallback(() => {
    pushUndo();
    const pages = [...commitCurrentPageToState()];
    pages.push(newPageWithRegions(pages));
    const updated = updatePageNumbersInPages(pages);
    updateLocalPages(updated);
    setActivePageIndex(updated.length - 1);
    scheduleAutoSave();
  }, [commitCurrentPageToState, pushUndo, scheduleAutoSave, updateLocalPages, updatePageNumbersInPages]);

  const handleDeletePage = useCallback((index) => {
    const pages = [...getPages()];
    if (pages.length <= 1) return;
    pushUndo();
    pages.splice(index, 1);
    const updated = updatePageNumbersInPages(pages);
    updateLocalPages(updated);
    const newIndex = index >= updated.length ? updated.length - 1 : index;
    setActivePageIndex(newIndex);
    scheduleAutoSave();
  }, [getPages, pushUndo, scheduleAutoSave, updateLocalPages, updatePageNumbersInPages]);

  const handleSelectPage = useCallback((index) => {
    if (index === activePageIndex) return;
    commitCurrentPageToState();
    setActivePageIndex(index);
  }, [activePageIndex, commitCurrentPageToState]);

  const handleScrollPageFocus = useCallback((index) => {
    if (index !== activePageIndex) setActivePageIndex(index);
    editorRef.current = pageBodyRefsMap.current[index] || null;
  }, [activePageIndex]);

  const handleScrollPageBlur = useCallback((event, index) => {
    const shell = event.currentTarget?.closest?.('.page-editor') || pageRefsMap.current[index];
    commitPageShellToState(index, shell);
  }, [commitPageShellToState]);

  const persistHeaderChanges = useCallback((headerHost, pageIndex) => {
    if (!headerHost) return;
    const sourceHeader = headerHost.querySelector('.doc-header');
    if (!sourceHeader) return;

    const committedPages = commitCurrentPageToState();
    const sourceHeaderHtml = sourceHeader.outerHTML;
    let nextPages = committedPages.map((pageHtml) => {
      const container = document.createElement('div');
      container.innerHTML = pageHtml || '';
      container.querySelector('.doc-header')?.remove();
      container.insertAdjacentHTML('afterbegin', sourceHeaderHtml);
      return container.innerHTML;
    });
    nextPages = updatePageNumbersInPages(nextPages);
    updateLocalPages(nextPages);

    nextPages.forEach((pageHtml, index) => {
      if (index === pageIndex) return;
      const shell = pageRefsMap.current[index];
      const target = shell?.querySelector('[data-page-header-region="true"]');
      if (!target) return;
      target.innerHTML = getPageRegionsHtml(pageHtml).headerHtml;
      updatePageShellRegionSpacing(shell);
    });
    updatePageShellRegionSpacing(pageRefsMap.current[pageIndex]);
    scheduleAutoSave();
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 180);
  }, [commitCurrentPageToState, scheduleAutoSave, updateLocalPages, updatePageNumbersInPages]);

  const handleHeaderFocus = useCallback((event, pageIndex) => {
    if (pageIndex !== activePageIndex) setActivePageIndex(pageIndex);
    const header = event.currentTarget.querySelector('.doc-header');
    const placeholder = header?.querySelector('.doc-region-placeholder');
    if (!header || !placeholder || header.textContent.replace(/\u00a0/g, '').trim()) return;
    header.innerHTML = '';
    const range = document.createRange();
    range.selectNodeContents(header);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [activePageIndex]);

  const handleHeaderInput = useCallback((event) => {
    const pageIndex = Number.parseInt(event.currentTarget.getAttribute('data-page-index') || '', 10);
    if (Number.isNaN(pageIndex)) return;
    persistHeaderChanges(event.currentTarget, pageIndex);
  }, [persistHeaderChanges]);

  const handleHeaderClick = useCallback((event) => {
    const image = event.target.closest?.('img[data-header-image="true"]');
    if (!image || !event.currentTarget.contains(image)) {
      setSelectedImageEl(null);
      return;
    }
    event.preventDefault();
    setSelectedImageEl(image);
  }, []);

  const handleHeaderPaste = useCallback((event) => {
    const clipboard = event.clipboardData;
    const items = clipboard?.items ? Array.from(clipboard.items) : [];
    const imageItem = items.find((item) => item.type?.startsWith('image/'));
    const imageFile = imageItem?.getAsFile?.() ||
      (clipboard?.files && Array.from(clipboard.files).find((file) => file.type?.startsWith('image/')));
    if (!imageFile) return;

    event.preventDefault();
    pushUndo();
    const headerHost = event.currentTarget;
    const pageIndex = Number.parseInt(headerHost.getAttribute('data-page-index') || '', 10);
    const selection = window.getSelection();
    const liveRange = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const savedRange = liveRange && headerHost.contains(liveRange.commonAncestorContainer)
      ? liveRange.cloneRange()
      : null;
    if (!savedRange || Number.isNaN(pageIndex)) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (!headerHost.isConnected) return;
      const image = document.createElement('img');
      image.src = String(reader.result || '');
      image.alt = 'Header image';
      image.setAttribute('data-header-image', 'true');
      image.setAttribute('data-image-align', 'left');
      image.contentEditable = 'false';

      const spacer = document.createTextNode('\u00a0');
      savedRange.deleteContents();
      savedRange.insertNode(spacer);
      savedRange.insertNode(image);
      const afterRange = document.createRange();
      afterRange.setStartAfter(spacer);
      afterRange.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(afterRange);
      persistHeaderChanges(headerHost, pageIndex);
      setSelectedImageEl(image);
    };
    reader.readAsDataURL(imageFile);
  }, [persistHeaderChanges, pushUndo]);

  const handleHeaderFirstPageOnlyChange = useCallback((checked) => {
    setHeaderFirstPageOnly(checked);
    const pages = commitCurrentPageToState();
    const canonicalHeader = getRegionsFromPages(pages).headerHtml;
    const nextPages = pages.map((pageHtml, index) => {
      const container = document.createElement('div');
      container.innerHTML = pageHtml || '';
      let header = container.querySelector('.doc-header');
      if (!checked && !header && canonicalHeader) {
        const headerHost = document.createElement('div');
        headerHost.innerHTML = canonicalHeader;
        header = headerHost.querySelector('.doc-header');
        if (header) container.prepend(header);
      }
      if (header) {
        if (checked) {
          header.removeAttribute('data-header-repeat');
          if (index > 0) header.remove();
        }
        else header.setAttribute('data-header-repeat', 'all');
      }
      return container.innerHTML;
    });
    updateLocalPages(nextPages);
    nextPages.forEach((pageHtml, index) => syncPageShell(index, pageHtml));
    scheduleAutoSave();
  }, [commitCurrentPageToState, scheduleAutoSave, syncPageShell, updateLocalPages]);

  const handleEditorMouseDown = useCallback((event) => {
    const editor = event.currentTarget;
    const target = event.target;
    const stepAction = target?.closest?.('[data-step-action]');
    if (stepAction) {
      event.preventDefault();
      return;
    }
    const region = target?.closest?.('.doc-header, .doc-footer');
    if (region) {
      event.preventDefault();
      requestAnimationFrame(() => moveCaretToBody(editor, region.classList.contains('doc-footer') ? 'end' : 'start'));
      return;
    }
    const sel = window.getSelection();
    if (sel && isWithinHeaderOrFooter(sel.focusNode)) {
      requestAnimationFrame(() => moveCaretToBody(editor, 'end'));
    }
  }, []);

  const handleNewDocument = useCallback(() => {
    setCurrentDocument({
      id: null,
      name: 'Untitled Document',
      pages: [''],
      contentType: 'editor',
      pdf: null,
      narration: null,
      folderPath: '',
      isTemplate: false,
      metadata: { createdAt: new Date(), updatedAt: new Date() },
    });
    setDocumentName('Untitled Document');
    setActivePageIndex(0);
    setLastSaved(null);
    setSaveError('');
    navigate('/document/new');
  }, [navigate, setCurrentDocument]);

  const loadMyDocumentsForModal = useCallback(async () => {
    if (!user?.uid) {
      setModalDocuments([]);
      return;
    }
    const docs = await loadDocuments(user.uid);

    let templateDocs = [];
    try {
      const templatesRef = collection(db, COLLECTIONS.DOCUMENTS);
      const templateQuery = query(templatesRef, where('isTemplate', '==', true));
      const templateSnap = await getDocs(templateQuery);
      templateDocs = templateSnap.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        return {
          id: docSnap.id,
          ...data,
          updatedAt: data.updatedAt?.toDate?.() || new Date(0),
        };
      });
    } catch (error) {
      console.warn('Template documents could not be loaded for modal:', error);
    }

    const ownDocs = (docs || []).map((item) => ({ ...item, modalKind: 'mine' }));
    const ownIds = new Set(ownDocs.map((item) => item.id));
    const sharedTemplates = templateDocs
      .filter((item) => (item.contentType || 'editor') === 'editor')
      .filter((item) => !ownIds.has(item.id))
      .map((item) => ({ ...item, modalKind: 'template' }));

    setModalDocuments([...sharedTemplates, ...ownDocs]);
  }, [loadDocuments, user?.uid]);

  const loadSharedDocumentsForModal = useCallback(async () => {
    if (!user?.uid) {
      setModalDocuments([]);
      return;
    }
    const sharesRef = collection(db, COLLECTIONS.SHARES);
    const recipientQuery = query(sharesRef, where('recipientId', '==', user.uid));
    const recipientSnap = await getDocs(recipientQuery);
    const sharedWithMe = recipientSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    setModalDocuments(sharedWithMe);
  }, [user?.uid]);

  const openDocumentsModal = useCallback(async (mode) => {
    setDocumentModalMode(mode);
    setModalFolderFilter('all');
    setShowDocumentModal(true);
    setModalLoading(true);
    setModalError('');
    try {
      if (mode === 'shared') {
        await loadSharedDocumentsForModal();
      } else {
        await loadMyDocumentsForModal();
      }
    } catch (error) {
      console.error('Failed to load documents modal data:', error);
      setModalError('Could not load documents. Please try again.');
      setModalDocuments([]);
    } finally {
      setModalLoading(false);
    }
  }, [loadMyDocumentsForModal, loadSharedDocumentsForModal]);

  const openDocumentFromModal = useCallback((item) => {
    const id = documentModalMode === 'shared' ? item.documentId : item.id;
    if (!id) return;
    setShowDocumentModal(false);
    navigate(`/document/${id}`);
  }, [documentModalMode, navigate]);

  const handleCloneTemplate = useCallback(async (item) => {
    if (!user?.uid) return;
    try {
      setTemplateActionId(item.id);
      setModalError('');
      const template = await FirestoreService.getDocument(item.id);
      const baseName = String(template?.name || item?.name || 'Untitled Document').trim() || 'Untitled Document';
      const cloned = await createDocument({
        name: `Copy of ${baseName}`,
        pages: Array.isArray(template?.pages) && template.pages.length > 0 ? template.pages : [''],
        userId: user.uid,
        folderPath: '',
        assessment: template?.assessment || null,
        contentType: 'editor',
        pdf: null,
        narration: null,
        isTemplate: false,
        templateSourceId: template?.id || item.id,
        templateSourceName: baseName,
      });
      setShowDocumentModal(false);
      if (cloned?.id) {
        navigate(`/document/${cloned.id}`);
      }
    } catch (error) {
      console.error('Failed to clone template:', error);
      setModalError('Could not clone this template. Please try again.');
    } finally {
      setTemplateActionId('');
    }
  }, [createDocument, navigate, user?.uid]);

  const handleDeleteDocument = useCallback(async (item) => {
    const name = item.name || item.documentName || 'Untitled Document';
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const wasCurrentDoc = currentDocument?.id === item.id;
      await deleteDocument(item.id);
      setModalDocuments((prev) => prev.filter((d) => d.id !== item.id));
      if (wasCurrentDoc) {
        setShowDocumentModal(false);
        navigate('/document/new');
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }, [currentDocument?.id, deleteDocument, navigate]);

  const handleOpenFolderModal = useCallback(() => {
    setFolderDraft(normalizeFolderPath(currentDocument?.folderPath || ''));
    setShowFolderModal(true);
  }, [currentDocument?.folderPath]);

  const handleSaveFolder = useCallback(() => {
    const doc = useDocumentStore.getState().currentDocument;
    if (!doc) return;
    useDocumentStore.getState().setCurrentDocument({
      ...doc,
      folderPath: normalizeFolderPath(folderDraft),
    });
    setShowFolderModal(false);
    scheduleAutoSave();
  }, [folderDraft, scheduleAutoSave]);

  const handleTemplateStatusChange = useCallback(async (item, nextIsTemplate) => {
    if (!user?.uid || !item?.id) return;
    try {
      setTemplateActionId(item.id);
      setModalError('');
      await updateDocument(item.id, { isTemplate: nextIsTemplate });
      setModalDocuments((prev) => prev.map((entry) => (
        entry.id === item.id
          ? { ...entry, isTemplate: nextIsTemplate }
          : entry
      )));
      if (currentDocument?.id === item.id) {
        useDocumentStore.getState().setCurrentDocument({
          ...useDocumentStore.getState().currentDocument,
          isTemplate: nextIsTemplate,
        });
      }
    } catch (error) {
      console.error('Failed to update template status:', error);
      setModalError('Could not update template status. Please try again.');
    } finally {
      setTemplateActionId('');
    }
  }, [currentDocument?.id, updateDocument, user?.uid]);

  const handleInsertMath = useCallback(async (latex) => {
    setShowMathModal(false);
    if (!latex?.trim()) {
      return;
    }

    const editor = editorRef.current;
    const range = selectionRef.current;
    if (!editor || !range) return;

      pushUndo();
      range.deleteContents();
      const mathUid = `math-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const mathSpan = document.createElement('span');
      mathSpan.className = 'math-container';
      mathSpan.contentEditable = 'false';
      mathSpan.style.cursor = 'pointer';
      mathSpan.setAttribute('data-math-content', latex);
      mathSpan.setAttribute('data-math-uid', mathUid);
      mathSpan.innerHTML = `\\(${latex}\\)`;
      range.insertNode(mathSpan);

      // Ensure there's spacing and a cursor landing spot after math
      let spaceNode = null;
      let spacer = null;
      const parent = mathSpan.parentNode;
      if (parent) {
        let nextNode = mathSpan.nextSibling;
        if (isZWS(nextNode)) nextNode = nextNode.nextSibling;
        if (nextNode?.nodeType === Node.TEXT_NODE && nextNode.textContent?.startsWith(' ')) {
          spaceNode = nextNode;
        } else {
          spaceNode = document.createTextNode(' ');
          parent.insertBefore(spaceNode, mathSpan.nextSibling);
        }

        let afterSpace = spaceNode.nextSibling;
        if (!isZWS(afterSpace)) {
          spacer = createZwsNode();
          parent.insertBefore(spacer, afterSpace);
        } else {
          spacer = afterSpace;
        }

        // Clean up duplicate ZWS nodes from repeated edits
        let zwsNext = spacer?.nextSibling;
        while (isZWS(zwsNext)) {
          const toRemove = zwsNext;
          zwsNext = zwsNext.nextSibling;
          toRemove.remove();
        }
      }

    // Render math first, then place cursor after React removes the modal
    if (useMathJax) {
      await renderMathJax(editor);
    } else {
      await renderMathLive(editor);
    }

    // Defer cursor placement so React has finished removing the modal.
    // Place cursor in the space text node (offset 1) — text nodes are the most
    // reliable cursor targets in contenteditable; ZWS spans and setStartAfter
    // on non-text nodes are inconsistently honoured by browsers.
      const placeCursorAfterMath = () => {
        const sel = window.getSelection();
        if (!sel) return;

        // Find the space text node — prefer using spaceNode if still in DOM
        let cursorTarget = spaceNode;
        if (!cursorTarget || !editor.contains(cursorTarget)) {
          // Re-find the math span by unique ID
          const mathEl = editor.querySelector(`[data-math-uid="${mathUid}"]`);
          if (mathEl && mathEl.parentNode) {
            // Walk forward past ZWS to find or create the space text node
            let sibling = mathEl.nextSibling;
            while (isZWS(sibling)) sibling = sibling.nextSibling;
            if (sibling?.nodeType === Node.TEXT_NODE && sibling.textContent?.startsWith(' ')) {
              cursorTarget = sibling;
            } else {
              const newSpace = document.createTextNode(' ');
              const newSpacer = createZwsNode();
              const insertBefore = mathEl.nextSibling;
              mathEl.parentNode.insertBefore(newSpace, insertBefore);
              mathEl.parentNode.insertBefore(newSpacer, newSpace.nextSibling);
              cursorTarget = newSpace;
            }
          } else {
            return;
          }
        }

        try {
          const r = document.createRange();
          // Place cursor at offset 1 inside the space text node (after the space char)
          const offset = Math.min(1, cursorTarget.textContent?.length || 0);
          r.setStart(cursorTarget, offset);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        } catch (_) { /* ignore range errors */ }
        editor.focus();
      };

    // First attempt after a frame, second attempt after 80ms as a fallback
    requestAnimationFrame(() => {
      placeCursorAfterMath();
      setTimeout(placeCursorAfterMath, 80);
    });

    scheduleAutoSave();
  }, [pushUndo, renderMathJax, renderMathLive, scheduleAutoSave, useMathJax]);

  // ─── Image insertion ────────────────────────────────────────
  const openImageModal = useCallback(() => {
    const range = resolveEditorSelectionRange();
    if (!range) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    selectionRef.current = range.cloneRange();
    setShowImageModal(true);
  }, [resolveEditorSelectionRange]);

  const handleInsertImage = useCallback(({ url, align }) => {
    setShowImageModal(false);
    insertImageAtRange(url, align, selectionRef.current);
  }, [insertImageAtRange]);

  const handleInsertSectionDivider = useCallback(async () => {
    const range = resolveEditorSelectionRange();
    if (!range) return;

    const dividerId = `section-divider-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    await insertHtmlAtCurrentSelection(
      `<div class="section-divider-block" contenteditable="false" data-section-divider-id="${dividerId}">` +
        '<span class="section-divider-line" aria-hidden="true"></span>' +
        '<span class="section-divider-label" contenteditable="true" spellcheck="true">Section label</span>' +
      '</div><p><br></p>',
      range.cloneRange()
    );

    requestAnimationFrame(() => {
      const editor = editorRef.current;
      const label = editor?.querySelector(`[data-section-divider-id="${dividerId}"] .section-divider-label`);
      if (!label) return;
      label.focus();
      const selection = window.getSelection();
      const labelRange = document.createRange();
      labelRange.selectNodeContents(label);
      selection?.removeAllRanges();
      selection?.addRange(labelRange);
      selectionRef.current = labelRange.cloneRange();
    });
  }, [insertHtmlAtCurrentSelection, resolveEditorSelectionRange]);

  const closeEmbedModal = useCallback(() => {
    setShowEmbedModal(false);
    setEmbedModalError('');
    setEmbedModalHtml('');
    setEmbedModalUrl('');
    setEmbedModalMode('html');
    stepEmbedTargetRef.current = null;
    stepEmbedRangeRef.current = null;
    stepEmbedEditRef.current = null;
  }, []);

  const openEmbedModalForStep = useCallback((stepBlock, kind = 'html', existingEmbedBlock = null) => {
    const selectionContext = ensureStepBodySelection(stepBlock);
    if (!selectionContext) return;
    stepEmbedTargetRef.current = stepBlock;
    stepEmbedEditRef.current = existingEmbedBlock;
    if (existingEmbedBlock) {
      const editRange = document.createRange();
      editRange.selectNode(existingEmbedBlock);
      stepEmbedRangeRef.current = editRange;
    } else {
      stepEmbedRangeRef.current = selectionContext.range.cloneRange();
    }
    setEmbedModalError('');
    const resolvedMode = existingEmbedBlock?.getAttribute('data-step-embed-kind') || kind;
    setEmbedModalMode(resolvedMode === 'youtube' ? 'youtube' : resolvedMode === 'url' ? 'url' : 'html');
    if (existingEmbedBlock) {
      if (resolvedMode === 'html') {
        setEmbedModalHtml(existingEmbedBlock.querySelector('.document-step-embed-content')?.innerHTML || '');
        setEmbedModalUrl('');
      } else {
        setEmbedModalHtml('');
        setEmbedModalUrl(existingEmbedBlock.getAttribute('data-embed-src') || '');
      }
    } else {
      setEmbedModalHtml('');
      setEmbedModalUrl('');
    }
    setShowEmbedModal(true);
  }, [ensureStepBodySelection]);

  const handleInsertStepEmbed = useCallback(async () => {
    const stepBlock = stepEmbedTargetRef.current;
    const savedRange = stepEmbedRangeRef.current;
    const editEmbedBlock = stepEmbedEditRef.current;
    if (!stepBlock || !savedRange) {
      closeEmbedModal();
      return;
    }

    let embedHtml = '';
    if (embedModalMode === 'html') {
      const sanitizedHtml = getSanitizedEmbedHtml(embedModalHtml);
      if (!sanitizedHtml) {
        setEmbedModalError('Paste valid HTML to embed.');
        return;
      }
      embedHtml = `
        <div class="document-step-embed-block" contenteditable="false" data-step-embed-kind="html">
          <div class="document-step-embed-label"><span class="document-step-embed-icon">${'<'}${'>'}</span>HTML Embed</div>
          <div class="document-step-embed-content">${sanitizedHtml}</div>
        </div>
      `;
    } else if (embedModalMode === 'url') {
      const embedUrl = getSafeEmbedUrl(embedModalUrl);
      if (!embedUrl) {
        setEmbedModalError('Enter a valid http or https URL.');
        return;
      }
      embedHtml = `
        <div class="document-step-embed-block" contenteditable="false" data-step-embed-kind="url" data-embed-src="${embedUrl}">
          <div class="document-step-embed-label"><span class="document-step-embed-icon">↗</span>Embed URL</div>
          <div class="document-step-embed-content">
            <iframe
              src="${embedUrl}"
              title="Embedded content"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      `;
    } else {
      const youtubeUrl = getYouTubeEmbedUrl(embedModalUrl);
      if (!youtubeUrl) {
        setEmbedModalError('Enter a valid YouTube URL or 11-character video ID.');
        return;
      }
      embedHtml = `
        <div class="document-step-embed-block" contenteditable="false" data-step-embed-kind="youtube" data-embed-src="${embedModalUrl}">
          <div class="document-step-embed-content">
            <iframe
              src="${youtubeUrl}"
              title="YouTube video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            ></iframe>
          </div>
        </div>
      `;
    }

    if (editEmbedBlock && stepBlock.contains(editEmbedBlock)) {
      editEmbedBlock.outerHTML = embedHtml;
      syncStepEmbedUi(stepBlock);
      commitCurrentPageToState();
      scheduleAutoSave();
      clearTimeout(overflowTimerRef.current);
      overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
      if (useMathJax) {
        await renderMathJax(editorRef.current);
      } else {
        await renderMathLive(editorRef.current);
      }
    } else {
      await insertHtmlAtCurrentSelection(`${embedHtml}<p><br></p>`, savedRange.cloneRange());
      syncStepEmbedUi(stepBlock);
    }
    closeEmbedModal();
  }, [
    commitCurrentPageToState,
    closeEmbedModal,
    embedModalHtml,
    embedModalMode,
    embedModalUrl,
    insertHtmlAtCurrentSelection,
    renderMathJax,
    renderMathLive,
    scheduleAutoSave,
    syncStepEmbedUi,
    useMathJax,
  ]);

  const handleDeleteStep = useCallback((stepBlock) => {
    const editor = editorRef.current;
    if (!editor || !stepBlock || !editor.contains(stepBlock)) return;

    pushUndo();

    const next = stepBlock.nextSibling;
    const prev = stepBlock.previousSibling;
    const nextAfterSpacer = isZWS(next) ? next.nextSibling : next;
    const previousEditable = isZWS(prev) ? prev.previousSibling : prev;

    if (isZWS(next)) next.remove();
    if (isZWS(prev)) prev.remove();
    stepBlock.remove();

    const cursorTarget = nextAfterSpacer && editor.contains(nextAfterSpacer)
      ? nextAfterSpacer
      : previousEditable && editor.contains(previousEditable)
        ? previousEditable
        : editor;

    requestAnimationFrame(() => {
      if (cursorTarget?.nodeType === Node.ELEMENT_NODE) {
        focusEditableNode(cursorTarget, 'start');
      }
    });

    commitCurrentPageToState();
    scheduleAutoSave();
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
  }, [commitCurrentPageToState, focusEditableNode, pushUndo, scheduleAutoSave]);

  const handleDeleteResponseArea = useCallback((responseBlock) => {
    const editor = editorRef.current;
    const blockId = responseBlock?.getAttribute('data-response-id');
    if (!editor || !responseBlock || !editor.contains(responseBlock)) return;

    pushUndo();

    if (blockId) {
      removeResponseArea(blockId);
      if (selectedResponseAreaId === blockId) {
        setSelectedResponseAreaId(null);
        setShowResponseAreaConfig(false);
      }
    }

    const next = responseBlock.nextSibling;
    const prev = responseBlock.previousSibling;
    const nextAfterSpacer = isZWS(next) ? next.nextSibling : next;
    const previousEditable = isZWS(prev) ? prev.previousSibling : prev;

    if (isZWS(next)) next.remove();
    if (isZWS(prev)) prev.remove();
    responseBlock.remove();

    const cursorTarget = nextAfterSpacer && editor.contains(nextAfterSpacer)
      ? nextAfterSpacer
      : previousEditable && editor.contains(previousEditable)
        ? previousEditable
        : editor;

    requestAnimationFrame(() => {
      if (cursorTarget?.nodeType === Node.ELEMENT_NODE) {
        focusEditableNode(cursorTarget, 'start');
      }
    });

    commitCurrentPageToState();
    scheduleAutoSave();
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
  }, [commitCurrentPageToState, focusEditableNode, pushUndo, removeResponseArea, scheduleAutoSave, selectedResponseAreaId]);

  const handleStepAction = useCallback((action, stepBlock) => {
    if (!stepBlock) return;

    if (action === 'focus') {
      ensureStepBodySelection(stepBlock, 'end');
      return;
    }

    if (action === 'math') {
      if (!ensureStepBodySelection(stepBlock, 'end')) return;
      openMathModal();
      return;
    }

    if (action === 'image') {
      if (!ensureStepBodySelection(stepBlock, 'end')) return;
      openImageModal();
      return;
    }

    if (action === 'audio') {
      setNarrationError('');
      setNarrationTarget({ type: 'step', stepBlock });
      setShowNarrationModal(true);
      return;
    }

    if (action === 'delete') {
      handleDeleteStep(stepBlock);
      return;
    }

    if (action === 'html' || action === 'url' || action === 'youtube') {
      if (stepBlock.querySelector('.document-step-embed-block')) return;
      openEmbedModalForStep(stepBlock, action);
    }
  }, [ensureStepBodySelection, handleDeleteStep, openEmbedModalForStep, openImageModal, openMathModal]);

  // ─── Response area insertion ────────────────────────────────
  const handleInsertResponseArea = useCallback((type) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    pushUndo();
    const range = sel.getRangeAt(0);
    range.deleteContents();

    const blockId = `ra_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const container = document.createElement('div');
    container.className = 'response-area-block';
    container.contentEditable = 'false';
    container.setAttribute('data-response-id', blockId);
    container.setAttribute('data-response-type', type);
    container.setAttribute('data-response-label', '');
    const initialConfig = {
      type,
      label: '',
      required: true,
      options: (type === 'multiple-choice' || type === 'multi-select') ? ['', ''] : null,
      points: 1,
    };
    container.innerHTML = buildResponseAreaBlockHtml(type, initialConfig);

    const startElement = range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer
      : range.startContainer.parentElement;
    const emptyParagraph = startElement?.closest?.('p');

    if (emptyParagraph && editor.contains(emptyParagraph) && isEffectivelyEmptyParagraph(emptyParagraph)) {
      emptyParagraph.parentNode?.insertBefore(container, emptyParagraph);
      emptyParagraph.remove();
    } else {
      range.insertNode(container);
    }

    // Keep a real block-level paragraph after the response area so it sits in
    // normal document flow and the caret has a stable landing point.
    const spacer = createZwsNode();
    const trailingParagraph = document.createElement('p');
    trailingParagraph.innerHTML = '<br>';
    container.parentNode?.insertBefore(spacer, container.nextSibling);
    spacer.parentNode?.insertBefore(trailingParagraph, spacer.nextSibling);
    focusEditableNode(trailingParagraph, 'start');

    // Add to store
    addResponseArea(blockId, {
      ...initialConfig,
      correctAnswer: null,
      correctAnswers: null,
    });

    commitCurrentPageToState();
    scheduleAutoSave();
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
  }, [addResponseArea, commitCurrentPageToState, focusEditableNode, pushUndo, scheduleAutoSave]);

  const handleDuplicateResponseArea = useCallback((responseBlock) => {
    const editor = editorRef.current;
    const blockId = responseBlock?.getAttribute('data-response-id');
    const blockType = responseBlock?.getAttribute('data-response-type');
    const sourceConfig = blockId ? currentDocument?.assessment?.responseAreas?.[blockId] : null;
    if (!editor || !responseBlock || !blockId || !blockType || !sourceConfig) return;

    pushUndo();

    const nextBlockId = `ra_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const duplicateBlock = responseBlock.cloneNode(true);
    duplicateBlock.setAttribute('data-response-id', nextBlockId);
    duplicateBlock.setAttribute('data-response-type', blockType);
    duplicateBlock.setAttribute('data-response-label', sourceConfig.label || '');

    const nextSibling = responseBlock.nextSibling;
    const insertAfter = isZWS(nextSibling) ? nextSibling.nextSibling : nextSibling;
    responseBlock.parentNode?.insertBefore(duplicateBlock, insertAfter || null);

    const spacer = createZwsNode();
    const trailingParagraph = document.createElement('p');
    trailingParagraph.innerHTML = '<br>';
    duplicateBlock.parentNode?.insertBefore(spacer, duplicateBlock.nextSibling);
    spacer.parentNode?.insertBefore(trailingParagraph, spacer.nextSibling);
    focusEditableNode(trailingParagraph, 'start');

    const duplicatedConfig = JSON.parse(JSON.stringify(sourceConfig));
    const assessment = currentDocument?.assessment;
    const responseAreas = {
      ...(assessment?.responseAreas || {}),
      [nextBlockId]: duplicatedConfig,
    };
    const existingOrder = Array.isArray(assessment?.responseAreaOrder) ? assessment.responseAreaOrder : [];
    const sourceIndex = existingOrder.indexOf(blockId);
    const responseAreaOrder = [...existingOrder];
    responseAreaOrder.splice(sourceIndex >= 0 ? sourceIndex + 1 : responseAreaOrder.length, 0, nextBlockId);

    if (currentDocument) {
      setCurrentDocument({
        ...currentDocument,
        assessment: {
          ...(assessment || {}),
          responseAreas,
          responseAreaOrder,
        },
      });
    }

    commitCurrentPageToState();
    scheduleAutoSave();
    clearTimeout(overflowTimerRef.current);
    overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
  }, [commitCurrentPageToState, currentDocument, focusEditableNode, pushUndo, scheduleAutoSave, setCurrentDocument]);

  const handleSelectResponseType = useCallback((type) => {
    const range = responseTypeRangeRef.current;
    if (range) {
      setSelectionToRange(range);
    }
    closeResponseTypeMenu();
    if (type === 'step') {
      handleInsertStep();
      return;
    }
    handleInsertResponseArea(type);
  }, [closeResponseTypeMenu, handleInsertResponseArea, handleInsertStep, setSelectionToRange]);

  const handleGenerateCreatorContent = useCallback(async () => {
    if (needsProfileCompletion) {
      setCreatorError('Complete your profile before using AI Content Creator.');
      setShowProfileModal(true);
      return;
    }

    const subject = creatorSubject.trim();
    const grade = creatorGrade.trim();
    const topic = creatorTopic.trim();
    const subtopic = creatorSubtopic.trim();
    const contentType = creatorContentType.trim();
    const projectId = creatorProjectId.trim();
    const instructions = creatorInstructions.trim();

    if (!subject || !grade || (!topic && !subtopic) || !contentType) {
      setCreatorError('Subject, grade, content type, and topic or subtopic are required.');
      return;
    }

    setCreatorError('');
    setIsGeneratingCreatorContent(true);
    try {
      const generateFn = httpsCallable(functions, 'generateContentCreatorDraft');
      const result = await generateFn({
        subject,
        grade,
        topic,
        subtopic,
        contentType,
        projectId: projectId || null,
        instructions,
      });
      const generated = String(result?.data?.content || '').trim();
      if (!generated) {
        throw new Error('No content was generated.');
      }
      setCreatorContent(generated);
      setCreatorDraft(generated);
    } catch (error) {
      setCreatorError(error?.message || 'Failed to generate content.');
    } finally {
      setIsGeneratingCreatorContent(false);
    }
  }, [
    creatorSubject,
    creatorGrade,
    creatorTopic,
    creatorSubtopic,
    creatorContentType,
    creatorProjectId,
    creatorInstructions,
    needsProfileCompletion,
  ]);

  const handleAddCreatorContentToDocument = useCallback(async () => {
    const text = creatorDraft.trim();
    if (!text) return;
    if (useV2Engine) {
      v2EngineRef.current?.insertMarkdown?.(text);
      return;
    }
    if (!editorRef.current) return;

    setCreatorError('');
    pushUndo();

    let htmlContent = '';
    try {
      htmlContent = await MathUtils.processContentWithMarkdown(text);
    } catch (err) {
      htmlContent = text.replace(/\n/g, '<br>');
    }

    const blockHtml = `<div class="generated-content-block">${htmlContent}</div><p><br></p>`;
    const editor = editorRef.current;

    if (restoreSelectionIfNeeded()) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const wrapper = document.createElement('div');
        wrapper.innerHTML = blockHtml;
        const fragment = document.createDocumentFragment();
        let lastNode = null;
        while (wrapper.firstChild) {
          lastNode = fragment.appendChild(wrapper.firstChild);
        }
        range.insertNode(fragment);
        if (lastNode) {
          const afterRange = document.createRange();
          afterRange.setStartAfter(lastNode);
          afterRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(afterRange);
        }
      } else {
        editor.insertAdjacentHTML('beforeend', blockHtml);
      }
    } else {
      editor.insertAdjacentHTML('beforeend', blockHtml);
    }

    commitCurrentPageToState();
    scheduleAutoSave();
    if (useMathJax) {
      await renderMathJax(editor);
    } else {
      await renderMathLive(editor);
    }
  }, [
    creatorDraft,
    commitCurrentPageToState,
    pushUndo,
    renderMathJax,
    renderMathLive,
    restoreSelectionIfNeeded,
    scheduleAutoSave,
    useMathJax,
    useV2Engine,
  ]);

  // ─── Image selection + resize overlay ──────────────────────
  const updateImageOverlay = useCallback((el) => {
    if (!el) { setImageOverlayRect(null); return; }
    const rect = el.getBoundingClientRect();
    setImageOverlayRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
  }, []);

  useEffect(() => {
    if (!selectedImageEl) { setImageOverlayRect(null); return; }
    updateImageOverlay(selectedImageEl);
    const onScroll = () => updateImageOverlay(selectedImageEl);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [selectedImageEl, updateImageOverlay]);

  const handleResizeStart = useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    const el = selectedImageEl;
    if (!el) return;
    const startX = e.clientX;
    const startW = el.offsetWidth;
    const isHeaderImage = el.matches?.('img[data-header-image="true"]');
    const onMove = (ev) => {
      const dx = corner.includes('right') ? ev.clientX - startX : startX - ev.clientX;
      const newW = Math.max(60, startW + dx);
      el.style.width = `${newW}px`;
      if (isHeaderImage) el.style.maxHeight = 'none';
      updateImageOverlay(el);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (isHeaderImage) {
        const headerHost = el.closest('[data-page-header-region="true"]');
        const pageIndex = Number.parseInt(headerHost?.getAttribute('data-page-index') || '', 10);
        if (headerHost && !Number.isNaN(pageIndex)) persistHeaderChanges(headerHost, pageIndex);
      } else {
        commitCurrentPageToState();
        scheduleAutoSave();
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [selectedImageEl, updateImageOverlay, commitCurrentPageToState, persistHeaderChanges, scheduleAutoSave]);

  const handleImageAlignChange = useCallback((newAlign) => {
    if (!selectedImageEl) return;
    selectedImageEl.setAttribute('data-image-align', newAlign);
    if (selectedImageEl.matches?.('img[data-header-image="true"]')) {
      const headerHost = selectedImageEl.closest('[data-page-header-region="true"]');
      const pageIndex = Number.parseInt(headerHost?.getAttribute('data-page-index') || '', 10);
      if (headerHost && !Number.isNaN(pageIndex)) persistHeaderChanges(headerHost, pageIndex);
    } else {
      commitCurrentPageToState();
      scheduleAutoSave();
    }
    updateImageOverlay(selectedImageEl);
  }, [selectedImageEl, commitCurrentPageToState, persistHeaderChanges, scheduleAutoSave, updateImageOverlay]);

  const handleDeleteSelectedImage = useCallback(() => {
    if (!selectedImageEl) return;
    const headerHost = selectedImageEl.closest?.('[data-page-header-region="true"]');
    const headerPageIndex = Number.parseInt(headerHost?.getAttribute('data-page-index') || '', 10);
    const next = selectedImageEl.nextSibling;
    const prev = selectedImageEl.previousSibling;
    if (isZWS(next)) next.remove();
    if (isZWS(prev)) prev.remove();
    selectedImageEl.remove();
    setSelectedImageEl(null);
    if (headerHost && !Number.isNaN(headerPageIndex)) {
      persistHeaderChanges(headerHost, headerPageIndex);
    } else {
      commitCurrentPageToState();
      scheduleAutoSave();
    }
  }, [selectedImageEl, commitCurrentPageToState, persistHeaderChanges, scheduleAutoSave]);

  // ─── Drag-and-drop images within editor ────────────────────
  const handleEditorDragStart = useCallback((e) => {
    const imgContainer = e.target.closest('.image-container');
    if (!imgContainer) return;
    draggedImageRef.current = imgContainer;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  }, []);

  const handleEditorDragOver = useCallback((e) => {
    if (!draggedImageRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleEditorDrop = useCallback((e) => {
    const dragged = draggedImageRef.current;
    draggedImageRef.current = null;
    if (!dragged || !editorRef.current) return;
    e.preventDefault();

    // Get drop position from mouse coordinates
    let range;
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(e.clientX, e.clientY);
    } else if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }
    if (!range) return;

    // Remove from old position
    const nextSib = dragged.nextSibling;
    if (isZWS(nextSib)) nextSib.remove();
    dragged.remove();

    // Insert at new position
    range.insertNode(dragged);
    const spacer = createZwsNode();
    dragged.parentNode?.insertBefore(spacer, dragged.nextSibling);

    setSelectedImageEl(dragged);
    commitCurrentPageToState();
    scheduleAutoSave();
  }, [commitCurrentPageToState, scheduleAutoSave]);

  const updateVisiblePageCounter = useCallback(() => {
    if (pageCounterFrameRef.current !== null) cancelAnimationFrame(pageCounterFrameRef.current);
    pageCounterFrameRef.current = requestAnimationFrame(() => {
      pageCounterFrameRef.current = null;
      const scroller = documentScrollRef.current;
      if (!scroller) return;
      const selector = useV2Engine ? '.v2-editable-page-frame' : '.page-editor';
      const pageNodes = Array.from(scroller.querySelectorAll(selector));
      if (!pageNodes.length) return;
      setVisiblePageCount((current) => (current === pageNodes.length ? current : pageNodes.length));

      const viewport = scroller.getBoundingClientRect();
      let bestIndex = 0;
      let bestVisibleHeight = -1;
      pageNodes.forEach((page, index) => {
        const rect = page.getBoundingClientRect();
        const visibleHeight = Math.max(0, Math.min(rect.bottom, viewport.bottom) - Math.max(rect.top, viewport.top));
        if (visibleHeight > bestVisibleHeight) {
          bestVisibleHeight = visibleHeight;
          bestIndex = index;
        }
      });
      setVisiblePageIndex((current) => (current === bestIndex ? current : bestIndex));
    });
  }, [useV2Engine]);

  useEffect(() => {
    updateVisiblePageCounter();
    const scroller = documentScrollRef.current;
    const observer = scroller
      ? new MutationObserver(updateVisiblePageCounter)
      : null;
    observer?.observe(scroller, { childList: true, subtree: true });
    window.addEventListener('resize', updateVisiblePageCounter);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateVisiblePageCounter);
      if (pageCounterFrameRef.current !== null) cancelAnimationFrame(pageCounterFrameRef.current);
    };
  }, [currentDocument?.pages?.length, updateVisiblePageCounter, zoom]);

  // ─── Loading state ────────────────────────────────────────
  if (!currentDocument) {
    if (loadError) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-700 font-medium mb-2">Document unavailable</p>
            <p className="text-sm text-gray-500 mb-4">{loadError}</p>
            <button
              onClick={() => navigate('/document/new')}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-sigma-600 text-white hover:bg-sigma-700 transition-colors"
            >
              Create new document
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading document...</p>
        </div>
      </div>
    );
  }

  const currentFolderLabel = getFolderLabel(currentDocument.folderPath);
  const totalDocumentPages = Math.max(1, visiblePageCount);
  const counterPageNumber = Math.min(visiblePageIndex + 1, totalDocumentPages);
  const currentSubscription = getUserSubscription(userData);
  const isTrialPlan = getCurrentPlan(userData).id === PLAN_IDS.FREE;
  const ownModalDocuments = modalDocuments.filter((item) => item.modalKind !== 'template');
  const templateModalDocuments = modalDocuments.filter((item) => item.modalKind === 'template');
  const folderOptions = Array.from(
    new Set(
      ownModalDocuments
        .map((item) => normalizeFolderPath(item.folderPath || ''))
        .filter((value) => value !== '')
    )
  ).sort((a, b) => a.localeCompare(b));
  const visibleModalDocuments = documentModalMode === 'shared'
    ? modalDocuments
    : modalFolderFilter === 'all'
      ? ownModalDocuments
      : ownModalDocuments.filter((item) => normalizeFolderPath(item.folderPath || '') === modalFolderFilter);

  return (
    <div
      className={clsx('flex h-[100dvh] min-h-[100dvh] flex-col overflow-hidden bg-gray-100', className)}
    >
      <div className="sticky top-0 z-40 shrink-0 bg-gray-100">
        {/* Document Header */}
        <div className="shrink-0 bg-sigma-800 text-white border-b border-sigma-900/40">
        {isTrialPlan && (
          <div className="border-b border-white/10 bg-amber-300/12 px-4 py-1.5 text-xs text-amber-50 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {currentSubscription.status === 'expired'
                  ? 'Trial ended. Upgrade to keep using saved documents, PDFs, sharing, and assessments.'
                  : `${currentSubscription.trialDaysRemaining || 0} trial day${currentSubscription.trialDaysRemaining === 1 ? '' : 's'} left. Trial access includes up to 10 documents with 5 pages each.`}
              </span>
              <Link to="/pricing" className="font-semibold text-amber-200 transition hover:text-white">
                Explore plans
              </Link>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-4 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:py-1 lg:gap-6">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:gap-4 lg:flex-nowrap lg:gap-5">
            <div className="relative" ref={appMenuRef}>
              <button
                type="button"
                onClick={() => setShowAppMenu((prev) => !prev)}
                className={clsx(
                  'w-8 h-8 rounded-lg border flex items-center justify-center transition-colors',
                  showAppMenu
                    ? 'bg-white text-sigma-800 border-transparent'
                    : 'bg-white/10 text-white border-white/25 hover:bg-white/20'
                )}
                title="Open app menu"
                aria-label="Open app menu"
                aria-expanded={showAppMenu}
              >
                <span className="grid grid-cols-2 gap-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                </span>
              </button>

              {showAppMenu && (
                <div className="absolute left-0 top-full z-50 mt-2 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-800 shadow-lg">
                  <div className="flex min-w-[280px] items-center sm:min-w-[440px]">
                    {appMenuTabs.map((tab, index) => (
                      <button
                        key={tab.key}
                        type="button"
                        disabled={tab.disabled}
                        onClick={() => {
                          if (tab.disabled || !tab.onClick) return;
                          setShowAppMenu(false);
                          tab.onClick();
                        }}
                        className={clsx(
                          'flex-1 px-3 py-2 text-sm font-medium transition-colors text-left',
                          index < appMenuTabs.length - 1 && 'border-r border-gray-200',
                          tab.disabled
                            ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                            : 'text-gray-700 hover:bg-sigma-50 hover:text-sigma-800'
                        )}
                        title={tab.badge || tab.label}
                      >
                        <span>{tab.label}</span>
                        {tab.badge && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
                            {tab.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 overflow-hidden"
              title="Account"
              onClick={() => setShowProfileModal(true)}
            >
              {user?.photoURL || userData?.photoURL ? (
                <img
                  src={user?.photoURL || userData?.photoURL}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserCircle2 className="w-6 h-6 text-white" />
              )}
            </button>

            <div className="flex items-center gap-3">
              <img src={logo} alt="Sigma Classroom" className="h-10 w-auto sm:h-14" />
            </div>

            <div className="min-w-0 flex-1 lg:max-w-xl">
              <div className="group relative">
                <input
                  type="text"
                  value={documentName}
                  onChange={(e) => handleDocumentNameChange(e.target.value)}
                  aria-label="Document name"
                  className={clsx(
                    'w-full text-lg font-semibold bg-white/10 border rounded-lg px-4 py-2 text-white placeholder:text-sigma-200 focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors',
                    (hasUnsavedChanges || isSaving)
                      ? 'border-amber-300/80'
                      : 'border-green-300/70'
                  )}
                  placeholder="Untitled Document"
                />
                <div className="pointer-events-none absolute left-4 -bottom-5 text-xs text-sigma-200 opacity-0 transition-opacity group-hover:opacity-100">
                  {isSaving
                    ? 'Saving...'
                    : hasUnsavedChanges
                      ? (lastSaved ? `Unsaved changes since ${lastSaved.toLocaleTimeString()}` : 'Unsaved changes')
                      : (lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : 'Saved')}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full overflow-x-auto pb-1 lg:w-auto lg:overflow-visible lg:pb-0">
            <div className="flex min-w-max items-center gap-2">
            <HeaderButton icon={FilePlus} label="New" onClick={handleNewDocument} />
            <HeaderButton icon={FolderOpen} label="Open" onClick={() => openDocumentsModal('my')} />
            <HeaderButton
              icon={FolderTree}
              label={currentFolderLabel}
              onClick={handleOpenFolderModal}
            />
            <HeaderButton icon={Save} label="Save" onClick={() => handleSave(true)} variant="solid" />
            <div className="w-px h-8 bg-white/20 mx-1" />
            <HeaderButton icon={Users} label="Shared" onClick={() => openDocumentsModal('shared')} />
            <HeaderButton
              icon={Share2}
              label="Share"
              onClick={() => {
                if (!canShareContent(userData)) {
                  setSaveError('Sharing requires the Basic or Pro plan.');
                  return;
                }
                setShowShareModal(true);
              }}
            />
            <HeaderButton icon={Compass} label="Tools" onClick={openToolsFromDocument} />
            <div className="flex items-center rounded-lg border border-white/20 bg-white/10 p-1">
              <button
                type="button"
                onClick={() => handleSwitchContentType('editor')}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  !isPdfMode ? 'bg-white text-sigma-800' : 'text-white hover:bg-white/10'
                )}
              >
                Editor
              </button>
              <button
                type="button"
                onClick={() => handleSwitchContentType('pdf')}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  isPdfMode ? 'bg-white text-sigma-800' : 'text-white hover:bg-white/10'
                )}
              >
                PDF
              </button>
            </div>
            
            {canAccessAdmin && !isMobileViewport && (
              <HeaderButton
                icon={Type}
                label="Creator"
                onClick={() => setShowContentCreator((prev) => !prev)}
                variant={showContentCreator ? 'accent' : 'ghost'}
              />
            )}
            
            <div className="w-px h-8 bg-white/20 mx-1" />
            {!isPdfMode && !useV2Engine && <HeaderButton icon={Plus} label="Page" onClick={handleAddPage} />}
            <HeaderButton icon={Download} label={isExporting ? 'Exporting…' : 'Export'} onClick={() => setShowExportModal(true)} disabled={isExporting} />
            <HeaderButton
              icon={ClipboardCheck}
              label="Assessment"
              onClick={() => {
                if (!canUseAssessmentModule(userData)) {
                  setSaveError('The assessment module requires the Pro plan.');
                  return;
                }
                setShowAssessmentSettings(true);
              }}
              variant={currentDocument?.assessment?.enabled ? 'accent' : 'ghost'}
            />
            {currentDocument?.assessment?.enabled && !isPdfMode && (
              <HeaderButton
                icon={FlaskConical}
                label="Test"
                onClick={() => {
                  if (useV2Engine) v2EngineRef.current?.commit?.();
                  else if (editorRef.current) commitCurrentPageToState();
                  setShowAssessmentPreview(true);
                }}
              />
            )}
            </div>
          </div>
        </div>
        </div>

        {shouldShowDesktopBanner && (
          <div className="border-b border-sky-200 bg-sky-50 px-3 py-3 sm:px-6">
          <div className="relative mx-auto flex max-w-5xl items-center gap-4 overflow-hidden rounded-2xl border border-sky-200 bg-gradient-to-r from-sky-700 via-cyan-600 to-teal-500 p-4 pr-12 text-white shadow-sm">
            <div className="relative h-20 w-24 shrink-0 overflow-hidden rounded-xl border border-white/25 bg-slate-950/20">
              <div className="absolute inset-2 rounded-lg border border-white/20 bg-slate-950/80" />
              <div className="absolute left-4 right-4 top-4 h-1.5 rounded-full bg-white/15" />
              <div className="absolute left-4 right-10 top-8 h-1.5 rounded-full bg-white/10" />
              <img src={logo} alt="" className="absolute bottom-2 right-2 h-8 w-auto opacity-90" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.24em] text-sky-100">
                <Monitor className="h-4 w-4" />
                Desktop Recommended
              </div>
              <p className="mt-1 text-sm leading-5 text-white/95">
                You can view and make quick edits here, but the document editor works best on a desktop or laptop screen.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsDesktopBannerDismissed(true)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
              aria-label="Dismiss desktop recommendation"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          </div>
        )}
      {/*
      {showToolsOnboarding && !isPdfMode && (
        <div className="border-b border-sigma-200 bg-sigma-50 px-3 py-3 sm:px-6">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 rounded-xl border border-sigma-200 bg-white px-4 py-4 shadow-sm lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sigma-700 text-white">
                <Compass className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">Build classroom assets without leaving the document flow</p>
                <p className="mt-0.5 text-sm leading-5 text-gray-600">
                  Open Geosketch or GraphSketch from here, create the diagram or graph, then use the return action in Tools to come back to this document.
                </p>
                <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-3">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="font-semibold text-gray-900">1. Open Tools</span>
                    <span className="mt-0.5 block">Your document is remembered.</span>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="font-semibold text-gray-900">2. Create Work</span>
                    <span className="mt-0.5 block">Sketch, graph, label, or save.</span>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="font-semibold text-gray-900">3. Return Here</span>
                    <span className="mt-0.5 block">Insert the exported asset.</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={openToolsFromDocument}
                className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-sigma-800"
              >
                Open Tools
                <Compass className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={dismissToolsOnboarding}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
                aria-label="Dismiss tools onboarding"
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      */}
      
        {(!!user && (!userData || !isProfileComplete)) && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              {!userData
                ? 'Complete your Sigma Classroom profile to enable saving.'
                : 'Finish your profile (name and institution) to enable saving.'}
              {saveError && (
                <span className="ml-2 text-red-600">{saveError}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowProfileModal(true)}
                className="px-3 py-2 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600"
              >
                Complete Profile
              </button>
            </div>
          </div>
          </div>
        )}

        {exportError && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <span className="text-sm text-red-700">{exportError}</span>
            <button onClick={() => setExportError('')} className="text-red-400 hover:text-red-600">
              <X size={16} />
            </button>
          </div>
          </div>
        )}

        {narrationError && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <span className="text-sm text-red-700">{narrationError}</span>
            <button onClick={() => setNarrationError('')} className="text-red-400 hover:text-red-600">
              <X size={16} />
            </button>
          </div>
          </div>
        )}

        {/* Toolbar */}
        {!isPdfMode && (
          <DocumentToolbar
            className="shrink-0"
            editorRef={useV2Engine ? v2EditorDomRef : editorRef}
            onFormat={useV2Engine ? ((command, value) => v2EngineRef.current?.format?.(command, value)) : execFormatCommand}
            onInsertMath={useV2Engine ? (() => v2EngineRef.current?.openMath?.('inline')) : openMathModal}
            onInsertImage={openImageModal}
            onInsertHeader={useV2Engine
              ? (() => v2EngineRef.current?.insertHeader?.(headerFirstPageOnly))
              : handleInsertHeader}
            onInsertFooter={useV2Engine
              ? (() => v2EngineRef.current?.insertFooter?.())
              : handleInsertFooter}
            onInsertPageNumber={useV2Engine
              ? ((location, startNumber) => v2EngineRef.current?.insertPageNumber?.(location, startNumber))
              : handleInsertPageNumber}
            onInsertSectionDivider={useV2Engine ? (() => v2EngineRef.current?.insertSectionDivider?.()) : handleInsertSectionDivider}
            onTableAction={useV2Engine
              ? ((action, options) => v2EngineRef.current?.tableAction?.(action, options))
              : undefined}
            headerFirstPageOnly={headerFirstPageOnly}
            onHeaderFirstPageOnlyChange={handleHeaderFirstPageOnlyChange}
            onPreserveSelection={useV2Engine
              ? (() => v2EngineRef.current?.captureDomSelection?.())
              : preserveSelection}
            zoom={zoom}
            onZoomOut={() => setZoom((current) => Math.max(50, current - 10))}
            onZoomIn={() => setZoom((current) => Math.min(200, current + 10))}
            onResetZoom={() => setZoom(100)}
          />
        )}
      </div>

      {/* {!isPdfMode && !showPreview && (
        <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center gap-2 overflow-x-auto">
          {getPages().map((_, index) => (
            <button
              key={`page-tab-${index}`}
              onClick={() => handleSelectPage(index)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-sm border transition-colors whitespace-nowrap',
                index === activePageIndex
                  ? 'bg-sigma-700 text-white border-sigma-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              )}
            >
              Page {index + 1}
            </button>
          ))}
        </div>
      )} */}

      {/* Editor / Preview + Content Creator Sidebar */}
      <div className="flex-1 min-h-0 bg-gray-100 overflow-hidden">
        <div className="flex h-full flex-col lg:flex-row">
          <div className="relative min-h-0 flex-1">
          <div
            ref={documentScrollRef}
            className="h-full overflow-y-auto overflow-x-auto"
            onScroll={updateVisiblePageCounter}
          >
            {!isPdfMode && !isMobileViewport && (
              <button
                type="button"
                onClick={() => {
                  if (useV2Engine) v2EngineRef.current?.commit?.();
                  else if (editorRef.current) commitCurrentPageToState();
                  setShowTeachingMode((current) => !current);
                }}
                className={clsx(
                  'fixed z-20 inline-flex items-center justify-center gap-2 border bg-white/30 px-4 py-2 text-sm font-semibold shadow-lg shadow-sigma-900/10 backdrop-blur transition-colors',
                  'left-6',
                  showTeachingMode
                    ? 'border-sky-600 bg-sky-50/95 text-sky-800'
                    : 'border-sigma-200 text-sigma-800 hover:bg-sigma-50'
                )}
                style={{ top: 'calc(64px + 56px)' }}
              >
                <Eye className="h-4 w-4" />
                {showTeachingMode ? 'Exit Teaching Mode' : 'Teaching Mode'}
              </button>
            )}
            {isPdfMode ? (
              <PdfDocumentPanel
                pdf={currentDocument?.pdf || null}
                isUploading={isUploadingPdf}
                error={pdfActionError}
                canEdit={!!user}
                onUpload={handlePdfUpload}
                onDelete={handlePdfDelete}
                audioPages={narrationPages}
                canManageAudio={isDocumentOwner}
                onAddPageAudio={openPdfNarrationModal}
                onManagePageAudio={openPdfNarrationModal}
                onMovePageAudio={updatePdfNarrationPosition}
                onRemovePageAudio={handlePdfNarrationDelete}
              />
            ) : useV2Engine ? (
              <div className="relative min-h-full">
                <div className="px-3 py-6 sm:px-6 sm:py-10">
                  <div
                    className="mx-auto min-w-max origin-top-left"
                    style={{
                      width: `${scaledWidth}mm`,
                      transform: `scale(${zoomScale})`,
                    }}
                  >
                    <DocumentEditorV2
                      embedded
                      documentId={documentId}
                      debugInitialDocument={debugInitialDocument}
                      engineRef={v2EngineRef}
                      onDirty={handleV2Dirty}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative min-h-full">
                <div className="px-3 py-6 sm:px-6 sm:py-10">
                  <div
                    className="mx-auto min-w-max"
                    style={{
                      width: `${scaledWidth}mm`,
                      minHeight: `${scaledHeight}mm`,
                    }}
                  >
                    <div className="origin-top-left" style={{ transform: `scale(${zoomScale})` }}>
                      {showPreview ? (
                        <div className="space-y-6">
                          {getPages().map((_, index) => (
                            <div key={`scroll-page-${index}`} className="group relative">
                              {getPages().length > 1 && (
                                <button
                                  onClick={() => handleDeletePage(index)}
                                  className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-white/80 text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 transition-all shadow-sm border border-gray-200"
                                  title={`Delete page ${index + 1}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              <div
                                ref={(el) => {
                                  pageRefsMap.current[index] = el;
                                  if (el) {
                                    pageBodyRefsMap.current[index] = el.querySelector('[data-page-body="true"]');
                                    if (index === activePageIndex) {
                                      editorRef.current = pageBodyRefsMap.current[index] || null;
                                    }
                                  }
                                }}
                                className={clsx(
                                  'page-editor bg-white shadow-lg rounded border',
                                  index === activePageIndex ? 'border-sigma-400 ring-2 ring-sigma-100' : 'border-gray-200'
                                )}
                                style={{
                                  width: `${pageWidthMm}mm`,
                                  height: `${pageHeightMm}mm`,
                                  '--page-padding': `${pagePaddingMm}mm`,
                                  '--page-padding-top': `${pagePaddingTopMm}mm`,
                                  '--page-padding-bottom': `${pagePaddingBottomMm}mm`,
                                  position: 'relative',
                                  boxSizing: 'border-box',
                                  outline: 'none',
                                  fontSize: '14px',
                                  lineHeight: '1.6',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                <div
                                  data-page-header-region="true"
                                  data-page-index={index}
                                  className="page-region-header header-editable"
                                  contentEditable
                                  suppressContentEditableWarning
                                  onFocus={(event) => handleHeaderFocus(event, index)}
                                  onBeforeInput={markUndoBoundary}
                                  onInput={handleHeaderInput}
                                  onPaste={handleHeaderPaste}
                                  onClick={handleHeaderClick}
                                  spellCheck
                                />
                                <div
                                  data-page-body="true"
                                  data-page-index={index}
                                  contentEditable
                                  suppressContentEditableWarning
                                  className="page-body-editor"
                                  onFocus={() => handleScrollPageFocus(index)}
                                  onBlur={(event) => handleScrollPageBlur(event, index)}
                                  onMouseDown={handleEditorMouseDown}
                                  onKeyDown={handleEditorKeyDown}
                                  onBeforeInput={handleEditorBeforeInput}
                                  onInput={handleEditorInput}
                                  onPaste={handleEditorPaste}
                                  onClick={handleEditorClick}
                                  onDragStart={handleEditorDragStart}
                                  onDragOver={handleEditorDragOver}
                                  onDrop={handleEditorDrop}
                                  spellCheck
                                />
                                <div data-page-footer-region="true" className="page-region-footer" contentEditable={false} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="group relative">
                          {getPages().length > 1 && (
                            <button
                              onClick={() => handleDeletePage(activePageIndex)}
                              className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-white/80 text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 transition-all shadow-sm border border-gray-200"
                              title={`Delete page ${activePageIndex + 1}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <div
                            ref={(el) => {
                              pageRefsMap.current[activePageIndex] = el;
                              if (el) {
                                pageBodyRefsMap.current[activePageIndex] = el.querySelector('[data-page-body="true"]');
                                editorRef.current = pageBodyRefsMap.current[activePageIndex] || null;
                              }
                            }}
                            className="page-editor bg-white shadow-lg rounded border border-gray-200"
                            style={{
                              width: `${pageWidthMm}mm`,
                              height: `${pageHeightMm}mm`,
                              '--page-padding': `${pagePaddingMm}mm`,
                              '--page-padding-top': `${pagePaddingTopMm}mm`,
                              '--page-padding-bottom': `${pagePaddingBottomMm}mm`,
                              position: 'relative',
                              boxSizing: 'border-box',
                              outline: 'none',
                              fontSize: '14px',
                              lineHeight: '1.6',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            <div
                              data-page-header-region="true"
                              data-page-index={activePageIndex}
                              className="page-region-header header-editable"
                              contentEditable
                              suppressContentEditableWarning
                              onFocus={(event) => handleHeaderFocus(event, activePageIndex)}
                              onBeforeInput={markUndoBoundary}
                              onInput={handleHeaderInput}
                              onPaste={handleHeaderPaste}
                              onClick={handleHeaderClick}
                              spellCheck
                            />
                            <div
                              data-page-body="true"
                              data-page-index={activePageIndex}
                              contentEditable
                              suppressContentEditableWarning
                              className="page-body-editor"
                              onKeyDown={handleEditorKeyDown}
                              onMouseDown={handleEditorMouseDown}
                              onBeforeInput={handleEditorBeforeInput}
                              onInput={handleEditorInput}
                              onPaste={handleEditorPaste}
                              onClick={handleEditorClick}
                              onDragStart={handleEditorDragStart}
                              onDragOver={handleEditorDragOver}
                              onDrop={handleEditorDrop}
                              spellCheck
                            />
                            <div data-page-footer-region="true" className="page-region-footer" contentEditable={false} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {!isPdfMode && (
            <div
              className="pointer-events-none absolute bottom-5 left-5 z-30 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur"
              aria-live="polite"
              aria-label={`Page ${counterPageNumber} of ${totalDocumentPages}`}
            >
              Page {counterPageNumber} of {totalDocumentPages}
            </div>
          )}
          </div>

          {shouldShowContentCreator && (
            <aside className="hidden w-[380px] border-l border-gray-200 bg-white lg:flex lg:flex-col">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">AI Content Creator</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Generate markdown + LaTeX content and insert it into this document.
                </p>
              </div>

              <div className="p-4 space-y-4 overflow-y-auto">
                {needsProfileCompletion && (
                  <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div>
                      <h4 className="text-sm font-semibold text-amber-900">Complete Your Profile</h4>
                      <p className="mt-1 text-xs text-amber-800">
                        AI Content Creator is locked until your profile is complete.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowProfileModal(true)}
                      className="w-full px-3 py-2 rounded-md border border-amber-300 bg-white text-sm font-medium text-amber-900 hover:bg-amber-100"
                    >
                      Complete Profile
                    </button>
                  </div>
                )}

                <fieldset disabled={needsProfileCompletion} className="space-y-4 disabled:opacity-60">
                <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Prompt Source</h4>
                  <p className="text-xs text-gray-600">
                    Master prompt is managed in Admin Area.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Open Admin Area
                  </button>
                </div>

                <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Source Files</h4>
                  <p className="text-xs text-gray-600">
                    Source files are managed in Admin Area and applied automatically.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Manage Source Files
                  </button>
                </div>

                <select
                  value={creatorProjectId}
                  onChange={(e) => setCreatorProjectId(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                >
                  {creatorProjects.length === 0 && (
                    <option value="">No projects configured</option>
                  )}
                  {creatorProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  value={creatorSubject}
                  onChange={(e) => setCreatorSubject(e.target.value)}
                  placeholder="Subject (e.g. Mathematics)"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                />
                <input
                  type="text"
                  value={creatorGrade}
                  onChange={(e) => setCreatorGrade(e.target.value)}
                  placeholder="Grade (e.g. Grade 10)"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                />
                <input
                  type="text"
                  value={creatorTopic}
                  onChange={(e) => setCreatorTopic(e.target.value)}
                  placeholder="Topic"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                />
                <input
                  type="text"
                  value={creatorSubtopic}
                  onChange={(e) => setCreatorSubtopic(e.target.value)}
                  placeholder="Subtopic (optional)"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                />
                <select
                  value={creatorContentType}
                  onChange={(e) => setCreatorContentType(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                >
                  {creatorContentTypeOptions.map((item) => (
                    <option key={item} value={item}>
                      {item.charAt(0).toUpperCase() + item.slice(1)}
                    </option>
                  ))}
                </select>
                <textarea
                  value={creatorInstructions}
                  onChange={(e) => setCreatorInstructions(e.target.value)}
                  placeholder="Extra instructions (optional)"
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                />

                <button
                  onClick={handleGenerateCreatorContent}
                  disabled={needsProfileCompletion || isGeneratingCreatorContent}
                  className="w-full px-3 py-2 rounded-md bg-sigma-700 text-white text-sm font-medium hover:bg-sigma-800 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isGeneratingCreatorContent ? 'Generating...' : 'Generate Content'}
                </button>

                {creatorError && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {creatorError}
                  </div>
                )}

                {(creatorContent || creatorDraft) && (
                  <>
                    <div className="pt-2 border-t border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Preview</h4>
                      <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3">
                        <MathRenderer content={creatorDraft || creatorContent} />
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Edit Content</h4>
                      <textarea
                        value={creatorDraft}
                        onChange={(e) => setCreatorDraft(e.target.value)}
                        rows={10}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300"
                      />
                    </div>

                    <button
                      onClick={handleAddCreatorContentToDocument}
                      className="w-full px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
                    >
                      Add To Document
                    </button>
                  </>
                )}
                </fieldset>
              </div>
            </aside>
          )}
        </div>
      </div>

      {showResponseTypeMenu && (
        <div
          id="response-type-menu"
          className="fixed z-50 w-56 rounded-lg border border-gray-200 bg-white shadow-xl"
          style={{ left: responseTypeMenuPos.x, top: responseTypeMenuPos.y }}
        >
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-200">
            Insert Block
          </div>
          <div className="py-1">
            {insertMenuOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSelectResponseType(opt.value)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-violet-50 hover:text-violet-800 transition-colors"
              >
                <opt.icon className="w-4 h-4 text-violet-500" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <InlineMathModal
        isOpen={showMathModal}
        initialLatex={initialMathLatex}
        anchorRect={mathAnchorRect}
        onInsert={handleInsertMath}
        onClose={() => setShowMathModal(false)}
      />

      <ImageUploadModal
        isOpen={showImageModal}
        userId={user?.uid}
        anchorRect={null}
        onInsert={useV2Engine
          ? (({ url, align }) => {
              setShowImageModal(false);
              v2EngineRef.current?.insertImage?.(url, align);
            })
          : handleInsertImage}
        onClose={() => setShowImageModal(false)}
      />

      {showEmbedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Embed Content</h3>
                <p className="text-sm text-gray-500">Add HTML, an embeddable URL, or a YouTube video into this step.</p>
              </div>
              <button
                type="button"
                onClick={closeEmbedModal}
                className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'html', label: 'HTML' },
                  { value: 'url', label: 'URL' },
                  { value: 'youtube', label: 'YouTube' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setEmbedModalMode(option.value);
                      setEmbedModalError('');
                    }}
                    className={clsx(
                      'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                      embedModalMode === option.value
                        ? 'border-sigma-700 bg-sigma-700 text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {embedModalMode === 'html' ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">HTML Snippet</label>
                  <textarea
                    value={embedModalHtml}
                    onChange={(e) => setEmbedModalHtml(e.target.value)}
                    rows={10}
                    placeholder="<iframe src='...'></iframe>"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                  />
                  <p className="text-xs text-gray-500">Scripts are stripped before insertion.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {embedModalMode === 'youtube' ? 'YouTube URL or Video ID' : 'Embed URL'}
                  </label>
                  <input
                    type="text"
                    value={embedModalUrl}
                    onChange={(e) => setEmbedModalUrl(e.target.value)}
                    placeholder={embedModalMode === 'youtube'
                      ? 'https://www.youtube.com/watch?v=...'
                      : 'https://example.com/embed'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                  />
                  <p className="text-xs text-gray-500">
                    {embedModalMode === 'youtube'
                      ? 'Standard YouTube links, shorts links, embed links, and raw 11-character IDs are supported.'
                      : 'Use a URL that allows embedding in an iframe.'}
                  </p>
                </div>
              )}

              {embedModalError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {embedModalError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={closeEmbedModal}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertStepEmbed}
                className="rounded-lg bg-sigma-700 px-4 py-2 text-sm font-medium text-white hover:bg-sigma-800"
              >
                Insert Embed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image resize overlay */}
      {selectedImageEl && imageOverlayRect && (
        <div
          className="fixed z-40 pointer-events-none"
          style={{
            top: imageOverlayRect.top,
            left: imageOverlayRect.left,
            width: imageOverlayRect.width,
            height: imageOverlayRect.height,
          }}
        >
          {/* Selection border */}
          <div className="absolute inset-0 border-2 border-sigma-500 rounded" />

          {/* Corner resize handles */}
          {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner) => {
            const isTop = corner.includes('top');
            const isLeft = corner.includes('left');
            return (
              <div
                key={corner}
                className="absolute w-3 h-3 bg-white border-2 border-sigma-600 rounded-sm pointer-events-auto"
                style={{
                  top: isTop ? -6 : undefined,
                  bottom: isTop ? undefined : -6,
                  left: isLeft ? -6 : undefined,
                  right: isLeft ? undefined : -6,
                  cursor: corner === 'top-left' || corner === 'bottom-right' ? 'nwse-resize' : 'nesw-resize',
                }}
                onMouseDown={(e) => handleResizeStart(e, corner)}
              />
            );
          })}

          {/* Floating toolbar below the image */}
          <div
            className="absolute left-1/2 -translate-x-1/2 pointer-events-auto flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg px-2 py-1"
            style={{ top: imageOverlayRect.height + 8 }}
          >
            {[
              { value: 'left', icon: AlignLeft, label: 'Float Left' },
              { value: 'center', icon: AlignCenter, label: 'Center' },
              { value: 'right', icon: AlignRight, label: 'Float Right' },
            ].map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => handleImageAlignChange(value)}
                title={label}
                className={clsx(
                  'w-7 h-7 flex items-center justify-center rounded transition-colors',
                  selectedImageEl?.getAttribute('data-image-align') === value
                    ? 'bg-sigma-700 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            ))}
            <div className="w-px h-5 bg-gray-200 mx-0.5" />
            <button
              onClick={handleDeleteSelectedImage}
              title="Delete image"
              className="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:bg-red-50 hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {showExportModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Export as PDF</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 mb-4">Choose page orientation:</p>
              <button
                onClick={() => handleExport('portrait')}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-200 hover:bg-sigma-50 hover:border-sigma-300 transition-colors text-left"
              >
                <div className="w-8 h-10 rounded border-2 border-sigma-400 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-900">Portrait</div>
                  <div className="text-xs text-gray-500">Standard vertical A4 layout</div>
                </div>
              </button>
              <button
                onClick={() => handleExport('landscape')}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-200 hover:bg-sigma-50 hover:border-sigma-300 transition-colors text-left"
              >
                <div className="w-10 h-8 rounded border-2 border-sigma-400 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-900">Landscape</div>
                  <div className="text-xs text-gray-500">Wide horizontal A4, two-column layout</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocumentModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {documentModalMode === 'shared' ? 'Shared Documents' : 'Open Document'}
              </h3>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {!modalLoading && !modalError && documentModalMode !== 'shared' && (
                <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-4">
                  <button
                    type="button"
                    onClick={() => setModalFolderFilter('all')}
                    className={clsx(
                      'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                      modalFolderFilter === 'all'
                        ? 'bg-sigma-700 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalFolderFilter('')}
                    className={clsx(
                      'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                      modalFolderFilter === ''
                        ? 'bg-sigma-700 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    Root
                  </button>
                  {folderOptions.map((folder) => (
                    <button
                      key={`folder-filter-${folder}`}
                      type="button"
                      onClick={() => setModalFolderFilter(folder)}
                      className={clsx(
                        'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                        modalFolderFilter === folder
                          ? 'bg-sigma-700 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {folder}
                    </button>
                  ))}
                </div>
              )}
              {modalLoading && (
                <div className="text-sm text-gray-600">Loading documents...</div>
              )}

              {!modalLoading && modalError && (
                <div className="text-sm text-red-600">{modalError}</div>
              )}

              {!modalLoading && !modalError && visibleModalDocuments.length === 0 && (
                <div className="text-sm text-gray-600">
                  {documentModalMode === 'shared'
                    ? 'No shared documents found.'
                    : modalFolderFilter === 'all'
                      ? 'No documents found.'
                      : `No documents found in ${getFolderLabel(modalFolderFilter)}.`}
                </div>
              )}

              {!modalLoading && !modalError && documentModalMode !== 'shared' && templateModalDocuments.length > 0 && (
                <div className="mb-5 space-y-2 border-b border-gray-200 pb-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Templates
                  </div>
                  {templateModalDocuments.map((item) => (
                    <div
                      key={`template-${item.id}`}
                      className="flex items-center gap-2"
                    >
                      <button
                        onClick={() => openDocumentFromModal(item)}
                        className="flex-1 text-left rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2 font-medium text-gray-900">
                          <span>{item.name || item.documentName || 'Untitled Document'}</span>
                          <span className="rounded-full bg-sigma-100 px-2 py-0.5 text-[11px] font-semibold text-sigma-800">
                            Template
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {`Template ID: ${item.id}`}
                          {(item.contentType === 'pdf' || item.documentContentType === 'pdf')
                            ? ' • PDF'
                            : (item.contentType || item.documentContentType) ? ' • Editor' : ''}
                        </div>
                      </button>
                      <button
                        onClick={() => handleCloneTemplate(item)}
                        disabled={templateActionId === item.id}
                        className="rounded-lg bg-sigma-700 px-3 py-2 text-sm font-medium text-white hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                        title="Clone template"
                      >
                        {templateActionId === item.id ? 'Cloning...' : 'Clone'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!modalLoading && !modalError && visibleModalDocuments.length > 0 && (
                <div className="space-y-2">
                  {visibleModalDocuments.map((item) => (
                    <div
                      key={`${documentModalMode}-${item.id}`}
                      className="flex items-center gap-2"
                    >
                      <button
                        onClick={() => openDocumentFromModal(item)}
                        className="flex-1 text-left rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2 font-medium text-gray-900">
                          <span>{item.name || item.documentName || 'Untitled Document'}</span>
                          {item.isTemplate ? (
                            <span className="rounded-full bg-sigma-100 px-2 py-0.5 text-[11px] font-semibold text-sigma-800">
                              Template
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-gray-500">
                          {documentModalMode === 'shared'
                            ? `Document ID: ${item.documentId || 'N/A'}`
                            : `ID: ${item.id}`}
                          {(item.contentType === 'pdf' || item.documentContentType === 'pdf')
                            ? ' • PDF'
                            : (item.contentType || item.documentContentType) ? ' • Editor' : ''}
                        </div>
                        {documentModalMode !== 'shared' && (
                          <div className="mt-1 text-xs text-gray-400">
                            Folder: {getFolderLabel(item.folderPath)}
                          </div>
                        )}
                      </button>
                      {documentModalMode !== 'shared' ? (
                        <>
                          {item.contentType !== 'pdf' ? (
                            <button
                              onClick={() => handleTemplateStatusChange(item, !item.isTemplate)}
                              disabled={templateActionId === item.id}
                              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                              title={item.isTemplate ? 'Remove from templates' : 'Add to templates'}
                            >
                              {templateActionId === item.id
                                ? 'Saving...'
                                : item.isTemplate
                                  ? 'Remove template'
                                  : 'Make template'}
                            </button>
                          ) : null}
                          {item.isTemplate ? (
                            <button
                              onClick={() => handleCloneTemplate(item)}
                              disabled={templateActionId === item.id}
                              className="rounded-lg bg-sigma-700 px-3 py-2 text-sm font-medium text-white hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                              title="Clone template"
                            >
                              {templateActionId === item.id ? 'Cloning...' : 'Clone'}
                            </button>
                          ) : null}
                          <button
                            onClick={() => handleDeleteDocument(item)}
                            className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                            title="Delete document"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showFolderModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Document Folder</h3>
                <p className="text-sm text-gray-500">Use `/` to create nested folders.</p>
              </div>
              <button
                onClick={() => setShowFolderModal(false)}
                className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Path
                </label>
                <input
                  type="text"
                  value={folderDraft}
                  onChange={(e) => setFolderDraft(e.target.value)}
                  placeholder="e.g. Exams/Grade 10"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                />
              </div>
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
                Current folder: {currentFolderLabel}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setFolderDraft('')}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Clear Folder
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowFolderModal(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveFolder}
                  className="rounded-lg bg-sigma-700 px-4 py-2 text-sm font-medium text-white hover:bg-sigma-800"
                >
                  Save Folder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      <AssessmentDocSettings
        isOpen={showAssessmentSettings}
        onSaved={() => {
          scheduleAutoSave();
        }}
        onClose={() => setShowAssessmentSettings(false)}
      />

      <ResponseAreaConfigModal
        isOpen={showResponseAreaConfig}
        blockId={selectedResponseAreaId}
        config={selectedResponseAreaId ? currentDocument?.assessment?.responseAreas?.[selectedResponseAreaId] : null}
        onDuplicate={() => {
          if (!selectedResponseAreaId || !editorRef.current) return;
          const responseBlock = editorRef.current.querySelector(`[data-response-id="${selectedResponseAreaId}"]`);
          if (responseBlock) {
            handleDuplicateResponseArea(responseBlock);
          }
          setShowResponseAreaConfig(false);
          setSelectedResponseAreaId(null);
        }}
        onSaved={() => {
          commitCurrentPageToState();
          scheduleAutoSave();
          clearTimeout(overflowTimerRef.current);
          overflowTimerRef.current = setTimeout(() => overflowRef.current?.(), 300);
        }}
        onClose={() => {
          setShowResponseAreaConfig(false);
          setSelectedResponseAreaId(null);
        }}
      />

      <ShareDocumentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        document={currentDocument}
      />

      <AssessmentDocPreviewModal
        isOpen={showAssessmentPreview}
        onClose={() => setShowAssessmentPreview(false)}
        document={currentDocument}
      />

      {showTeachingMode && !isPdfMode && (
        <div ref={fullScreenRootRef} className="fixed inset-0 z-50 bg-slate-950">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-white/10 bg-sigma-900 px-6 py-4 text-white backdrop-blur">
              <div>
                <h2 className="text-lg font-semibold">{currentDocument?.name || 'Untitled Document'}</h2>
                <p className="text-sm text-slate-300">Teaching mode: click step chips to reveal or hide worked steps.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center rounded-lg border border-white/15 bg-white/5 p-1">
                  <button
                    type="button"
                    onClick={() => setTeachingZoom((current) => Math.max(50, current - 10))}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-lg font-semibold text-white transition-colors hover:bg-white/10"
                    title="Zoom out"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    onClick={() => setTeachingZoom(100)}
                    className="min-w-[68px] rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
                    title="Reset zoom"
                  >
                    {teachingZoom}%
                  </button>
                  <button
                    type="button"
                    onClick={() => setTeachingZoom((current) => Math.min(200, current + 10))}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-lg font-semibold text-white transition-colors hover:bg-white/10"
                    title="Zoom in"
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTeachingMode(false)}
                  className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10"
                >
                  <X className="h-4 w-4" />
                  Exit Teaching Mode
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-slate-900 px-6 py-8">
              <div
                className="mx-auto origin-top"
                style={{
                  width: `${210 * (teachingZoom / 100)}mm`,
                }}
              >
                <div style={{ transform: `scale(${teachingZoom / 100})`, transformOrigin: 'top center' }}>
                  <DocumentPreview
                    document={{ ...currentDocument, pages: getPages() }}
                    pageWidthMm={pageWidthMm}
                    pageHeightMm={pageHeightMm}
                    pagePaddingMm={pagePaddingMm}
                    pagePaddingTopMm={pagePaddingTopMm}
                    pagePaddingBottomMm={pagePaddingBottomMm}
                    useMathJax={useMathJax}
                    mode="teaching"
                    openSteps={openTeachingSteps}
                    onToggleStep={(stepId) => {
                      setOpenTeachingSteps((current) => ({
                        ...current,
                        [stepId]: !current[stepId],
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DocumentNarrationModal
        isOpen={showNarrationModal}
        isSaving={isUploadingNarration}
        title={
          narrationTarget?.type === 'pdf-page'
            ? `Add Page ${narrationTarget.pageNumber} Audio`
            : 'Add Step Audio'
        }
        description={
          narrationTarget?.type === 'pdf-page'
            ? 'Record or upload audio for this PDF page. You can drag the icon to reposition it afterwards.'
            : 'Record or upload audio for this step block.'
        }
        saveLabel={narrationTarget?.type === 'pdf-page' ? 'Save Page Audio' : 'Save Step Audio'}
        onClose={() => {
          if (isUploadingNarration) return;
          setShowNarrationModal(false);
          setNarrationTarget(null);
        }}
        onSave={handleNarrationSave}
      />
    </div>
  );
};

const DocumentEditor = ({ documentId, className, debugInitialDocument = null }) => {
  return (
    <LegacyDocumentEditor
      documentId={documentId}
      className={className}
      debugInitialDocument={debugInitialDocument}
      useV2Engine={getEditorVersion() === 'v2'}
    />
  );
};

// Preview — renders the stored HTML
  const DocumentPreview = ({
    document: doc,
    pageWidthMm,
    pageHeightMm,
    pagePaddingMm,
    pagePaddingTopMm,
    pagePaddingBottomMm,
    useMathJax,
    mode = 'preview',
    openSteps = {},
    onToggleStep,
  }) => {
  const previewContainerRef = useRef(null);

  useEffect(() => {
    if (!previewContainerRef.current) return;
    const pageContentNodes = previewContainerRef.current.querySelectorAll('[data-preview-page-content]');
    const renderMath = async () => {
      pageContentNodes.forEach((node) => {
        updatePageShellRegionSpacing(node);
        node.querySelectorAll('[contenteditable="true"]').forEach((editable) => {
          editable.setAttribute('contenteditable', 'false');
        });

        if (mode === 'teaching') {
          const stepBlocks = Array.from(node.querySelectorAll('.document-step-block'));
          stepBlocks.forEach((block, index) => {
            const stepId = block.getAttribute('data-step-id') || `teaching-step-${index}`;
            const shell = block.querySelector('.document-step-shell');
            const header = block.querySelector('.document-step-header');
            const toolbar = block.querySelector('.document-step-toolbar');
            const body = block.querySelector('.document-step-body');
            if (!shell || !header || !body) return;

            const stepNumber = header.querySelector('.document-step-number')?.textContent?.trim();
            const stepTitle = header.querySelector('.document-step-title')?.textContent?.trim();
            const buttonLabel = [stepNumber ? `Step ${stepNumber}` : 'Step', stepTitle].filter(Boolean).join(': ');
            let toggle = shell.querySelector('.teaching-step-chip');
            if (!toggle) {
              toggle = document.createElement('button');
              toggle.type = 'button';
              toggle.className = 'teaching-step-chip';
              shell.insertBefore(toggle, shell.firstChild);
            }

            toggle.textContent = buttonLabel;
            toggle.onclick = () => onToggleStep?.(stepId);
            toggle.setAttribute('aria-expanded', openSteps[stepId] ? 'true' : 'false');
            header.style.display = 'none';
            if (toolbar) toolbar.style.display = 'none';
            body.style.display = openSteps[stepId] ? '' : 'none';
            block.classList.toggle('teaching-step-open', !!openSteps[stepId]);
            block.classList.toggle('teaching-step-closed', !openSteps[stepId]);
          });
        }

        replaceDollarMathInContainer(node);
        const containers = node.querySelectorAll('.math-container[data-math-content]');
        containers.forEach((el) => {
          const latex = el.getAttribute('data-math-content');
          el.innerHTML = `\\(${latex}\\)`;
        });
      });

      if (useMathJax) {
        await MathUtils.renderMath(previewContainerRef.current);
        return;
      }

      try {
        const { renderMathInElement } = await import('mathlive');
        const mathNodes = previewContainerRef.current?.querySelectorAll('.math-container[data-math-content]');
        mathNodes?.forEach((el) => {
          const latex = el.getAttribute('data-math-content');
          if (latex) el.innerHTML = `\\(${latex}\\)`;
          renderMathInElement(el, { renderAccessibleContent: false });
        });
      } catch (err) {
        console.warn('Preview math render failed:', err);
      }
    };
    renderMath();
  }, [doc, mode, onToggleStep, openSteps, useMathJax]);

  return (
    <div
      ref={previewContainerRef}
      className={clsx(
        'space-y-6',
        mode === 'teaching' && 'document-teaching-preview'
      )}
    >
      {(Array.isArray(doc.pages) && doc.pages.length > 0 ? doc.pages : ['']).map((pageHtml, index) => {
        const { headerHtml, bodyHtml, footerHtml } = getPageRegionsHtml(pageHtml || '');
        return (
          <div
            key={`preview-page-${index}`}
            className={clsx(
              'page-preview bg-white shadow-lg rounded border border-gray-200',
              mode === 'teaching' && 'teaching-page-preview'
            )}
            style={{
              width: `${pageWidthMm}mm`,
              height: `${pageHeightMm}mm`,
              '--page-padding': `${pagePaddingMm}mm`,
              '--page-padding-top': `${pagePaddingTopMm}mm`,
              '--page-padding-bottom': `${pagePaddingBottomMm}mm`,
              fontSize: '14px',
              lineHeight: '1.6',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
            data-preview-page-content
          >
            <div
              data-page-header-region="true"
              data-page-index={index}
              className="page-region-header"
              dangerouslySetInnerHTML={{ __html: headerHtml }}
            />
            <div
              className="page-body-preview"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
            <div
              data-page-footer-region="true"
              className="page-region-footer"
              dangerouslySetInnerHTML={{ __html: footerHtml }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default DocumentEditor;
